import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useQueries, type UseQueryResult } from '@tanstack/react-query';
import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { apiFetch } from '../../../auth/apiFetch.ts';
import { SurfaceShell } from '../../../components/SurfaceShell.tsx';
import { Panel, PanelGrid, Eyebrow } from '../../../components/Panel.tsx';
import {
  Toolbar,
  ToolbarSpacer,
  ToolbarDivider,
  ToolbarLabel,
  SegmentedControl,
} from '../../../components/Toolbar.tsx';
import { Button, IconButton } from '../../../components/Button.tsx';
import { SearchInput } from '../../../components/Field.tsx';
import { Meter } from '../../../components/Meter.tsx';
import { KeyValue, type KVItem } from '../../../components/KeyValue.tsx';
import type { MatrixReading } from '../../../components/StatusMatrix.tsx';
import { Sparkline } from '../../../components/Sparkline.tsx';
import { StatusDot } from '../../../components/StatusDot.tsx';
import { StatusBadge } from '../../../components/StatusBadge.tsx';
import { LivenessBadge } from '../../../components/LivenessBadge.tsx';
import { LiveTag } from '../../../components/Freshness.tsx';
import { useFeedWord } from '../../../shell/TopBar.tsx';
import { Empty } from '../../../components/Empty.tsx';
import {
  FaultLine, Figure, figure, unread, useRead, type Read,
} from '../../../components/states/index.ts';
import { Chip } from '../../../components/Chip.tsx';
import { Icon } from '../../../shell/Icon.tsx';
import { useNow } from '../../../components/useNow.ts';
import {
  LIVENESS_STATE,
  MATRIX_KEY,
  STATUS_STATE,
  level,
  levelTone,
  matrixTone,
  type Tone,
} from '../../../components/state.ts';
import {
  BATTERY_BAD,
  BATTERY_WARN,
  CERT_BAD_DAYS,
  CERT_WARN_DAYS,
  FLEET_METRICS,
  SEEN_BAD_MS,
  SEEN_WARN_MS,
  type Reason,
} from '../../../components/fleetMetrics.ts';
import { ReasonList } from '../../../components/ReasonList.tsx';
import { NO_VALUE, duration, latlon, num, utc, utcTime } from '../../../components/format.ts';
import {
  STATUSES,
  useRobots,
  type Liveness,
  type Robot,
  type RobotStatus,
} from '../../../hooks/useRobots.ts';
import { useRobotScans, type ProcessingState, type Scan } from '../../../hooks/useScans.ts';
import { useTelemetry, type TelemetryReading } from '../../../hooks/useTelemetry.ts';
import { usePendingCommands, type Command } from '../../../hooks/useCommands.ts';
import { realtimeConfig } from '../../../realtime/config.ts';
import { useFleetRoute } from '../../../router/useFleetRoute.ts';
import styles from './FleetMap.module.css';

/* ==========================================================================
   Fleet map — the geospatial command surface.

   Three panes, in the idiom THE BAR §11.1 measures off the Skydio flight
   deck: a fixed roster rail, the map stage, and an inspector that ANCHORS TO
   ITS MARKER rather than floating as a modal. Everything drawn comes off the
   robot record or that robot's telemetry history — position, heading, mode,
   liveness, battery, and the real sequence of fixes. Nothing is invented: a
   robot with no fix is absent from the map and present, named, in the rail
   and in a standing notice, because silently dropping it is the lie.

   The basemap is dark and desaturated so the semantic palette on top of it
   is the only saturated thing on screen. When tiles cannot be reached the
   stage does not become a grey void — the graticule takes over, the health
   line says exactly that, and every marker still sits at its true projected
   position.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Basemap
   -------------------------------------------------------------------------- */

/* Esri's Dark Gray Canvas — a keyless, genuinely dark raster basemap whose
   labels ship as a separate layer, so "labels" is a real layer toggle rather
   than a different basemap. Native tiles stop at z16; Leaflet upscales past
   that, which is honest for a yard-scale view of a 1 m fix. */
const TILE_URL =
  'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}';
const LABEL_URL =
  'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}';
const TILE_MAX_NATIVE = 16;
const ATTRIBUTION = 'Esri · HERE · Garmin · © OpenStreetMap';
const BASEMAP_NAME = 'Esri Dark Gray';

/** Pixel radius inside which two robots collapse into one count marker. */
const CLUSTER_RADIUS = 46;
/** At and above this zoom every marker carries its id. */
const LABEL_ZOOM = 13;
/** At and above this zoom an ambient track shows its individual fixes. */
const FIX_ZOOM = 15;
/** Padding around the overlay's drawing box, so a pan reveals real geometry. */
const OVERLAY_PAD = 420;
/** The filter box, addressed by `/`. */
const FILTER_ID = 'fleet-map-filter';

/* Track windows. The agent reports on a ~2.4 min cadence, so these are stated
   in RECORDS and the resolved wall-clock span is printed on the map rather
   than assumed — a window quoted in minutes would be a guess about a rate the
   console does not control. */
/** Records pulled per robot for the always-on fleet track layer. */
const TRACK_LIMIT = 160;
/** Records pulled for the selected robot — the long history, in the accent. */
const SELECTED_LIMIT = 200;
/** Height the bottom-left health/notice stack owns, so nothing is drawn under it. */
const FURNITURE_H = 96;
/** The recent slice of an ambient track that steps up to full opacity. */
const HEAD_MS = 20 * 60_000;

/** How often the fleet's health is re-resolved while live — the fleet poll's
 *  own cadence. Thresholds are 2 min / 1 h / 30 d; nothing is lost by not
 *  re-reading them 5 000 times between two records. */
const HEALTH_TICK_MS = 5_000;

/* --------------------------------------------------------------------------
   The track window — the one time axis on this surface
   --------------------------------------------------------------------------
   The fleet track layer holds several hours of real fixes. Drawing all of it
   and offering no way to move inside it is a claim the surface cannot back:
   the operator can see that 4 320 records exist and cannot ask where the
   coverage sits inside them. So the window is an explicit control with a
   presence band behind it — gaps in the band are gaps in the recording, per
   THE BAR §13 tell 21 — and the axis it scrubs is NAMED (tell 22).

   It governs THE WHOLE SURFACE, not just the history layer: the window's
   leading edge is this view's clock, and the roster, the markers, the facet
   counts and both panel liveness tags are all read at it. See `replayFleet`.
   */
/** Selectable window lengths. `null` = every record held. */
const WINDOW_SPANS: readonly { ms: number | null; label: string }[] = [
  { ms: 20 * 60_000, label: '20m' },
  { ms: 60 * 60_000, label: '1h' },
  { ms: 4 * 60 * 60_000, label: '4h' },
  { ms: null, label: 'All' },
];
/** Buckets across the recorded span. One bucket ≈ 2 px at a 400 px band. */
const PRESENCE_BUCKETS = 200;
/** Shortest window the scrubber will resolve to, so a drag can never empty it. */
const WINDOW_MIN_MS = 60_000;
/** Room the ruler reserves at its right end for the axis name — 14 mono
 *  characters at 10px, plus its own inset. */
const AXIS_W = 92;
/** A ruler label (`|04:00`) is left-anchored at its tick and this wide. */
const TICK_W = 40;

/* Replay transport. A rate control that offers 1x only is a fast fail
   (THE BAR §13, bonus tells), and an ops surface whose one time axis cannot
   be PLAYED lets the operator inspect an instant but never watch an hour of
   fleet movement run. The ladder is chosen against the recording this console
   actually holds — ~17 h of history at a ~2.4 min agent cadence — so 60× puts
   an hour of it on screen in a minute and 600× walks the whole recording in
   about a minute and a half. */
const RATES: readonly number[] = [1, 60, 600, 3600];
/** How often the playhead advances. 4 Hz: fast enough to read as motion,
 *  slow enough that a replayed projection of 28 robots is not recomputed at
 *  frame rate. */
const PLAY_TICK_MS = 250;
/** Opacity bands used to fade the selected track along its length by fix age. */
const FADE_BANDS = 18;
/* The ambient fleet history is the same idea at fleet scale, and it is the
   layer that decides whether this map has data on it or not. It is drawn in
   each robot's OWN state hue, on an age ramp measured against the window's
   own clock — so "bright" means "just now" for every track on the map at
   once, not "late in this particular robot's slice". Seven bands is the
   fewest that still reads as a ramp rather than as steps, and at 27 tracks it
   is ~190 sub-paths rather than ~4 300 segments. */
const AMB_BANDS = 7;
/** The faintest an ambient track's oldest band is ever drawn. */
const AMB_MIN_A = 0.2;
/** The strongest its newest band is drawn, before the head steps up again. */
const AMB_MAX_A = 0.92;
/** Metres the site hull is pushed out from its members, in screen pixels. */
const SITE_PAD_PX = 15;
/** Smallest radius a site extent is ever drawn at, so it stays a visible mark
 *  when the hull collapses at continental zoom — and clears the cluster
 *  bubble that stands in the same place. */
const SITE_MIN_R = 21;

/* The docked inspector's geometry, shared by the card, the reveal pan and the
   overlay's chip clamp — one definition, so nothing is ever placed underneath
   it by a constant that drifted. */
const CARD_LEFT = 12;
const CARD_W = 340;
/** Everything the card owns, plus room for a marker's own label. */
const CARD_CLEAR = CARD_LEFT + CARD_W + 70;

/* --------------------------------------------------------------------------
   The pin, and the subject's footprint
   --------------------------------------------------------------------------
   A marker is not a point. It is a 36 px glyph wearing a chip that can run
   ~120 px wide and sits 20 px above or below the pin. Every camera move in
   this view frames THAT box, because a robot revealed with its id sliced off
   by the pane edge — or tucked under the legend — is a robot you have to go
   looking for a second time.

   These are the pin's real dimensions, in its own 36×36 viewBox, and the
   layout constants below are derived from them rather than guessed — a
   number that drifts here is a chip through the arc of the marker it
   belongs to. */
/** The pin's viewBox, and the size of the button that carries it. */
const PIN_BOX = 36;
const PIN_C = PIN_BOX / 2;
/** Radius of the state ring's centreline; it is stroked 2 px, so it spans 6–8. */
const RING_R = 7;
/** The dark cartographic ground the symbol carries with it. */
const GROUND_R = 9;
/** A faulted pin wears a second ring outside the first; its ground is wider. */
const ALERT_R = 10.8;
const ALERT_GROUND_R = 11.6;
/** Distance from centre to the heading wedge's tip. */
const NEEDLE_TIP = 15.2;
/* A robot carrying orders it has not finished wears a dashed ring OUTSIDE
   both state rings and inside PIN_R, so it is a fourth independent reading on
   the same symbol rather than a recolouring of one that already means
   something else. Dash, not fill: it is a claim about a queue, not about
   health. */
const TASK_R = 13.4;
/** The drawn radius of a commanded waypoint's target glyph. */
const ORDER_R = 6;

/* A pin that prints its own age is WIDER than its ring: the text is hung off
   the ground circle's right edge and runs past PIN_R. The placer has to know
   that, or a chip laid against the pin — including the pin's OWN chip — is
   laid straight over the one reading the marker prints for itself. */
/** Gap from the ground circle's edge to the first glyph of the age. */
const PIN_AGE_GAP = 2.5;
/** 10px mono advance, used to size the age text without measuring it. */
const MONO_10 = 6.05;

/** The drawn radius of a pin, needle and fault ring included. */
const PIN_R = 16;
const LABEL_H = 16;
const LABEL_GAP = 4;
/** Distance from the pin's centre to the near edge of its label chip. */
const LABEL_OFF = PIN_R + LABEL_GAP;
/** Half the width the widest three-field label chip reaches. */
const SUBJ_X = 62;
/** How far the subject reaches above and below its own centre, label included. */
const SUBJ_Y = LABEL_OFF + LABEL_H;
/** Breathing room between the subject and any pane edge or panel. */
const EDGE_PAD = 10;

interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

function overlaps(a: Box, b: Box): boolean {
  return a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0;
}

/** Everything the overlay must not print under: the corner stacks, the docked
 *  inspector, the follow tag. */
const CHROME_SEL = '[data-furniture="true"]';
/** The subset ANCHORED to a pane edge — the only chrome a `fitBounds` can
 *  meaningfully pad away from, since padding is a rectangle. A chip floating
 *  mid-stage is an obstacle for text but not a reason to zoom out. */
const EDGE_CHROME_SEL = '[data-furniture="true"][data-edge="true"]';

/** Chrome drawn ON the tiles, in map-container coordinates, measured.
 *  Measured rather than assumed: the legend toggles, the layer tree opens,
 *  and the bottom-left notice stack grows a row per robot without a fix. */
function stageChrome(map: L.Map, sel: string = CHROME_SEL): Box[] {
  const stage = map.getContainer().parentElement;
  if (stage === null) return [];
  const cr = map.getContainer().getBoundingClientRect();
  const out: Box[] = [];
  for (const el of stage.querySelectorAll<HTMLElement>(sel)) {
    const r = el.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) continue;
    out.push({
      x0: r.left - cr.left,
      y0: r.top - cr.top,
      x1: r.right - cr.left,
      y1: r.bottom - cr.top,
    });
  }
  return out;
}

/** The rectangle a subject may occupy: the stage minus the inspector, minus
 *  the room its own label needs, minus the pane edges. */
function clearRect(map: L.Map, cardOpen: boolean): Box {
  const size = map.getSize();
  return {
    x0: (cardOpen ? CARD_CLEAR : EDGE_PAD) + SUBJ_X,
    y0: EDGE_PAD + SUBJ_Y,
    x1: size.x - EDGE_PAD - SUBJ_X,
    y1: size.y - EDGE_PAD - SUBJ_Y,
  };
}

/** Where a subject centred on `p` would actually paint. */
function subjectAt(p: { x: number; y: number }): Box {
  return { x0: p.x - SUBJ_X, y0: p.y - SUBJ_Y, x1: p.x + SUBJ_X, y1: p.y + SUBJ_Y };
}

/** The container point that puts a subject in the most open water available:
 *  inside the clear rect, off every piece of chrome, and as near the middle of
 *  what is left as that allows. */
function clearTarget(map: L.Map, cardOpen: boolean): L.Point | null {
  const box = clearRect(map, cardOpen);
  if (box.x1 <= box.x0 || box.y1 <= box.y0) return null;
  const chrome = stageChrome(map).map((b) => ({
    x0: b.x0 - 8,
    y0: b.y0 - 8,
    x1: b.x1 + 8,
    y1: b.y1 + 8,
  }));
  const cx = (box.x0 + box.x1) / 2;
  const cy = (box.y0 + box.y1) / 2;
  let best: { x: number; y: number; free: boolean; d: number } | null = null;
  for (let i = 0; i <= 8; i += 1) {
    for (let j = 0; j <= 6; j += 1) {
      const x = box.x0 + ((box.x1 - box.x0) * i) / 8;
      const y = box.y0 + ((box.y1 - box.y0) * j) / 6;
      const s = subjectAt({ x, y });
      const free = !chrome.some((c) => overlaps(s, c));
      const d = Math.hypot(x - cx, y - cy);
      if (best === null || (free !== best.free ? free : d < best.d)) best = { x, y, free, d };
    }
  }
  return best === null ? null : L.point(best.x, best.y);
}

/** Park one position on the clear target — "centre" for a stage that is not
 *  symmetric, used by follow mode and by centre-on-robot. */
function holdAt(map: L.Map, lat: number, lon: number, cardOpen: boolean): void {
  const target = clearTarget(map, cardOpen);
  if (target === null) {
    map.panTo([lat, lon], { animate: false });
    return;
  }
  const cp = map.latLngToContainerPoint([lat, lon]);
  if (Math.abs(cp.x - target.x) < 1 && Math.abs(cp.y - target.y) < 1) return;
  map.panBy([cp.x - target.x, cp.y - target.y], { animate: false });
}

/** Padding for every `fitBounds` on this stage, so fitted geometry lands in
 *  the visible part of the map rather than behind the inspector or the legend.
 *  Each panel pushes the fit away along its OWN SHORT AXIS — the direction
 *  that costs the fewest map pixels. Pushing a 236x199 legend block sideways
 *  rather than upwards costs a whole zoom level on a fleet spread across
 *  three continents, which is the difference between a readable fit and a
 *  world map with three dots on it. */
function stagePadding(map: L.Map, cardOpen: boolean): L.FitBoundsOptions {
  const size = map.getSize();
  /* What the subject itself needs, before any panel is considered. */
  const base = {
    top: EDGE_PAD + SUBJ_Y,
    bottom: EDGE_PAD + SUBJ_Y,
    left: (cardOpen ? CARD_CLEAR : EDGE_PAD) + SUBJ_X,
    right: EDGE_PAD + SUBJ_X,
  };
  const panel = { top: 0, bottom: 0, left: 0, right: 0 };
  for (const b of stageChrome(map, EDGE_CHROME_SEL)) {
    if (b.y1 - b.y0 <= b.x1 - b.x0) {
      if (b.y0 < size.y / 2) panel.top = Math.max(panel.top, b.y1 + EDGE_PAD + SUBJ_Y);
      else panel.bottom = Math.max(panel.bottom, size.y - b.y0 + EDGE_PAD + SUBJ_Y);
    } else if (b.x0 < size.x / 2) {
      panel.left = Math.max(panel.left, b.x1 + EDGE_PAD + SUBJ_X);
    } else {
      panel.right = Math.max(panel.right, size.x - b.x0 + EDGE_PAD + SUBJ_X);
    }
  }
  /* A panel may claim at most a third of the stage; padding that eats the
     stage would make `fitBounds` zoom to nothing. */
  const fit = (b: number, p: number, dim: number) =>
    Math.min(Math.max(b, Math.min(p, dim * 0.34)), Math.max(24, dim / 2 - 48));
  return {
    paddingTopLeft: L.point(
      fit(base.left, panel.left, size.x),
      fit(base.top, panel.top, size.y),
    ),
    paddingBottomRight: L.point(
      fit(base.right, panel.right, size.x),
      fit(base.bottom, panel.bottom, size.y),
    ),
    animate: false,
  };
}

/* --------------------------------------------------------------------------
   Derived state — one definition, shared by the rail, the markers, the legend
   -------------------------------------------------------------------------- */

type Health = 'fault' | 'nodata' | 'held' | 'notcurrent' | 'nominal';

/* --------------------------------------------------------------------------
   ONE definition of fleet health, and this surface does not own it.
   --------------------------------------------------------------------------
   The console asks "which robots need me" in three places at once: the app
   strip's pulse counts, this map's health facets, and this map's legend. They
   used to be two different computations wearing the same four words — the
   strip counted the worst of SIX metrics, the map ran three ad-hoc rules —
   so the strip could read `4 fault` while the facet row 60 px below read
   `Fault 2`. Identical vocabulary, different answers, nothing disclosing that
   they were different questions. An operator who notices that once stops
   trusting both numbers, and they are the two numbers this console exists to
   publish.

   So the map no longer has an opinion. `components/fleetMetrics` owns which
   quantities the fleet is judged on (MODE · LINK · BATT · SEEN · CERT · REG),
   which thresholds each crosses, and what its readings are called;
   `state.matrixTone` owns the projection onto colour and `state.MATRIX_KEY`
   owns the words. This file reads all three. A robot's class here is the
   WORST of those six readings — the same rule `fleetExceptions` sorts the
   operations page by — so the strip, the facets and the legend are now the
   same computation and cannot drift.

   What the map adds is the part the strip has no room for: the reasons. A
   pin, a roster row and a card all name the readings that put the robot in
   its class (`SEEN 7h 39m · BATT 22%`), so "not current" is never a word with
   no evidence behind it.
   -------------------------------------------------------------------------- */

/**
 * A robot record as this surface reads it, with its health already resolved.
 *
 * Live, it is the API's record. Off live it is that record rewritten to the
 * telemetry in force at the window's leading edge — and `noRecord` marks the
 * ones this console holds nothing for at that instant. Mode, link, charge and
 * pose are all properties of a RECORD, so with no record none of them may be
 * claimed: the live values would be the ones nine hours in the future.
 *
 * `health` and `reasons` are stamped ONCE per read, by `stampHealth`, against
 * the one clock this surface is showing. Deriving them per component would be
 * how the rail and the legend start disagreeing at a threshold.
 */
type RawRobot = Robot & { noRecord?: boolean };
type MapRobot = RawRobot & {
  health: Health;
  /** Every non-nominal reading behind `health`, worst metric order. */
  reasons: readonly Reason[];
};

/** Severity order matches `fleetMetrics.severityOf`: fault > no data > held. */
const HEALTH_ORDER: readonly Health[] = ['fault', 'nodata', 'held', 'notcurrent', 'nominal'];

/** The class each of `matrixTone`'s five colours names. */
const HEALTH_BY_TONE: Partial<Record<Tone, Health>> = {
  error: 'fault',
  offline: 'nodata',
  maintenance: 'held',
  warn: 'notcurrent',
  nominal: 'nominal',
};

const HEALTH_TONE: Record<Health, Tone> = {
  fault: 'error',
  nodata: 'offline',
  held: 'maintenance',
  notcurrent: 'warn',
  nominal: 'nominal',
};

/* The words come from the console's own key rather than being retyped here:
   the app strip reads `MATRIX_KEY` too, and two legends over one palette that
   are spelled out twice are two legends that will drift. Sentence case is
   this surface's presentation of them, not a second vocabulary. */
const HEALTH_LABEL: Record<Health, string> = HEALTH_ORDER.reduce(
  (acc, h) => {
    const word = MATRIX_KEY.find((k) => k.tone === HEALTH_TONE[h])?.label ?? h;
    acc[h] = word.charAt(0).toUpperCase() + word.slice(1);
    return acc;
  },
  {} as Record<Health, string>,
);

/** The facet row, in severity order — `all` first, `nominal` is not a facet. */
const HEALTH_FACETS: readonly (Health | 'all')[] = [
  'all',
  ...HEALTH_ORDER.filter((h) => h !== 'nominal'),
];

/** The six metric names, for the readout that says what these counts count. */
const HEALTH_BASIS = FLEET_METRICS.map((m) => m.label.toUpperCase()).join(' · ');

/**
 * The console's six readings, taken at THIS surface's clock.
 *
 * `FLEET_METRICS` owns the quantities and the thresholds. Two of its six are
 * relative to `Date.now()`, which is the wrong instant on a surface that can
 * be scrubbed nine hours into the past — so SEEN and CERT are re-read here
 * against `at`, using the same exported limits. The definition is still
 * fleetMetrics'; only the clock is ours.
 */
function readingsAt(r: RawRobot, at: number): Reason[] {
  const out: Reason[] = [];
  for (const m of FLEET_METRICS) {
    let reading: MatrixReading | null;
    if (m.key === 'seen') {
      const age = seenAge(r, at);
      reading =
        age === null
          ? null
          : { tone: levelTone(level(age, SEEN_WARN_MS, SEEN_BAD_MS, true)), text: duration(age) };
    } else if (m.key === 'cert') {
      const days =
        r.cert_not_after === null ? null : (Date.parse(r.cert_not_after) - at) / 86_400_000;
      reading =
        days === null || Number.isNaN(days)
          ? null
          : {
              tone: levelTone(level(days, CERT_WARN_DAYS, CERT_BAD_DAYS)),
              text: `${Math.round(days)}d`,
            };
    } else {
      reading = m.read(r);
    }
    /* A metric with no reading at all is a reading: "we hold nothing for this"
       is an exception, not a blank — `fleetExceptions` counts it the same way. */
    if (reading === null) {
      out.push({ label: m.label, text: NO_VALUE, tone: 'offline', hollow: true });
      continue;
    }
    const tone = matrixTone(reading.tone);
    if (tone === 'nominal') continue;
    out.push({ label: m.label, text: reading.text, tone, hollow: reading.hollow ?? false });
  }
  return out;
}

/** Worst of the readings — the same order `fleetExceptions` sorts on. */
function healthFrom(reasons: readonly Reason[]): Health {
  let worst: Health = 'nominal';
  for (const rs of reasons) {
    const h = HEALTH_BY_TONE[rs.tone] ?? 'nominal';
    if (HEALTH_ORDER.indexOf(h) < HEALTH_ORDER.indexOf(worst)) worst = h;
  }
  return worst;
}

/** Resolve the whole fleet's health once, at one instant. */
function stampHealth(list: readonly RawRobot[], at: number): MapRobot[] {
  return list.map((r) => {
    const reasons = readingsAt(r, at);
    return { ...r, reasons, health: healthFrom(reasons) };
  });
}

/** The resolved class. Kept as a function so every call site reads the same. */
function healthOf(r: MapRobot): Health {
  return r.health;
}

/** Nothing is arriving from this robot — the symbol draws hollow, not coloured. */
function isHollow(r: MapRobot): boolean {
  return r.noRecord === true || LIVENESS_STATE[r.liveness].hollow;
}

/**
 * The readings a roster row already draws for itself.
 *
 * MODE is the row's status dot's colour and LINK is whether that dot is
 * hollow; BATT is the meter and SEEN is the age column, both already toned by
 * the same thresholds. Reprinting any of the four as text 40 px away would be
 * the same reading twice. CERT and REG are the two the row has no column for
 * — and they are exactly the two that would otherwise leave a row with an
 * amber edge bar and nothing on it to explain the bar.
 */
const COLUMN_METRICS: ReadonlySet<string> = new Set(['Mode', 'Link', 'Batt', 'Seen']);

/** Reasons this robot is flagged that are NOT already visible as a column. */
function offColumnReasons(r: MapRobot): readonly Reason[] {
  return r.reasons.filter((x) => !COLUMN_METRICS.has(x.label));
}

/**
 * The one reading a marker's chip must print to justify its own colour.
 *
 * The chip already carries charge and age, and the pin carries mode and link,
 * so four of the six readings explain themselves. The other two do not: a pin
 * painted amber for a cert inside 30 days sits beside `51% 12s` with nothing
 * on the chip amber, which is colour on a map carrying state by itself. The
 * chip earns the extra ~50px only on the robots that need it — two of
 * twenty-eight here — and `null` everywhere else, so the placer reserves
 * nothing it will not use.
 *
 * A single shared answer, so `RobotChip` and the width the placer reserves
 * for it can never disagree.
 */
function chipReason(r: MapRobot): Reason | null {
  const off = offColumnReasons(r);
  return off.find((x) => !x.hollow) ?? off[0] ?? null;
}

/** How that reading is spelled on a chip: `CERT 9d`. */
function chipReasonText(x: Reason): string {
  return `${x.label.toUpperCase()} ${x.text}`;
}

/** `Not current — CERT 9d`. Why a symbol wears the colour it wears, in one
 *  string, for the places that can only speak in a title or to a reader. */
function whyText(r: MapRobot): string {
  if (r.reasons.length === 0) return HEALTH_LABEL.nominal.toLowerCase();
  return `${HEALTH_LABEL[r.health].toLowerCase()} — ${r.reasons
    .map((x) => `${x.label.toUpperCase()} ${x.text}`)
    .join(' · ')}`;
}

function worstHealth(rs: readonly MapRobot[]): Health {
  let worst: Health = 'nominal';
  for (const r of rs) {
    const h = healthOf(r);
    if (HEALTH_ORDER.indexOf(h) < HEALTH_ORDER.indexOf(worst)) worst = h;
  }
  return worst;
}

/** Age of the newest telemetry record, in ms. `null` = never phoned home. */
function seenAge(r: RawRobot, now: number): number | null {
  if (r.last_seen === null) return null;
  const t = Date.parse(r.last_seen);
  return Number.isNaN(t) ? null : Math.max(0, now - t);
}

function seenTone(age: number | null): Tone {
  return levelTone(level(age, SEEN_WARN_MS, SEEN_BAD_MS, true));
}

type FixedRobot = MapRobot & { lat: number; lon: number };

/** Does this pin print its own age? Mirrors `RobotMarker`. */
function pinPrintsAge(r: MapRobot, now: number): boolean {
  const age = seenAge(r, now);
  return isHollow(r) || (age !== null && age > SEEN_WARN_MS);
}

/** How far right of the pin's centre its own age text actually reaches. */
function pinRightExtent(r: MapRobot, now: number): number {
  if (!pinPrintsAge(r, now)) return PIN_R;
  const ground = healthOf(r) === 'fault' ? ALERT_GROUND_R : GROUND_R;
  return Math.max(PIN_R, ground + PIN_AGE_GAP + duration(seenAge(r, now), { compact: 1 }).length * MONO_10 + 1);
}

function hasFix(r: MapRobot): r is FixedRobot {
  return r.lat !== null && r.lon !== null && !Number.isNaN(r.lat) && !Number.isNaN(r.lon);
}

function metaString(r: MapRobot, key: string): string | null {
  const v = r.meta[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function siteKey(r: MapRobot): string {
  return metaString(r, 'site') ?? 'unsited';
}

function siteLabel(r: MapRobot): string {
  return metaString(r, 'site_label') ?? siteKey(r);
}

/** `portland-yard` → `Portland`. The selector is narrow; the title is full. */
function shortSite(key: string): string {
  const head = key.split('-')[0] ?? key;
  return head.charAt(0).toUpperCase() + head.slice(1);
}

function payloadNumber(p: Record<string, unknown>, key: string): number | null {
  const v = p[key];
  return typeof v === 'number' && !Number.isNaN(v) ? v : null;
}

/* --------------------------------------------------------------------------
   Tracks — every robot's recent path, not just the selected one

   A fleet map that draws a path only for the thing you already clicked tells
   you nothing you did not know. Drawn for all of them, the paths ARE the
   reading: an arc means the robot is working a route, a knot means it is
   holding station, a dot means it has not moved in hours. That is legible as
   texture across a whole yard before a single marker is read.

   Every vertex is a real telemetry record. The API has no fleet-wide track
   endpoint, so this is one bounded request per robot; the ambient window is
   deliberately short and polled slowly, because at a ~2.4 min cadence a 5 s
   poll of an 8 h history is traffic that cannot change the picture.
   -------------------------------------------------------------------------- */

interface Fix {
  lat: number;
  lon: number;
  /** Epoch ms of the record. */
  t: number;
  /** Heading in degrees true at that record, `null` if not reported. */
  hdg: number | null;
  /** Battery charge at that record. */
  bat: number | null;
  /** Operational mode the agent reported alongside it. */
  mode: RobotStatus | null;
  /** Ground speed, m/s. */
  spd: number | null;
}

/** One robot's window slice, plus the reading its path is drawn in. */
interface AmbientTrack {
  id: string;
  fixes: readonly Fix[];
  tone: Tone;
}

/** The agent's `state` string, accepted only when it is a mode we know. */
function asStatus(v: string | null): RobotStatus | null {
  return v !== null && (STATUSES as readonly string[]).includes(v) ? (v as RobotStatus) : null;
}

async function fetchTrack(robotId: string, limit: number): Promise<TelemetryReading[]> {
  const r = await apiFetch(
    `/api/robots/${encodeURIComponent(robotId)}/telemetry?limit=${limit}`,
  );
  if (!r.ok) throw new Error(`status ${r.status}`);
  const body = (await r.json()) as { readings?: TelemetryReading[] };
  return body.readings ?? [];
}

/** Newest-first readings → oldest-first fixes, dropping records with no pose. */
function toFixes(readings: readonly TelemetryReading[]): Fix[] {
  const out: Fix[] = [];
  for (let i = readings.length - 1; i >= 0; i -= 1) {
    const t = readings[i];
    if (t === undefined || t.lat === null || t.lon === null) continue;
    const ms = Date.parse(t.ts);
    if (Number.isNaN(ms)) continue;
    out.push({
      lat: t.lat,
      lon: t.lon,
      t: ms,
      hdg: t.heading_deg,
      bat: t.battery_pct,
      mode: asStatus(t.state),
      spd: t.speed_mps,
    });
  }
  return out;
}

/* --------------------------------------------------------------------------
   Capture sessions — the ground a robot actually mapped
   --------------------------------------------------------------------------
   Every scan record carries the WGS84 bounding box of its capture. Those boxes
   are the only geometry on this platform that says where work was done rather
   than where a machine happened to be, so the map draws them for the selected
   robot. The endpoint is per-robot (`/api/robots/{id}/scans`), which is why
   this is a selection layer and not an always-on one.

   `Scan` in `hooks/useScans.ts` does not yet declare the bbox columns the API
   sends, so they are read through a local widening rather than by editing a
   shared type from inside this view. */
type ScanBox = Scan &
  Partial<Record<'bbox_min_lat' | 'bbox_min_lon' | 'bbox_max_lat' | 'bbox_max_lon', number | null>>;

interface SessionExtent {
  id: number;
  /** The human-meaningful tail of `20260827T1101Z_row-14`. */
  name: string;
  session: string;
  state: ProcessingState;
  south: number;
  west: number;
  north: number;
  east: number;
  started: number | null;
  /** Capture duration in ms, or null when the session has not stopped. */
  ran: number | null;
}

function num6(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function toSessions(scans: readonly Scan[]): SessionExtent[] {
  const out: SessionExtent[] = [];
  for (const raw of scans) {
    const b = raw as ScanBox;
    const south = num6(b.bbox_min_lat);
    const west = num6(b.bbox_min_lon);
    const north = num6(b.bbox_max_lat);
    const east = num6(b.bbox_max_lon);
    if (south === null || west === null || north === null || east === null) continue;
    const t0 = raw.started_utc === null ? NaN : Date.parse(raw.started_utc);
    const t1 = raw.stopped_utc === null ? NaN : Date.parse(raw.stopped_utc);
    out.push({
      id: raw.id,
      name: raw.session_id.includes('_')
        ? raw.session_id.slice(raw.session_id.indexOf('_') + 1)
        : raw.session_id,
      session: raw.session_id,
      state: raw.processing_state,
      south,
      west,
      north,
      east,
      started: Number.isNaN(t0) ? null : t0,
      ran: Number.isNaN(t0) || Number.isNaN(t1) ? null : t1 - t0,
    });
  }
  return out.sort((a, b) => (b.started ?? 0) - (a.started ?? 0));
}

interface FleetTracks {
  byId: ReadonlyMap<string, readonly Fix[]>;
  /** Total vertices held — the honest size of the layer. */
  fixes: number;
  /** Robots whose track has arrived, still in flight, and whose request failed. */
  loaded: number;
  pending: number;
  failed: number;
  /** Wall-clock span actually covered, epoch ms. */
  oldest: number | null;
  newest: number | null;
  /* Feed provenance. Not the data — how the data GOT here. One round trip
     per robot, so "when did this layer last hear anything" and "when did the
     slowest one last answer" are two different questions, and the spread
     between them is the only honest thing this console can say about link
     quality. THE BAR §13 tell 13 — a figure with no distribution behind it is
     a decoration. */
  /** Newest and oldest successful response across the fan-out, epoch ms. */
  freshest: number | null;
  stalest: number | null;
}

/** `14:19:12Z` from epoch ms — every time on this surface is Zulu. */
function zulu(ms: number | undefined): string {
  return ms === undefined ? NO_VALUE : utcTime(new Date(ms).toISOString());
}

function useFleetTracks(ids: readonly string[], enabled: boolean): FleetTracks {
  /* `combine` is memoised on the id list, not rebuilt inline: TanStack only
     reuses a combined result while the combine FUNCTION is identity-stable, so
     an inline closure would re-fold every track on every render — including
     the once-a-second age tick — and hand every downstream memo a fresh
     object to invalidate on. */
  const combine = useCallback(
    (results: readonly UseQueryResult<TelemetryReading[], Error>[]): FleetTracks => {
      const byId = new Map<string, readonly Fix[]>();
      let fixes = 0;
      let loaded = 0;
      let pending = 0;
      let failed = 0;
      let oldest: number | null = null;
      let newest: number | null = null;
      let freshest: number | null = null;
      let stalest: number | null = null;
      results.forEach((res, i) => {
        const id = ids[i];
        if (id === undefined) return;
        if (res.data === undefined) {
          if (res.isPending) pending += 1;
          else failed += 1;
          return;
        }
        loaded += 1;
        const at = res.dataUpdatedAt;
        if (at > 0) {
          if (freshest === null || at > freshest) freshest = at;
          if (stalest === null || at < stalest) stalest = at;
        }
        const pts = toFixes(res.data);
        if (pts.length === 0) return;
        byId.set(id, pts);
        fixes += pts.length;
        const a = pts[0];
        const b = pts[pts.length - 1];
        if (a !== undefined && (oldest === null || a.t < oldest)) oldest = a.t;
        if (b !== undefined && (newest === null || b.t > newest)) newest = b.t;
      });
      return { byId, fixes, loaded, pending, failed, oldest, newest, freshest, stalest };
    },
    [ids],
  );

  return useQueries({
    queries: ids.map((id) => ({
      queryKey: ['telemetry', id, TRACK_LIMIT] as const,
      queryFn: () => fetchTrack(id, TRACK_LIMIT),
      enabled,
      /* The agent reports on a ~2.4 min cadence. Polling a multi-hour history
         every five seconds would be traffic that cannot change the picture. */
      refetchInterval: 20_000,
      staleTime: 10_000,
    })),
    combine,
  });
}

/* --------------------------------------------------------------------------
   Replay — the fleet as it read at one instant
   --------------------------------------------------------------------------
   The transport bar at the foot of this surface is not a history filter, it
   is the surface's clock. Scrub it and EVERY reading on the screen has to
   move with it: the pin's position and heading, the chip's battery and age,
   the roster's age column, the health facets, the legend's counts, and both
   panel liveness tags. A map that answers "07:00Z" with a pin at the live
   pose and an age of `17s` is not showing history, it is showing now while
   claiming otherwise — and it is the one defect a time-travelling ops
   surface cannot survive.

   So the projection below rewrites each robot record to the telemetry record
   IN FORCE at the window's leading edge, and every derivation downstream —
   `healthOf`, `seenAge`, the markers, the rail, the card — runs unchanged
   against it. Nothing is interpolated between records and nothing is
   invented: a robot with no record at or before that instant is off the map
   with a named reason, exactly as a robot with no fix is now.
   -------------------------------------------------------------------------- */

/** Newest fix at or before `t`. `null` when the held track starts after it. */
function fixAt(pts: readonly Fix[], t: number): Fix | null {
  let lo = 0;
  let hi = pts.length - 1;
  let best: Fix | null = null;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const f = pts[mid];
    if (f === undefined) break;
    if (f.t <= t) {
      best = f;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return best;
}

/** Why a robot is not drawn at the replayed instant. */
type ReplayMiss =
  /** History is held for it, but it starts AFTER the window's edge. This is
   *  a limit of what the console pulled, not a claim about the robot: it is
   *  "no record here", never "it was not reporting". */
  | 'before'
  /** No history is held for it at all — the request is in flight or failed. */
  | 'unheld';

/**
 * Link state at a past instant, by the same recency rule the server applies
 * to `now`: inside two minutes is live, inside an hour is stale, past that it
 * had stopped reporting. `backfilling` describes how a record ARRIVED and
 * cannot be recovered from the record itself, so a replayed link state never
 * claims it.
 */
function livenessAt(ageMs: number): Liveness {
  if (ageMs <= SEEN_WARN_MS) return 'live';
  if (ageMs <= SEEN_BAD_MS) return 'stale';
  return 'offline';
}

interface Replay {
  robots: readonly RawRobot[];
  miss: ReadonlyMap<string, ReplayMiss>;
}

/** Every robot record rewritten to the record in force at `t`. */
function replayFleet(
  all: readonly Robot[],
  byId: ReadonlyMap<string, readonly Fix[]>,
  t: number,
): Replay {
  const robots: RawRobot[] = [];
  const miss = new Map<string, ReplayMiss>();
  /* No record at this instant means no reading of any kind at this instant.
     Carrying the live mode or the live charge through would put a value nine
     hours in the future beside a timestamp nine hours in the past. */
  const blank = (r: Robot): RawRobot => ({
    ...r,
    lat: null,
    lon: null,
    heading_deg: null,
    battery_pct: null,
    last_seen: null,
    liveness: 'offline',
    noRecord: true,
  });
  for (const r of all) {
    if (r.liveness === 'provisioning') {
      robots.push(r);
      continue;
    }
    const pts = byId.get(r.robot_id);
    if (pts === undefined || pts.length === 0) {
      miss.set(r.robot_id, 'unheld');
      robots.push(blank(r));
      continue;
    }
    const f = fixAt(pts, t);
    if (f === null) {
      miss.set(r.robot_id, 'before');
      robots.push(blank(r));
      continue;
    }
    robots.push({
      ...r,
      lat: f.lat,
      lon: f.lon,
      heading_deg: f.hdg,
      battery_pct: f.bat,
      status: f.mode ?? r.status,
      liveness: livenessAt(Math.max(0, t - f.t)),
      last_seen: new Date(f.t).toISOString(),
    });
  }
  return { robots, miss };
}

/* --------------------------------------------------------------------------
   Site extents — the convex hull of everywhere a site's robots have been

   Not a drawn boundary the platform does not have, and not a circle around a
   centroid: the polygon is the hull of every recorded fix plus every current
   pose belonging to that site. It is therefore a claim the data can back —
   "this is the ground these robots worked" — and it is stable under filtering,
   because a site's extent is a property of the site, not of the roster query.
   -------------------------------------------------------------------------- */

interface Pt {
  x: number;
  y: number;
}

/** Andrew's monotone chain, counter-clockwise, no collinear points. */
function convexHull(points: readonly Pt[]): Pt[] {
  if (points.length < 3) return [...points];
  const pts = [...points].sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x));
  const cross = (o: Pt, a: Pt, b: Pt) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const build = (src: readonly Pt[]): Pt[] => {
    const out: Pt[] = [];
    for (const p of src) {
      while (out.length >= 2) {
        const a = out[out.length - 2] as Pt;
        const b = out[out.length - 1] as Pt;
        if (cross(a, b, p) > 0) break;
        out.pop();
      }
      out.push(p);
    }
    out.pop();
    return out;
  };
  const lower = build(pts);
  const upper = build([...pts].reverse());
  return [...lower, ...upper];
}

interface SiteExtent {
  key: string;
  label: string;
  /** Hull vertices in geographic order. Empty when the site has one pose. */
  ring: readonly { lat: number; lon: number }[];
  centre: { lat: number; lon: number };
  /** Robots of this site carrying a fix. */
  members: number;
  /** Vertices the hull was computed from — the evidence behind the shape. */
  samples: number;
  /** Longest chord across the hull, in metres. */
  spanM: number;
  worst: Health;
}

function buildSiteExtents(
  positioned: readonly FixedRobot[],
  tracks: ReadonlyMap<string, readonly Fix[]>,
): SiteExtent[] {
  const bySite = new Map<string, { robots: FixedRobot[]; pts: Pt[] }>();
  for (const r of positioned) {
    const k = siteKey(r);
    let e = bySite.get(k);
    if (e === undefined) {
      e = { robots: [], pts: [] };
      bySite.set(k, e);
    }
    e.robots.push(r);
    e.pts.push({ x: r.lon, y: r.lat });
    for (const f of tracks.get(r.robot_id) ?? []) e.pts.push({ x: f.lon, y: f.lat });
  }

  const out: SiteExtent[] = [];
  for (const [key, e] of bySite) {
    const first = e.robots[0];
    if (first === undefined) continue;
    const ring = convexHull(e.pts).map((p) => ({ lat: p.y, lon: p.x }));
    let sx = 0;
    let sy = 0;
    for (const p of e.pts) {
      sx += p.x;
      sy += p.y;
    }
    const centre = { lat: sy / e.pts.length, lon: sx / e.pts.length };
    let spanM = 0;
    for (let i = 0; i < ring.length; i += 1) {
      for (let j = i + 1; j < ring.length; j += 1) {
        const a = ring[i] as { lat: number; lon: number };
        const b = ring[j] as { lat: number; lon: number };
        spanM = Math.max(spanM, L.latLng(a.lat, a.lon).distanceTo(L.latLng(b.lat, b.lon)));
      }
    }
    out.push({
      key,
      label: siteLabel(first),
      ring,
      centre,
      members: e.robots.length,
      samples: e.pts.length,
      spanM,
      worst: worstHealth(e.robots),
    });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
}

/** `11`, or `0/11` when a filter is hiding some of a site's robots. */
function siteCountText(shown: number, members: number): string {
  return shown === members ? String(members) : `${shown}/${members}`;
}

/** `1.4 km` / `380 m` — one cell, unit as a dimmer suffix at the call site. */
function metres(m: number): { value: string; unit: string } {
  return m >= 1000
    ? { value: (m / 1000).toFixed(m >= 10_000 ? 0 : 1), unit: 'km' }
    : { value: String(Math.round(m)), unit: 'm' };
}

/* --------------------------------------------------------------------------
   Grouping
   -------------------------------------------------------------------------- */

type GroupBy = 'site' | 'link' | 'mode' | 'task';

interface Group {
  key: string;
  label: string;
  robots: MapRobot[];
  /** Grouped by task: when the oldest order in this group was issued. The
   *  header's elapsed badge is then TIME IN THIS STATE — how long the fleet
   *  has been waiting on an acknowledgement — rather than the age of a
   *  telemetry record, which is a different question with a different answer.
   *  THE BAR §13 tell 19. */
  issuedAt?: number | null;
}

const LINK_ORDER: readonly string[] = ['offline', 'provisioning', 'stale', 'backfilling', 'live'];
const MODE_ORDER: readonly string[] = ['error', 'maintenance', 'charging', 'active', 'idle'];
/* The tasking lifecycle, worst first — the same ordering rule the other two
   discrete groupings use. `skydio-dfr-incident-dispatch.png` groups its queue
   by exactly this: what is being acted on, then what is waiting, then what is
   not in play, each header carrying its count. */
const TASK_ORDER: readonly string[] = ['sent', 'pending', 'none'];
const TASK_LABEL: Record<string, string> = {
  sent: 'Sent · unacknowledged',
  pending: 'Queued in cloud',
  none: 'No orders',
};

function groupRobots(
  robots: readonly MapRobot[],
  by: GroupBy,
  tasking: ReadonlyMap<string, readonly Command[]>,
): Group[] {
  const buckets = new Map<string, Group>();
  const push = (key: string, label: string, r: MapRobot) => {
    const g = buckets.get(key);
    if (g === undefined) buckets.set(key, { key, label, robots: [r] });
    else g.robots.push(r);
  };

  for (const r of robots) {
    if (by === 'site') push(siteKey(r), siteLabel(r), r);
    else if (by === 'link') push(r.liveness, LIVENESS_STATE[r.liveness].label, r);
    else if (by === 'mode') push(r.status, STATUS_STATE[r.status].label, r);
    else {
      /* A robot is filed under the LOUDEST state in its queue: one command on
         the wire and unacknowledged outranks three still sitting in the
         cloud, because only the first one is already beyond recall. */
      const q = tasking.get(r.robot_id) ?? [];
      const key = q.some((c) => c.status === 'sent')
        ? 'sent'
        : q.length > 0
          ? 'pending'
          : 'none';
      push(key, TASK_LABEL[key] ?? key, r);
    }
  }

  if (by === 'task') {
    for (const g of buckets.values()) {
      if (g.key === 'none') continue;
      /* Only the commands actually in THIS state count. A robot filed under
         `sent` may also be holding queued ones, and letting a 4-hour queued
         command set the badge on the acknowledged-wire group would print a
         number that is true of neither. */
      let oldest: number | null = null;
      for (const r of g.robots) {
        for (const c of tasking.get(r.robot_id) ?? []) {
          if (c.status !== g.key) continue;
          const t = Date.parse(c.issued_at);
          if (!Number.isNaN(t) && (oldest === null || t < oldest)) oldest = t;
        }
      }
      g.issuedAt = oldest;
    }
  }

  const out = [...buckets.values()];
  if (by === 'site') {
    /* Stable and alphabetical. A site must not jump up the rail because one
       of its robots faulted — an operator navigates this list by muscle. */
    out.sort((a, b) => a.label.localeCompare(b.label));
  } else {
    const order = by === 'link' ? LINK_ORDER : by === 'mode' ? MODE_ORDER : TASK_ORDER;
    out.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  }
  for (const g of out) g.robots.sort((a, b) => a.robot_id.localeCompare(b.robot_id));
  return out;
}

/* --------------------------------------------------------------------------
   Leaflet instance
   -------------------------------------------------------------------------- */

type TileState = 'pending' | 'ok' | 'unavailable';

interface MapHandle {
  map: L.Map | null;
  pane: HTMLElement | null;
  /** Bumped whenever the projection changed — zoom, reset, resize, pan end. */
  view: number;
  zoom: number;
  tiles: TileState;
}

function useLeafletMap(
  hostRef: MutableRefObject<HTMLDivElement | null>,
  showLabels: boolean,
  onBackgroundClick: () => void,
): MapHandle {
  const [map, setMap] = useState<L.Map | null>(null);
  const [pane, setPane] = useState<HTMLElement | null>(null);
  const [view, setView] = useState(0);
  const [zoom, setZoom] = useState(3);
  const [tiles, setTiles] = useState<TileState>('pending');
  const labelLayer = useRef<L.TileLayer | null>(null);
  const bgClick = useRef(onBackgroundClick);
  bgClick.current = onBackgroundClick;

  useEffect(() => {
    const host = hostRef.current;
    if (host === null) return;

    const instance = L.map(host, {
      center: [40, -40],
      zoom: 3,
      minZoom: 2,
      maxZoom: 19,
      zoomControl: false,
      attributionControl: false,
      /* An ops console does not animate its viewport. A 250ms zoom is 250ms
         in which the overlay and the tiles disagree about where north is. */
      zoomAnimation: false,
      fadeAnimation: false,
      markerZoomAnimation: false,
      worldCopyJump: true,
      /* Quarter-level zoom snapping, so a `fitBounds` lands ON the subject
         instead of on the whole-integer level below it. At integer snapping a
         fleet spread over 10 km in a 950x475 stage fits at z13 and leaves
         two thirds of the picture empty, because z14 is exactly twice as
         close and does not fit. The camera controls still step whole levels
         (`zoomDelta`); only the fits use the finer ladder. */
      zoomSnap: 0.25,
      zoomDelta: 1,
    });

    const base = L.tileLayer(TILE_URL, {
      maxZoom: 19,
      maxNativeZoom: TILE_MAX_NATIVE,
      className: 'f3-basetile',
      attribution: ATTRIBUTION,
    });

    /* Basemap health, and it has to be right in both directions: claiming a
       basemap that is not there hides the graticule the operator needs, and
       claiming an outage that is not happening is the console crying wolf
       about its own chrome.

       Leaflet aborts in-flight tile requests whenever the view changes — and
       the view changes once, immediately, when the fleet's bounds arrive — so
       a naive error count reports an outage on every cold start. Two guards:
       a single successful tile latches `ok` for good, and an outage is only
       declared after a grace window in which nothing at all loaded. */
    const started = Date.now();
    let loaded = 0;
    let failed = 0;
    base.on('tileload', () => {
      loaded += 1;
      setTiles('ok');
    });
    base.on('tileerror', () => {
      failed += 1;
      if (loaded === 0 && failed >= 4 && Date.now() - started > 4000) {
        setTiles('unavailable');
      }
    });
    /* Nothing arrived and nothing errored either — a hung endpoint looks the
       same to the operator as a refused one, so say so rather than sitting on
       "connecting" for ever. */
    const graceTimer = window.setTimeout(() => {
      if (loaded === 0) setTiles('unavailable');
    }, 12_000);
    base.addTo(instance);

    labelLayer.current = L.tileLayer(LABEL_URL, {
      maxZoom: 19,
      maxNativeZoom: TILE_MAX_NATIVE,
      className: 'f3-labeltile',
    });

    const overlayPane = instance.createPane('f3data');
    overlayPane.style.zIndex = '620';

    const bump = () => setView((v) => v + 1);
    const onZoom = () => {
      setZoom(instance.getZoom());
      bump();
    };
    instance.on('zoomend', onZoom);
    instance.on('viewreset', bump);
    instance.on('moveend', bump);
    instance.on('resize', bump);
    instance.on('click', () => bgClick.current());

    const ro = new ResizeObserver(() => instance.invalidateSize({ animate: false }));
    ro.observe(host);

    setMap(instance);
    setPane(overlayPane);
    setZoom(instance.getZoom());

    return () => {
      window.clearTimeout(graceTimer);
      ro.disconnect();
      instance.off();
      instance.remove();
      labelLayer.current = null;
      setMap(null);
      setPane(null);
    };
  }, [hostRef]);

  useEffect(() => {
    const layer = labelLayer.current;
    if (map === null || layer === null) return;
    if (showLabels) layer.addTo(map);
    else layer.remove();
  }, [map, showLabels]);

  return { map, pane, view, zoom, tiles };
}

/* --------------------------------------------------------------------------
   Overlay geometry
   -------------------------------------------------------------------------- */

/** Where a marker's chip may sit, relative to its own pin. */
type LabelSide = 'below' | 'above' | 'right' | 'left';
const SIDES: readonly LabelSide[] = ['below', 'above', 'right', 'left'];

/* --------------------------------------------------------------------------
   The label layer
   --------------------------------------------------------------------------
   EVERY string this map draws over the world — a robot's chip, a site's name,
   a capture session's name, the track's window caption, an off-screen site's
   range — is laid out by one placer and drawn in one layer above every vector
   overlay. Two rules make that layer readable rather than merely present:

     · a chip is OPAQUE. Not 92% opaque: a graticule dot-line, a capture
       bounding box or a dashed site hull leaking through at 8% is still a
       line drawn through the middle of a word.
     · a chip that cannot sit against its own anchor STEPS AWAY BY WHOLE ROWS
       and grows a 1px leader back to it. If even that is full, the chip is
       dropped and counted — and the count is printed in the panel header, so
       "27 drawn" never quietly means "24 named".
   -------------------------------------------------------------------------- */

/** A chip's pitch when it has to step off its anchor: its own height + a gap. */
const CHIP_ROW = LABEL_H + 4;
/** How many rows a displaced chip may walk before it is dropped instead. */
const CHIP_STEPS = 4;

/** A 1px hairline from a displaced chip back to the thing it names. */
interface Leader {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** A chip's resolved position, in LAYER coordinates. `ax`/`ay` are the
 *  fraction of the chip's own box that sits before the anchor point, so one
 *  render path covers a chip hung below a pin, beside it, or on a corner. */
interface Anchored {
  x: number;
  y: number;
  ax: number;
  ay: number;
  leader: Leader | null;
}

interface LabelPlan extends Anchored {
  compact: boolean;
}

interface Placed {
  robot: FixedRobot;
  p: L.Point;
}

/* --------------------------------------------------------------------------
   Tasking
   --------------------------------------------------------------------------
   A command that has been issued and not yet acknowledged is the one fact a
   fleet map owes an operator that a fleet TABLE cannot give them: where the
   machine has been told to go. `/api/commands/pending` carries the queue and,
   for a `move`, the waypoint itself — so the leg from the robot's current fix
   to its commanded position is real geometry off the real contract, not an
   illustration of one.

   THE BAR §11.9: `sent` is not `accepted` is not `executing`. This endpoint
   distinguishes `pending` (queued in the cloud, not yet on the wire) from
   `sent` (delivered, unacknowledged), and both are rendered as themselves.
   -------------------------------------------------------------------------- */

/** A command in flight, projected onto the stage. */
interface Order {
  id: number;
  robotId: string;
  /** The command verb, verbatim off the record — never mapped to a synonym. */
  command: string;
  status: string;
  issuedAt: number;
  lat: number;
  lon: number;
  /** The robot's current fix — the tail of the leg. */
  from: { lat: number; lon: number };
}

/** The commanded position carried by a `move`, or `null` for every other
 *  verb — a `pause` has no geometry and must not be given any. */
function orderWaypoint(c: Command): { lat: number; lon: number } | null {
  const wp = c.params['waypoint'];
  if (wp === null || typeof wp !== 'object') return null;
  const lat = payloadNumber(wp as Record<string, unknown>, 'lat');
  const lon = payloadNumber(wp as Record<string, unknown>, 'lon');
  if (lat === null || lon === null) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { lat, lon };
}

/* An acknowledgement is not a telemetry record, and it does not go stale on
   the same clock. Telemetry is expected every few seconds, so 2 minutes is
   already a fault; a command may legitimately sit on the wire while the robot
   finishes what it is doing. These are the limits this surface tones command
   ages against, and they are stated here rather than borrowed from the
   freshness ladder — borrowing would paint the whole queue amber and spend
   the alarm channel on normal operation. */
const ACK_WARN_MS = 5 * 60_000;
const ACK_BAD_MS = 30 * 60_000;

/** How old an unacknowledged command is allowed to get before it reads. */
function ackTone(ms: number | null): Tone {
  return levelTone(level(ms, ACK_WARN_MS, ACK_BAD_MS, true));
}

/** A stable empty list, so a robot with no orders does not re-render its card
 *  every poll on a fresh array identity. */
const EMPTY_COMMANDS: readonly Command[] = [];
/** The same, for the per-robot index. */
const EMPTY_TASKING: ReadonlyMap<string, readonly Command[]> = new Map();

/** Great-circle range between two fixes, in metres. */
function rangeM(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const R = 6_371_000;
  const p1 = (a.lat * Math.PI) / 180;
  const p2 = (b.lat * Math.PI) / 180;
  const dp = p2 - p1;
  const dl = ((b.lon - a.lon) * Math.PI) / 180;
  const h = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

interface Cluster {
  key: string;
  p: L.Point;
  members: Placed[];
  bounds: L.LatLngBounds;
}

function buildClusters(placed: readonly Placed[], radius: number): Cluster[] {
  const taken = new Set<string>();
  const out: Cluster[] = [];
  for (const seed of placed) {
    if (taken.has(seed.robot.robot_id)) continue;
    const members: Placed[] = [seed];
    taken.add(seed.robot.robot_id);
    for (const other of placed) {
      if (taken.has(other.robot.robot_id)) continue;
      if (seed.p.distanceTo(other.p) <= radius) {
        members.push(other);
        taken.add(other.robot.robot_id);
      }
    }
    let sx = 0;
    let sy = 0;
    for (const m of members) {
      sx += m.p.x;
      sy += m.p.y;
    }
    out.push({
      key: seed.robot.robot_id,
      p: L.point(sx / members.length, sy / members.length),
      members,
      bounds: L.latLngBounds(members.map((m) => L.latLng(m.robot.lat, m.robot.lon))),
    });
  }
  return out;
}

/** Degrees per graticule line, chosen so ~4–10 lines cross the viewport. */
const GRAT_STEPS: readonly number[] = [
  30, 10, 5, 2, 1, 0.5, 0.2, 0.1, 0.05, 0.02, 0.01, 0.005, 0.002, 0.001,
];

function gratStep(spanDeg: number): number {
  for (const s of GRAT_STEPS) if (spanDeg / s >= 4) return s;
  return 0.001;
}

function fmtDeg(v: number, step: number): string {
  const digits = step >= 1 ? 0 : step >= 0.1 ? 1 : step >= 0.01 ? 2 : 3;
  return `${v.toFixed(digits)}°`;
}

/* --------------------------------------------------------------------------
   Layers
   -------------------------------------------------------------------------- */

interface LayerState {
  labels: boolean;
  graticule: boolean;
  sites: boolean;
  tracks: boolean;
  robots: boolean;
  ids: boolean;
  track: boolean;
  sessions: boolean;
  commands: boolean;
}

/* Ordered the way Gotham orders a layer tree: ground first, then the
   persistent operational geometry, then the symbols on top of it. */
const LAYER_ROWS: readonly { key: keyof LayerState; label: string }[] = [
  { key: 'labels', label: 'Basemap labels' },
  { key: 'graticule', label: 'Graticule' },
  { key: 'sites', label: 'Site extents' },
  { key: 'tracks', label: 'Fleet tracks' },
  { key: 'track', label: 'Selected track' },
  { key: 'sessions', label: 'Capture sessions' },
  { key: 'commands', label: 'Commands' },
  { key: 'robots', label: 'Robots' },
  { key: 'ids', label: 'Robot labels' },
];

/* ==========================================================================
   The view
   ========================================================================== */

export function FleetMap() {
  const feedWord = useFeedWord();
  const robotsQ = useRobots();
  const {
    data: robots,
    isFetching,
    isError,
    dataUpdatedAt: robotsAt,
    errorUpdateCount: robotsErrs,
  } = robotsQ;
  /* THE ROSTER READ, IN THE CONSOLE'S ONE FAILURE LANGUAGE.

     Every census on this surface — `0/0 positioned`, `All sites 0`, the five
     health facets, `0 in view / 0 drawn`, the legend's whole grid — is counted
     from `robots ?? []`, and a map that has read nothing draws every one of
     them as a confident zero. `Fault 0` on a dead roster is the most dangerous
     of them: it is the reading an operator scans this row for.

     It also replaces the one place in this console where the browser's own
     words reached the screen: the roster printed `Fleet unavailable — Failed
     to fetch`, which tells an operator nothing about their fleet and
     everything about our transport. */
  const robotsRead = useRead(robotsQ, {
    path: '/api/robots',
    everyMs: 5_000,
    subject: 'the fleet register',
  });
  /* The fleet-wide command queue. Already fetched by the shell's own counter,
     so this is a cache read on the same key rather than a second request. */
  const {
    data: pendingRaw,
    dataUpdatedAt: cmdAt,
    isError: cmdErr,
  } = usePendingCommands();
  const { navigate } = useFleetRoute();
  const now = useNow();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [groupBy, setGroupBy] = useState<GroupBy>('site');
  const [facet, setFacet] = useState<Health | 'all'>('all');
  const [site, setSite] = useState<string>('all');
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set<string>());
  const [follow, setFollow] = useState(false);
  /* The track window. `span === null` is every record held; `end === null` is
     live — the window's leading edge tracks the newest fix that arrives. */
  const [winSpan, setWinSpan] = useState<number | null>(null);
  const [winEnd, setWinEnd] = useState<number | null>(null);
  /* The transport. `playing` advances the leading edge on its own; `rate` is
     how much recorded time one second of wall clock buys. */
  const [playing, setPlaying] = useState(false);
  /* 600× is the default because it is the rate this RECORDING wants: the
     agent reports on a ~2.4 min cadence, so at 600× each 250 ms tick of the
     playhead crosses roughly one telemetry record per robot and the picture
     changes every tick. At 60× the pins would sit still for ten ticks at a
     time, which reads as a stutter rather than as motion. */
  const [rate, setRate] = useState(600);
  /** The feed drill-down in the bottom-left corner, closed by default. */
  const [feedOpen, setFeedOpen] = useState(false);
  const [layersOpen, setLayersOpen] = useState(false);
  /** Chips the label placer had to drop this render — printed in the header. */
  const [hiddenLabels, setHiddenLabels] = useState(0);
  const [legendOpen, setLegendOpen] = useState(true);
  const [layers, setLayers] = useState<LayerState>({
    labels: true,
    graticule: true,
    sites: true,
    tracks: true,
    robots: true,
    ids: true,
    track: true,
    sessions: true,
    commands: true,
  });

  const hostRef = useRef<HTMLDivElement | null>(null);
  const fittedRef = useRef(false);
  const filterRef = useRef<string>('all|all');

  const select = useCallback((id: string | null) => {
    setSelectedId(id);
    if (id === null) setFollow(false);
  }, []);

  const deselect = useCallback(() => select(null), [select]);

  const { map, pane, view, zoom, tiles } = useLeafletMap(hostRef, layers.labels, deselect);

  /* The fleet as the API reports it NOW. Everything below reads `all`, which
     is this list while the window is live and the replayed projection of it
     while the window is not. */
  const rawAll = useMemo(() => robots ?? [], [robots]);

  /* ---- the always-on fleet track layer ---------------------------------
     Addressed by a STABLE, sorted list of every robot that has ever reported,
     so changing a filter re-draws the layer without re-fetching it. A robot
     that has never phoned home is excluded rather than asked. The list comes
     off the RAW records: a replayed link state must never change what this
     surface asks the API for. */
  const trackIds = useMemo(
    () =>
      rawAll
        .filter((r) => r.liveness !== 'provisioning')
        .map((r) => r.robot_id)
        .sort(),
    [rawAll],
  );
  /* Off live, the track layer is not optional — it is the only source of a
     past pose, so it is fetched whether or not its own layer is drawn. */
  const live = winEnd === null;
  const tracks = useFleetTracks(trackIds, layers.tracks || layers.sites || !live);

  /* ---- the track window, resolved -------------------------------------- */
  /* The recorded extent the window moves inside. Null until the first track
     lands; every readout below prints an em dash rather than a zero. */
  const recOldest = tracks.oldest;
  const recNewest = tracks.newest;
  const winEndMs = winEnd ?? recNewest;
  const winStartMs =
    winEndMs === null
      ? null
      : winSpan === null
        ? (recOldest ?? winEndMs)
        : Math.max(recOldest ?? winEndMs - winSpan, winEndMs - winSpan);
  const inWindow = useCallback(
    (t: number) =>
      winStartMs === null || winEndMs === null ? true : t >= winStartMs && t <= winEndMs,
    [winStartMs, winEndMs],
  );
  /* The whole recorded extent the band paints — what the window moves inside.
     Measured off the records held, never quoted from a nominal window. */
  const recSpan = recOldest !== null && recNewest !== null ? recNewest - recOldest : null;

  /* ---- the instant this surface is showing -----------------------------
     One clock for the whole view. Live, it is the wall clock and it ticks;
     scrubbed, it is the window's leading edge and it is STILL — which is the
     visible difference between a live surface and a replayed one. Every age,
     every pose, every count below is measured against it. */
  const replay = useMemo(
    () => (live || winEndMs === null ? null : replayFleet(rawAll, tracks.byId, winEndMs)),
    [live, winEndMs, rawAll, tracks.byId],
  );
  const asOf = replay === null || winEndMs === null ? now : winEndMs;
  /* Health is a property of the record as READ, so it is resolved on the poll
     cadence rather than on the 1 Hz age ticker: re-deriving 168 readings every
     second to advance a 30-day cert threshold is churn, not accuracy, and it
     would re-cluster the whole stage on every tick. Off live the clock is the
     window's leading edge and is exact. */
  const healthAt =
    replay === null || winEndMs === null ? Math.floor(now / HEALTH_TICK_MS) * HEALTH_TICK_MS : winEndMs;
  const all = useMemo(
    () => stampHealth(replay === null ? rawAll : replay.robots, healthAt),
    [replay, rawAll, healthAt],
  );

  /* ---- facets and filtering --------------------------------------------
     Each facet group counts with the OTHER filters applied, so `Fremont 9`
     drops to `Fremont 1` the moment the health facet says Fault. A count
     that ignores the filter beside it is a count you have to distrust. */

  const matchesQuery = useCallback(
    (r: MapRobot) => {
      const q = query.trim().toLowerCase();
      if (q === '') return true;
      return (
        r.robot_id.toLowerCase().includes(q) ||
        (r.name ?? '').toLowerCase().includes(q) ||
        r.morphology.toLowerCase().includes(q) ||
        siteLabel(r).toLowerCase().includes(q)
      );
    },
    [query],
  );

  /* Everything the site selector's counts are computed over. */
  const forSiteCounts = useMemo(
    () => all.filter((r) => matchesQuery(r) && (facet === 'all' || healthOf(r) === facet)),
    [all, facet, matchesQuery],
  );

  /* Everything the health selector's counts are computed over. */
  const forHealthCounts = useMemo(
    () => all.filter((r) => matchesQuery(r) && (site === 'all' || siteKey(r) === site)),
    [all, site, matchesQuery],
  );

  const counts = useMemo(() => {
    const c: Record<Health, number> = {
      fault: 0,
      nodata: 0,
      held: 0,
      notcurrent: 0,
      nominal: 0,
    };
    for (const r of forHealthCounts) c[healthOf(r)] += 1;
    return c;
  }, [forHealthCounts]);

  /* Sites, in the order the rail lists them, each carrying its own count and
     its worst reading — so the selector doubles as a distribution. */
  const sites = useMemo(() => {
    const bySite = new Map<string, MapRobot[]>();
    for (const r of all) {
      const k = siteKey(r);
      const list = bySite.get(k);
      if (list === undefined) bySite.set(k, [r]);
      else list.push(r);
    }
    return [...bySite.entries()]
      .map(([key, rs]) => ({
        key,
        label: siteLabel(rs[0] as MapRobot),
        short: shortSite(key),
        count: forSiteCounts.filter((r) => siteKey(r) === key).length,
        worst: worstHealth(rs),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [all, forSiteCounts]);

  const filtered = useMemo(
    () =>
      all.filter(
        (r) =>
          matchesQuery(r) &&
          (site === 'all' || siteKey(r) === site) &&
          (facet === 'all' || healthOf(r) === facet),
      ),
    [all, facet, site, matchesQuery],
  );

  /* ---- tasking ---------------------------------------------------------
     What the fleet has been TOLD to do, as opposed to what it is doing. The
     endpoint holds only what is in flight now — there is no history behind
     it — so off live this whole layer is empty rather than claiming a past
     queue it cannot read. */
  const inFlight = useMemo<readonly Command[]>(
    () => (live ? (pendingRaw ?? []) : []),
    [live, pendingRaw],
  );
  /* Scrubbing off live takes the queue's own source away with it, so a rail
     grouped by task would silently refile all 28 robots under "no orders" —
     an answer that is not merely empty but wrong. It falls back to site. */
  useEffect(() => {
    if (!live) setGroupBy((g) => (g === 'task' ? 'site' : g));
  }, [live]);

  /** Every in-flight command, by robot, oldest first. */
  const tasking = useMemo(() => {
    const m = new Map<string, Command[]>();
    for (const c of inFlight) {
      const l = m.get(c.robot_id);
      if (l === undefined) m.set(c.robot_id, [c]);
      else l.push(c);
    }
    for (const l of m.values()) l.sort((a, b) => Date.parse(a.issued_at) - Date.parse(b.issued_at));
    return m;
  }, [inFlight]);
  const groups = useMemo(
    () => groupRobots(filtered, groupBy, tasking),
    [filtered, groupBy, tasking],
  );

  const selected = useMemo(
    () => all.find((r) => r.robot_id === selectedId) ?? null,
    [all, selectedId],
  );

  /* Fixes per bucket across the whole recorded extent, and how many distinct
     robots reported in each. A bucket with no records draws nothing, so a
     coverage hole is a hole rather than a smoothed-over flat line. */
  const presence = useMemo(() => {
    if (recOldest === null || recNewest === null || recNewest <= recOldest) return null;
    const step = (recNewest - recOldest) / PRESENCE_BUCKETS;
    const fixes = new Int32Array(PRESENCE_BUCKETS);
    const robots = new Int32Array(PRESENCE_BUCKETS);
    let seen = new Set<number>();
    let peak = 1;
    for (const pts of tracks.byId.values()) {
      seen = new Set<number>();
      for (const f of pts) {
        const i = Math.min(PRESENCE_BUCKETS - 1, Math.max(0, Math.floor((f.t - recOldest) / step)));
        fixes[i] = (fixes[i] ?? 0) + 1;
        if (!seen.has(i)) {
          seen.add(i);
          robots[i] = (robots[i] ?? 0) + 1;
        }
      }
    }
    for (let i = 0; i < PRESENCE_BUCKETS; i += 1) peak = Math.max(peak, robots[i] ?? 0);
    return { t0: recOldest, t1: recNewest, step, fixes, robots, peak };
  }, [tracks.byId, recOldest, recNewest]);

  /* ---- telemetry for the selected robot: the track and the vitals ------- */
  const {
    data: telemetry,
    isFetching: trackFetching,
    dataUpdatedAt: telemetryAt,
  } = useTelemetry(selected !== null ? selected.robot_id : undefined, {
    limit: SELECTED_LIMIT,
  });

  /* The API returns newest-first; a track is drawn oldest → newest. Off live
     the rows are CLIPPED at the window's leading edge, so the card, its
     sparkline and its vitals describe the same instant as the pin. */
  const ordered = useMemo(() => {
    const rows = [...(telemetry ?? [])].reverse();
    if (live || winEndMs === null) return rows;
    return rows.filter((t) => {
      const ms = Date.parse(t.ts);
      return Number.isNaN(ms) || ms <= winEndMs;
    });
  }, [telemetry, live, winEndMs]);

  const fixes = useMemo<readonly Fix[]>(() => toFixes(telemetry ?? []), [telemetry]);

  const latest = useMemo<TelemetryReading | null>(
    () => (ordered.length > 0 ? (ordered[ordered.length - 1] as TelemetryReading) : null),
    [ordered],
  );

  const battery = useMemo(
    () => ({ values: ordered.map((t) => t.battery_pct), times: ordered.map((t) => t.ts) }),
    [ordered],
  );

  /* ---- how long this robot has held its mode -----------------------------
     THE BAR §13 tell 19: a queue that does not show time-in-state is not a
     queue. The platform keeps no transition log, so this is measured off the
     unbroken RUN of telemetry records carrying the same `state` — real
     records, no interpolation. When that run reaches the oldest record held
     the true answer is older than what we can see, and the readout says so
     with a `≥` rather than quoting a number it cannot back. */
  const modeHeld = useMemo(() => {
    const last = ordered[ordered.length - 1];
    if (last === undefined) return null;
    const mode = asStatus(last.state);
    if (mode === null) return null;
    let i = ordered.length - 1;
    while (i > 0) {
      const prev = ordered[i - 1];
      if (prev === undefined || asStatus(prev.state) !== mode) break;
      i -= 1;
    }
    const first = ordered[i];
    if (first === undefined) return null;
    const t0 = Date.parse(first.ts);
    if (Number.isNaN(t0)) return null;
    return {
      mode,
      ms: Math.max(0, asOf - t0),
      /* The run runs off the end of the history held: a lower bound. */
      bounded: i === 0,
      records: ordered.length - i,
      since: t0,
    };
  }, [ordered, asOf]);

  /* ---- projection ------------------------------------------------------ */
  const positioned = useMemo(() => filtered.filter(hasFix), [filtered]);
  const unpositioned = useMemo(() => filtered.filter((r) => !hasFix(r)), [filtered]);
  const allPositioned = useMemo(() => all.filter(hasFix), [all]);

  /* Site extents come off EVERY positioned robot and its whole track, not off
     the filtered set — the ground a site works does not shrink because the
     roster is filtered to faults. */
  const extents = useMemo(
    () => (layers.sites ? buildSiteExtents(allPositioned, tracks.byId) : []),
    [layers.sites, allPositioned, tracks.byId],
  );

  /** In-flight commands belonging to the robots this filter is drawing. */
  const taskedCount = useMemo(() => {
    let n = 0;
    for (const r of positioned) n += tasking.get(r.robot_id)?.length ?? 0;
    return n;
  }, [positioned, tasking]);
  /** Robots wearing the tasking ring — every drawn robot carrying orders
   *  EXCEPT the selected one, whose queue is printed in full on its card and
   *  whose pin is already accent from edge to edge. The legend's count is of
   *  the rings actually on the stage, not of a superset it would have to be
   *  read against. */
  const taskedPins = useMemo(() => {
    let n = 0;
    for (const r of positioned) {
      if (r.robot_id === selectedId) continue;
      if ((tasking.get(r.robot_id)?.length ?? 0) > 0) n += 1;
    }
    return n;
  }, [positioned, tasking, selectedId]);
  /* The Commands layer owns everything tasking draws on the STAGE — the legs,
     the targets and the pins' rings. Turning a layer off has to turn all of
     it off; a toggle that leaves half its geometry behind is a toggle the
     operator stops trusting. The rail's grouping is not a map layer and is
     unaffected. */
  const mapTasking = useMemo<ReadonlyMap<string, readonly Command[]>>(
    () => (layers.commands ? tasking : EMPTY_TASKING),
    [layers.commands, tasking],
  );
  /** The subset that carries geometry: a commanded position to draw a leg to. */
  const orders = useMemo<Order[]>(() => {
    if (!layers.commands) return [];
    const byId = new Map(positioned.map((r) => [r.robot_id, r]));
    const out: Order[] = [];
    for (const c of inFlight) {
      const wp = orderWaypoint(c);
      if (wp === null) continue;
      const r = byId.get(c.robot_id);
      if (r === undefined) continue;
      out.push({
        id: c.id,
        robotId: c.robot_id,
        command: c.command,
        status: c.status,
        issuedAt: Date.parse(c.issued_at),
        lat: wp.lat,
        lon: wp.lon,
        from: { lat: r.lat, lon: r.lon },
      });
    }
    return out;
  }, [layers.commands, inFlight, positioned]);

  const placed = useMemo<Placed[]>(() => {
    if (map === null || !layers.robots) return [];
    void view;
    return positioned.map((r) => ({ robot: r, p: map.latLngToLayerPoint([r.lat, r.lon]) }));
  }, [map, view, positioned, layers.robots]);

  /* The ambient layer draws the tracks of the robots this filter matches,
     clipped to the window. A track whose window slice holds one vertex is a
     dot, not a path, and is dropped rather than drawn as a zero-length line.

     Each track carries its robot's OWN reading with it. A fleet history drawn
     in one grey is a picture of where the fleet has been; a fleet history
     drawn in the fleet's own state palette is a picture of WHICH ROBOT has
     been where — the faulted one's path is red on the way in, and you can see
     that before you click anything. */
  const drawnTracks = useMemo<AmbientTrack[]>(() => {
    if (!layers.tracks) return [];
    const out: AmbientTrack[] = [];
    for (const r of positioned) {
      const f = tracks.byId.get(r.robot_id);
      if (f === undefined) continue;
      const win = f.filter((x) => inWindow(x.t));
      if (win.length > 1) {
        out.push({ id: r.robot_id, fixes: win, tone: HEALTH_TONE[healthOf(r)] });
      }
    }
    return out;
  }, [layers.tracks, positioned, tracks.byId, inWindow]);

  const drawnVertices = useMemo(
    () => drawnTracks.reduce((n, t) => n + t.fixes.length, 0),
    [drawnTracks],
  );

  /* The wall-clock span the drawn vertices actually cover — which under a
     window narrower than the recording is the window, and under `All` is
     whatever the API returned. Never quoted, always measured. */
  const drawnSpan = useMemo(() => {
    let lo = Number.POSITIVE_INFINITY;
    let hi = Number.NEGATIVE_INFINITY;
    for (const t of drawnTracks) {
      const a = t.fixes[0];
      const b = t.fixes[t.fixes.length - 1];
      if (a !== undefined) lo = Math.min(lo, a.t);
      if (b !== undefined) hi = Math.max(hi, b.t);
    }
    return Number.isFinite(lo) && Number.isFinite(hi) ? hi - lo : null;
  }, [drawnTracks]);

  /* The selected robot's own track obeys the same window. */
  const winFixes = useMemo(() => fixes.filter((f) => inWindow(f.t)), [fixes, inWindow]);

  /* ---- capture sessions for the selected robot -------------------------- */
  const { data: scanList } = useRobotScans(selected?.robot_id);
  /* Off live the footprints are CLIPPED to the instant, like everything else
     on this surface: a capture that started at 11:00 must not be drawn on a
     map claiming to show 02:47, because a footprint is a claim that this
     ground has been mapped. A session with no start time cannot be placed on
     the clock at all, so it is drawn live and withheld in replay rather than
     guessed at. */
  const sessions = useMemo<readonly SessionExtent[]>(() => {
    if (!layers.sessions || selected === null) return [];
    const all = toSessions(scanList ?? []);
    if (live || winEndMs === null) return all;
    const at = winEndMs;
    return all.filter((e) => e.started !== null && e.started <= at);
  }, [layers.sessions, selected, scanList, live, winEndMs]);

  /* ---- the legend's numbers ---------------------------------------------
     Counted over the robots the map is actually DRAWING, not over the fleet.
     The facet chips in the toolbar answer "how many are there"; the legend is
     the key to the symbols in front of you and has to answer "how many of
     these are on this stage". A legend that reports 28 while one pin is drawn
     contradicts the surface it is explaining. THE BAR §13 tell 10. */
  const drawnCounts = useMemo(() => {
    const c: Record<Health, number> = {
      fault: 0,
      nodata: 0,
      held: 0,
      notcurrent: 0,
      nominal: 0,
    };
    for (const r of positioned) c[healthOf(r)] += 1;
    return c;
  }, [positioned]);

  /* …and, per class, how many the filter matched that the map could NOT draw.
     This is the whole reason the legend and the facet row are allowed to print
     different numbers: `No data 0` beside a toolbar reading `No data 1` is a
     contradiction unless the legend says where the missing one went. It reads
     `0 +1` and names the robot in its title. */
  const offStageCounts = useMemo(() => {
    const c: Record<Health, number> = {
      fault: 0,
      nodata: 0,
      held: 0,
      notcurrent: 0,
      nominal: 0,
    };
    for (const r of unpositioned) c[healthOf(r)] += 1;
    return c;
  }, [unpositioned]);
  const offStageIds = useMemo(() => {
    const m = new Map<Health, string[]>();
    for (const r of unpositioned) {
      const l = m.get(healthOf(r));
      if (l === undefined) m.set(healthOf(r), [r.robot_id]);
      else l.push(r.robot_id);
    }
    return m;
  }, [unpositioned]);

  /* The pin's OTHER axis: whether the fix under the symbol is current or old,
     and whether the robot reports a heading at all. Every row of this map's
     key carries the count of what is actually drawn — a legend with no
     numbers in it is decoration, not an instrument. */
  const drawnShape = useMemo(() => {
    let fresh = 0;
    let old = 0;
    let heading = 0;
    for (const r of positioned) {
      if (r.heading_deg !== null) heading += 1;
      if (isHollow(r)) continue;
      const a = seenAge(r, asOf);
      if (a !== null && a > SEEN_WARN_MS) old += 1;
      else fresh += 1;
    }
    return { fresh, old, heading };
  }, [positioned, asOf]);

  /* How many of each site's robots this filter is actually drawing. The extent
     itself is built from the WHOLE fleet — the ground a site works does not
     shrink because the roster is filtered to faults — but the count on its
     chip has to agree with the count in the toolbar, or it is a number the
     operator has to distrust. */
  const shownBySite = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of positioned) m.set(siteKey(r), (m.get(siteKey(r)) ?? 0) + 1);
    return m;
  }, [positioned]);

  const clusters = useMemo(() => buildClusters(placed, CLUSTER_RADIUS), [placed]);

  /* How many of the drawn markers are actually on screen. A map that says
     "27 robots" while showing four is answering a different question. */
  const inView = useMemo(() => {
    if (map === null) return 0;
    void view;
    const b = map.getBounds();
    return placed.filter((p) => b.contains(L.latLng(p.robot.lat, p.robot.lon))).length;
  }, [map, view, placed]);

  const fleetBounds = useCallback(() => {
    if (allPositioned.length === 0) return null;
    return L.latLngBounds(allPositioned.map((r) => L.latLng(r.lat, r.lon)));
  }, [allPositioned]);

  /* Open on the busiest site, not on the world.
     A fleet spread over three continents fits a whole-planet view as three
     dots in an ocean — that is THE BAR §13 tell 26, and it is the single
     easiest way for a geo view to convey nothing. The camera starts where
     the robots are; `Fit fleet` and the `All` site segment give the global
     view in one click, and the rail still lists every site. */
  useEffect(() => {
    if (map === null || fittedRef.current || allPositioned.length === 0) return;
    fittedRef.current = true;
    const bySite = new Map<string, FixedRobot[]>();
    for (const r of allPositioned) {
      const k = siteKey(r);
      const list = bySite.get(k);
      if (list === undefined) bySite.set(k, [r]);
      else list.push(r);
    }
    let best: FixedRobot[] | null = null;
    for (const list of bySite.values()) {
      if (best === null || list.length > best.length) best = list;
    }
    const open = best ?? allPositioned;
    map.fitBounds(L.latLngBounds(open.map((r) => L.latLng(r.lat, r.lon))), {
      ...stagePadding(map, false),
      maxZoom: 15,
    });
  }, [map, allPositioned]);

  /* Changing a filter moves the camera with it — a filter that leaves the map
     looking somewhere else is a filter that lies. Filter to "Fault" and the
     map has to show you the faults, not the city you happened to be over. */
  useEffect(() => {
    if (map === null || !fittedRef.current) return;
    const key = `${site}|${facet}`;
    if (filterRef.current === key) return;
    filterRef.current = key;
    const rs = positioned;
    if (rs.length === 0) return;
    map.fitBounds(L.latLngBounds(rs.map((r) => L.latLng(r.lat, r.lon))), {
      ...stagePadding(map, selectedId !== null),
      maxZoom: rs.length === 1 ? 17 : 16,
    });
    // `positioned` is the fit target, but only a deliberate filter press may
    // move the camera — a poll that changes a battery reading must not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, site, facet]);

  const fitFleet = useCallback(() => {
    const b = fleetBounds();
    if (map === null || b === null) return;
    map.fitBounds(b, stagePadding(map, selectedId !== null));
  }, [map, fleetBounds, selectedId]);

  /* Fit whatever the current filter matches. */
  const fitMatches = useCallback(() => {
    if (map === null || positioned.length === 0) return;
    map.fitBounds(L.latLngBounds(positioned.map((r) => L.latLng(r.lat, r.lon))), {
      ...stagePadding(map, selectedId !== null),
      maxZoom: positioned.length === 1 ? 17 : 16,
    });
  }, [map, positioned, selectedId]);

  /* Fit the map to one group — the fastest way from "which site" to "that
     site", and the reason the rail and the stage are one surface. */
  const fitGroup = useCallback(
    (g: Group) => {
      const withFix = g.robots.filter(hasFix);
      if (map === null || withFix.length === 0) return;
      const b = L.latLngBounds(withFix.map((r) => L.latLng(r.lat, r.lon)));
      map.fitBounds(b, { ...stagePadding(map, selectedId !== null), maxZoom: 16 });
    },
    [map, selectedId],
  );

  /* Follow mode — named, visible, and off unless the operator turned it on.
     Turning it on from a continental view zooms in once: following a robot
     across an ocean-sized viewport keeps it centred and shows nothing. */
  useEffect(() => {
    if (!follow || map === null || selected === null || !hasFix(selected)) return;
    if (map.getZoom() < 15) {
      map.setView([selected.lat, selected.lon], 16, { animate: false });
    }
    holdAt(map, selected.lat, selected.lon, true);
  }, [follow, map, selected]);

  /* Double-clicking a rail row zooms to the robot. "Centred" means centred in
     the map the operator can SEE — with the inspector open that is not the
     middle of the pane. */
  const centreOn = useCallback(
    (r: MapRobot) => {
      if (map === null || !hasFix(r)) return;
      map.setView([r.lat, r.lon], Math.max(map.getZoom(), 16), { animate: false });
      holdAt(map, r.lat, r.lon, selectedId !== null);
    },
    [map, selectedId],
  );

  /* Framing the selected robot means framing its history too — a track that
     spans 90 m is a single pixel at site zoom, and an operator asking to look
     at a robot is asking where it has been. */
  const frameSelection = useCallback(() => {
    if (map === null || selected === null || !hasFix(selected)) return;
    if (winFixes.length > 1) {
      /* Capped at the basemap's last real level plus one, exactly as a
         cluster's fit is. A 4 m track would otherwise pull the camera to z19
         and hand the operator an 8x-upscaled raster — blurred buildings that
         read as a lower-resolution world rather than a closer one. */
      map.fitBounds(L.latLngBounds(winFixes.map((f) => L.latLng(f.lat, f.lon))), {
        ...stagePadding(map, true),
        maxZoom: TILE_MAX_NATIVE + 1,
      });
      return;
    }
    centreOn(selected);
  }, [map, selected, winFixes, centreOn]);

  /* Selecting brings the robot into OPEN WATER, not merely onto the pane.
     The stage is not the rectangle it looks like: the inspector docks over
     its left edge, the legend and zoom stack own the bottom-right, the notice
     stack owns the bottom-left, and the marker itself carries a label chip
     that has to fit somewhere. Panning by the exact shortfall — the obvious
     implementation — parks the one robot you asked for flush against the
     frame, where the pane clips it and its own id runs off the edge.

     So: if the subject's whole footprint already sits inside the clear rect
     with a real margin and clear of every measured panel, the camera does not
     move at all. Otherwise it moves once, and lands the subject in the most
     open part of the stage. */
  const reveal = useCallback(
    (id: string | null) => {
      if (id === null || map === null) return;
      const r = all.find((x) => x.robot_id === id);
      if (r === undefined || !hasFix(r)) return;
      const cp = map.latLngToContainerPoint([r.lat, r.lon]);
      /* The card is about to mount but is not in the DOM yet, so it is the one
         occluder stated rather than measured. */
      const box = clearRect(map, true);
      const size = map.getSize();
      if (box.x1 <= box.x0 || box.y1 <= box.y0) {
        map.panTo([r.lat, r.lon], { animate: false });
        return;
      }
      /* "A real margin" = 15% of the pane, floored at 120 px, and never more
         than the clear rect can actually give. */
      const mx = Math.min(Math.max(120, size.x * 0.15), (box.x1 - box.x0) / 2);
      const my = Math.min(Math.max(120, size.y * 0.15), (box.y1 - box.y0) / 2);
      const settled =
        cp.x >= box.x0 + mx &&
        cp.x <= box.x1 - mx &&
        cp.y >= box.y0 + my &&
        cp.y <= box.y1 - my &&
        !stageChrome(map).some((c) => overlaps(subjectAt(cp), c));
      if (settled) return;
      const target = clearTarget(map, true);
      if (target === null) {
        map.panTo([r.lat, r.lon], { animate: false });
        return;
      }
      map.panBy([cp.x - target.x, cp.y - target.y], { animate: false });
    },
    [all, map],
  );

  const selectRobot = useCallback(
    (id: string | null) => {
      select(id);
      reveal(id);
    },
    [reveal, select],
  );


  /* Follow means "keep the camera on this robot as it moves". Nothing moves
     in replay, so the mode is released rather than left on and inert. */
  useEffect(() => {
    if (!live) setFollow(false);
  }, [live]);

  /* ---- the transport ----------------------------------------------------
     The one time axis is playable. Pressing play from live REWINDS by the
     window's own length and runs forward to the newest record — "watch the
     last hour of the fleet run" is the question this control exists to
     answer — and when the leading edge reaches the newest record the surface
     returns to live and the transport stops itself. Nothing here interpolates
     between records: the projection at each step is still the newest record
     at or before that instant. */
  const rewindPoint = useCallback((): number | null => {
    if (recOldest === null || recNewest === null) return null;
    const from = winSpan === null ? recOldest : recNewest - winSpan;
    return Math.max(recOldest, Math.min(recNewest - WINDOW_MIN_MS, from));
  }, [recOldest, recNewest, winSpan]);

  const togglePlay = useCallback(() => {
    setPlaying((was) => {
      if (was) return false;
      if (recOldest === null || recNewest === null) return false;
      if (live) {
        const from = rewindPoint();
        if (from === null) return false;
        setWinEnd(from);
      }
      return true;
    });
  }, [live, recOldest, recNewest, rewindPoint]);

  useEffect(() => {
    if (!playing) return;
    if (recNewest === null) return;
    const id = window.setInterval(() => {
      setWinEnd((prev) => {
        const cur = prev ?? recNewest;
        const next = cur + PLAY_TICK_MS * rate;
        if (next >= recNewest) {
          setPlaying(false);
          return null;
        }
        return next;
      });
    }, PLAY_TICK_MS);
    return () => window.clearInterval(id);
  }, [playing, rate, recNewest]);

  /* Any manual seek takes the transport back off autopilot: a playhead that
     keeps running after the operator grabbed it is a control fighting its
     user. */
  const seekTo = useCallback((ms: number) => {
    setPlaying(false);
    setWinEnd(ms);
  }, []);

  const goLive = useCallback(() => {
    setPlaying(false);
    setWinEnd(null);
  }, []);

  /** Whole presence buckets — the recording's own resolution, not a round
   *  number of seconds that lands between two records. */
  const stepBy = useCallback(
    (buckets: number) => {
      if (recOldest === null || recNewest === null) return;
      const step = (recNewest - recOldest) / PRESENCE_BUCKETS;
      const cur = winEnd ?? recNewest;
      const next = cur + buckets * step;
      setPlaying(false);
      if (next >= recNewest) setWinEnd(null);
      else setWinEnd(Math.max(recOldest + WINDOW_MIN_MS, next));
    },
    [recOldest, recNewest, winEnd],
  );

  const cycleRate = useCallback(() => {
    setRate((r) => RATES[(RATES.indexOf(r) + 1) % RATES.length] ?? 1);
  }, []);

  /* How much recorded time a press of play would actually run through. It is
     printed in the control's own title with the wall-clock cost beside it, so
     "play" is never a button whose duration the operator discovers by
     waiting: a 17 h recording at 600× is 1m 44s and the control says so. */
  const playRunMs = useMemo(() => {
    if (recNewest === null) return null;
    const from = playing ? (winEnd ?? recNewest) : (rewindPoint() ?? recNewest);
    return Math.max(0, recNewest - from);
  }, [playing, winEnd, recNewest, rewindPoint]);

  /* The surface's keys, and they are the transport's keys because the
     transport is the surface's clock: `/` filter, Esc deselect, space
     play/pause, `,`/`.` step a bucket, `L` go live. Each one is printed in
     the affordance it drives (THE BAR §6), never only here. */
  const keysRef = useRef({ togglePlay, stepBy, goLive });
  keysRef.current = { togglePlay, stepBy, goLive };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing =
        t !== null && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === '/' && !typing) {
        e.preventDefault();
        document.getElementById(FILTER_ID)?.focus();
      } else if (e.key === 'Escape') {
        if (typing) (t as HTMLElement).blur();
        select(null);
      } else if (typing) {
        return;
        /* Space is a button's own activation key, so it belongs to whatever
           the operator is standing on rather than to the transport. */
      } else if (e.key === ' ' && t?.tagName !== 'BUTTON') {
        e.preventDefault();
        keysRef.current.togglePlay();
      } else if (e.key === ',') {
        e.preventDefault();
        keysRef.current.stepBy(-1);
      } else if (e.key === '.') {
        e.preventDefault();
        keysRef.current.stepBy(1);
      } else if (e.key === 'l' || e.key === 'L') {
        e.preventDefault();
        keysRef.current.goLive();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [select]);

  const toggleGroup = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleLayer = useCallback((key: keyof LayerState) => {
    setLayers((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  /* The graticule is not optional when there is no basemap under it. */
  const gratOn = layers.graticule || tiles === 'unavailable';

  /* The stalest pose actually drawn — the health line describes the picture
     in front of the operator, not the fleet in the abstract. */
  const oldestFix = useMemo(() => {
    let worst: number | null = null;
    for (const r of positioned) {
      const age = seenAge(r, asOf);
      if (age !== null && (worst === null || age > worst)) worst = age;
    }
    return worst;
  }, [positioned, asOf]);

  /* Why each robot the filter matched is NOT on the map. Live there is one
     reason — the record carries no pose. Off live there are two more, and
     they are different facts: "it was not reporting then" is a reading, "we
     hold no history for it" is a gap in this console. Collapsing the three
     into one line would make the second look like the first. */
  const missing = useMemo(() => {
    const noFix: MapRobot[] = [];
    const before: MapRobot[] = [];
    const unheld: MapRobot[] = [];
    for (const r of unpositioned) {
      const m = replay?.miss.get(r.robot_id);
      if (m === 'before') before.push(r);
      else if (m === 'unheld') unheld.push(r);
      else noFix.push(r);
    }
    const at = zulu(winEndMs ?? undefined);
    return [
      {
        key: 'nofix',
        tone: 'offline' as const,
        robots: noFix,
        text: 'with no fix — not drawn',
        why: 'These robot records carry no position.',
      },
      {
        key: 'before',
        tone: 'offline' as const,
        robots: before,
        text: `no record held at ${at} — not drawn`,
        why: `This console holds the newest ${TRACK_LIMIT} telemetry records per robot, and for these the oldest one is later than ${at}. It is not a claim that they were silent — it is the edge of the history pulled.`,
      },
      {
        key: 'unheld',
        tone: 'warn' as const,
        robots: unheld,
        text: 'no history held — not drawn',
        why: 'The telemetry request for these robots is still in flight or failed.',
      },
    ].filter((g) => g.robots.length > 0);
  }, [unpositioned, replay, winEndMs]);

  /* ---- the feeds behind this surface ------------------------------------
     Four sources, four cadences, four failure modes. Measured off the query
     cache; the realtime row reads its own config and says why it is not
     running rather than rendering a dead light. */
  const feeds = useMemo<FeedSource[]>(() => {
    const rt = realtimeConfig();
    return [
      {
        key: 'robots',
        label: 'robots',
        source: robotsErrs > 0 ? `/api/robots · ${robotsErrs} failed` : '/api/robots',
        count: String(rawAll.length),
        every: '5s',
        at: robotsAt > 0 ? robotsAt : null,
        tone: isError ? 'error' : 'nominal',
        hollow: isError,
        note: isError
          ? `Fleet request failing — ${robotsErrs} error${robotsErrs === 1 ? '' : 's'} since load`
          : 'The robot records themselves — identity, mode, link, battery, pose',
      },
      {
        key: 'tracks',
        label: 'fleet track',
        source:
          tracks.failed > 0
            ? `…/telemetry ×${trackIds.length} · ${tracks.failed} failed`
            : tracks.pending > 0
              ? `…/telemetry ×${trackIds.length} · ${tracks.pending} in flight`
              : `…/telemetry ×${trackIds.length}`,
        count: String(tracks.fixes),
        every: '20s',
        at: tracks.freshest,
        atSlow: tracks.stalest,
        tone: tracks.failed > 0 ? 'error' : tracks.pending > 0 ? 'warn' : 'nominal',
        hollow: tracks.loaded === 0,
        note: `One request per robot, newest ${TRACK_LIMIT} records each. ${tracks.loaded} answered, ${tracks.pending} in flight, ${tracks.failed} failed. Where two ages are shown they are the freshest and the stalest response in the fan-out — the spread is what a single figure would hide.`,
      },
      {
        key: 'selection',
        label: 'selection',
        source: selected === null ? 'not subscribed' : `…/${selected.robot_id}/telemetry`,
        count: selected === null ? NO_VALUE : String((telemetry ?? []).length),
        every: selected === null ? NO_VALUE : '5s',
        at: selected === null ? null : telemetryAt > 0 ? telemetryAt : null,
        tone: selected === null ? 'offline' : 'nominal',
        hollow: selected === null,
        note:
          selected === null
            ? 'No robot selected — this feed is not subscribed'
            : `The selected robot's own history, newest ${SELECTED_LIMIT} records`,
      },
      {
        key: 'commands',
        label: 'command queue',
        source: cmdErr ? '/api/commands/pending · failing' : '/api/commands/pending',
        count: String((pendingRaw ?? []).length),
        every: '3s',
        at: cmdAt > 0 ? cmdAt : null,
        tone: cmdErr ? 'error' : 'nominal',
        hollow: cmdErr,
        note: cmdErr
          ? 'The pending-command request is failing — the tasking layer is not being drawn from live data'
          : 'Commands issued and not yet acknowledged, fleet-wide. Holds no history, so the tasking layer is empty off live.',
      },
      {
        key: 'realtime',
        label: 'realtime',
        source: rt === null ? 'VITE_IOT_ENDPOINT unset' : `mqtt · ${rt.endpoint}`,
        count: NO_VALUE,
        every: rt === null ? 'poll' : 'push',
        at: null,
        tone: 'offline',
        hollow: true,
        note:
          rt === null
            ? 'MQTT over WebSocket against AWS IoT, gated on VITE_IOT_ENDPOINT and a Cognito ID token. Neither is configured here, so every surface on this console falls back to polling — which is what both panel tags say.'
            : 'Realtime transport configured',
      },
    ];
  }, [
    rawAll.length,
    robotsAt,
    robotsErrs,
    isError,
    trackIds.length,
    tracks.fixes,
    tracks.freshest,
    tracks.stalest,
    tracks.loaded,
    tracks.pending,
    tracks.failed,
    selected,
    telemetry,
    telemetryAt,
    pendingRaw,
    cmdAt,
    cmdErr,
  ]);

  const simulated = useMemo(
    () => all.filter((r) => metaString(r, 'kind') === 'sim').length,
    [all],
  );
  const filtering = facet !== 'all' || site !== 'all' || query.trim() !== '';

  return (
    <SurfaceShell
      eyebrow="Fleet"
      title="Live map"
      subtitle={
        <>
          {/* The identity row states the ONE fact only it can state — how much
              of the fleet this map is able to draw. `3 sites` and `2 faulted`
              used to sit here too and both are already in the facet row 40px
              below, at counts that move with the filter; two authorities for
              one number is how an operator learns to distrust both. */}
          <span
            className={styles.kpi}
            title="Robots with a position, of every robot registered — the rest are named in the map's bottom-left notices"
          >
            <span className={styles.kpiNum}>
              <Figure of={robotsRead} value={allPositioned.length} />
            </span>
            <span className={styles.kpiOf}>/<Figure of={robotsRead} value={all.length} /></span>
            <span className={styles.kpiLabel}>positioned</span>
          </span>
          {simulated > 0 && <Chip tone="accent">Simulated {simulated}</Chip>}
        </>
      }
    >
      <div className={styles.layout}>
        <Toolbar label="Map filters">
          <SearchInput
            id={FILTER_ID}
            placeholder="Filter roster"
            aria-label="Filter roster by id, name, morphology or site"
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            className={styles.search}
          />
          <span className={styles.kbd} aria-hidden="true">
            /
          </span>
          <ToolbarDivider />
          <ToolbarLabel>Site</ToolbarLabel>
          <SegmentedControl<string>
            label="Site — filters the roster and moves the camera"
            value={site}
            onChange={setSite}
            items={[
              { value: 'all', label: 'All sites', count: figure(robotsRead, forSiteCounts.length) },
              ...sites.map((s) => ({
                value: s.key,
                label: s.short,
                count: figure(robotsRead, s.count),
                /* A facet with nothing behind it is a dead end, not a choice —
                   but an UNREAD facet is not a dead end, it is unknown, and
                   greying every control on the row is the map telling an
                   operator their fleet has no sites. */
                disabled: !unread(robotsRead) && s.count === 0,
              })),
            ]}
          />
          <ToolbarDivider />
          <ToolbarLabel>Health</ToolbarLabel>
          <SegmentedControl<Health | 'all'>
            label={`Filter by health — the worst of ${HEALTH_BASIS} per robot, the same reading the app strip counts`}
            value={facet}
            onChange={setFacet}
            items={HEALTH_FACETS.map((h) =>
              h === 'all'
                ? { value: h, label: 'All', count: figure(robotsRead, forHealthCounts.length) }
                : {
                    value: h,
                    label: HEALTH_LABEL[h],
                    count: figure(robotsRead, counts[h]),
                    /* A facet with nothing behind it is a dead end, not a
                       choice — the same rule the site selector follows. The
                       one currently applied is never disabled, or scrubbing a
                       filtered view into an empty instant would trap it. */
                    disabled: !unread(robotsRead) && counts[h] === 0 && facet !== h,
                  },
            )}
          />
          {/* What these four counts COUNT, printed rather than left to a
              tooltip (THE BAR §8, bonus fast-fail). The app strip publishes
              the same four words 60px above this row; naming the basis is how
              an operator can see the two are one question and not two. */}
          <span
            className={styles.basis}
            title={`Each robot is classed by the worst of its six fleet readings — ${HEALTH_BASIS} — which is the same rule the app strip's pulse counts and the operations exception list use. A robot flagged on more than one reads as its worst.`}
          >
            {/* Narrows, never disappears: the line that says what these four
                counts count is the one piece of this row a narrow viewport may
                not silently drop. */}
            <span className={styles.basisLong}>worst of {HEALTH_BASIS}</span>
            <span className={styles.basisShort}>worst of {FLEET_METRICS.length}</span>
          </span>
          {/* THE ONE page-level fact that governs every count to its left, and
              it belongs HERE rather than in the shell's identity block: the app
              strip collapses that block to zero height, so a `Replay` chip left
              in it is a chip nobody sees — while the strip's own pulse counts,
              which are always live, sit 60px above these facets wearing the
              same four words. Scrubbed, the two are answering the same question
              at two different instants, and this is the chip that says so. */}
          {!live && winEndMs !== null && (
            <>
              <ToolbarDivider />
              <Chip
                tone="warn"
                mono
                icon="history"
                onDismiss={goLive}
                dismissLabel="Return to live"
                title={`Replay — every count and every marker on this surface is the fleet as it read at ${zulu(
                  winEndMs,
                )}, including the health counts to the left. The app strip's own counts stay live. Dismiss to return to live.`}
              >
                Replay {zulu(winEndMs)}
              </Chip>
            </>
          )}
          {/* Page-level provenance. It belongs in the identity block, and
              SurfaceShell has one — but the app strip collapses that header to
              zero height and keeps only its action slot, so a chip left there
              is a chip nobody sees. It lives on the surface's own top row
              instead. THE BAR §11.10: a fixture is never unlabelled. */}
          {simulated > 0 && (
            <>
              <ToolbarDivider />
              <Chip tone="accent" title="Robots running in Isaac Sim, not physical machines">
                Simulated {simulated}
              </Chip>
            </>
          )}
          {filtering && (
            <>
              <ToolbarDivider />
              <Chip
                mono
                onDismiss={() => {
                  setFacet('all');
                  setSite('all');
                  setQuery('');
                }}
                dismissLabel="Clear roster filters"
              >
                {filtered.length}/{all.length} shown
              </Chip>
            </>
          )}
          <ToolbarSpacer />
        </Toolbar>

        <PanelGrid cols="var(--w-rail) minmax(0, 1fr)" className={styles.panes}>
          {/* ---------------- roster rail ---------------- */}
          <Panel
            title="Roster"
            meta={
              /* Both panels answer freshness for THEMSELVES, and both are
                 bound to the same clock: scrub the band and this flips to
                 `Polling off 07:00:37Z` because the rail is then listing the
                 fleet as it read at that instant. THE BAR §5/§6 — freshness
                 is per panel, and it changes the moment you leave live. */
              <LiveTag
                live={live && !isError}
                at={live ? null : winEndMs}
                label={feedWord}
              />
            }
            loading={isFetching && all.length === 0}
            pad="none"
          >
            {/* The rail's two numeric columns name themselves. A header that
                says what a column is beats a tooltip that explains it —
                THE BAR §8, bonus fast-fail. */}
            {/* The rail's own controls sit in a 24px row above its columns,
                not in the 28px panel header. Off live that header's liveness
                tag grows to carry a Zulu instant, and a panel that drops its
                own title to make room for a control has lost its identity —
                THE BAR §6. Total furniture above the first row: 70px, inside
                §11.12's 96px budget. */}
            {!isError && all.length > 0 && (
              <div className={styles.rosterTools}>
                <ToolbarLabel>Group</ToolbarLabel>
                <SegmentedControl<GroupBy>
                  label="Group roster by"
                  value={groupBy}
                  onChange={setGroupBy}
                  items={[
                    { value: 'site', label: 'Site' },
                    { value: 'link', label: 'Link' },
                    { value: 'mode', label: 'Mode' },
                    {
                      value: 'task',
                      label: 'Task',
                      /* Off live the queue endpoint has nothing to say — it
                         holds no history — so the grouping that reads it is a
                         dead end rather than an empty one. */
                      disabled: !live,
                    },
                  ]}
                />
                <span className={styles.spacer} />
                <span
                  className={styles.headCount}
                  title={`${filtered.length} of ${all.length} robots match this filter`}
                >
                  {filtered.length}
                  {filtered.length !== all.length && (
                    <span className={styles.headCountOf}>/{all.length}</span>
                  )}
                </span>
              </div>
            )}
            {!isError && all.length > 0 && (
              <div className={styles.rosterHead}>
                <span className={styles.headId}>Robot</span>
                <span className={styles.spacer} />
                <span
                  className={styles.headMeter}
                  title="Battery charge, with its 15% limit marked on the track"
                >
                  Battery
                </span>
                <span
                  className={styles.headAge}
                  title={
                    live
                      ? 'Time since the last telemetry record'
                      : "Age of each robot's newest record at the window's leading edge"
                  }
                >
                  Age
                </span>
              </div>
            )}
            {/* `no reply /api/robots · 4 lost · last good 15:52:06Z` — the
                console's own fault line, in the console's own words, where a
                raw `TypeError: Failed to fetch` used to land on an operator. */}
            {robotsRead.fault !== null && <FaultLine read={robotsRead} />}
            {!isError && all.length === 0 && !isFetching && (
              <Empty of={robotsRead}>No robots registered.</Empty>
            )}
            {!isError && all.length > 0 && filtered.length === 0 && (
              <Empty detail={facet === 'all' ? query : HEALTH_LABEL[facet]}>
                No robot matches this filter.
              </Empty>
            )}
            <RosterList
              groups={groups}
              collapsed={collapsed}
              onToggleGroup={toggleGroup}
              onFitGroup={fitGroup}
              selectedId={selectedId}
              hoverId={hoverId}
              onSelect={selectRobot}
              onHover={setHoverId}
              onCentre={centreOn}
              now={asOf}
            />
          </Panel>

          {/* ---------------- map stage ---------------- */}
          <Panel
            viewport
            pad="none"
            scroll={false}
            title="Map"
            meta={
              <>
                <span title="Current zoom level">z{Math.round(zoom * 10) / 10}</span>
                <span title="Markers inside the viewport, of the markers drawn">
                  <Figure of={robotsRead} value={inView} /> in view
                  <span className={styles.metaDim}>
                    {' / '}<Figure of={robotsRead} value={placed.length} /> drawn
                  </span>
                </span>
                {/* The map does not print a label it cannot print cleanly, so
                    it says how many it withheld. Zoom in and this goes to
                    zero. THE BAR §13 tell 24 — a readout that does not say
                    how much it is not telling you is not a readout. */}
                {hiddenLabels > 0 && (
                  <span
                    className={styles.metaHidden}
                    title="Labels the placer could not fit without printing over another label, a marker or a panel. Zoom in to recover them."
                  >
                    {hiddenLabels} label{hiddenLabels === 1 ? '' : 's'} hidden
                  </span>
                )}
                <LiveTag live={live && !isError} at={live ? null : winEndMs} label={feedWord} />
              </>
            }
            tools={
              <>
                <IconButton
                  icon="layers"
                  label="Layers"
                  active={layersOpen}
                  onClick={() => setLayersOpen((v) => !v)}
                />
                <IconButton
                  icon={legendOpen ? 'eye' : 'eye-off'}
                  label={legendOpen ? 'Hide legend' : 'Show legend'}
                  active={legendOpen}
                  onClick={() => setLegendOpen((v) => !v)}
                />
                <IconButton icon="crosshair" label="Fit fleet" onClick={fitFleet} />
              </>
            }
            className={styles.mapPanel}
          >
            <div className={styles.mapBody}>
            {/* `data-upscaled` steps the basemap back once the view is past
                the last level the source publishes: an interpolated raster
                should not be as loud as a real one, and the graticule and the
                overlay are still exact at any zoom. */}
            <div className={styles.stage} data-upscaled={zoom > TILE_MAX_NATIVE}>
              <div ref={hostRef} className={styles.canvas} />

              {pane !== null &&
                map !== null &&
                createPortal(
                  <MapOverlay
                    map={map}
                    view={view}
                    zoom={zoom}
                    clusters={clusters}
                    layers={layers}
                    graticule={gratOn}
                    extents={extents}
                    shownBySite={shownBySite}
                    ambient={drawnTracks}
                    clearLeft={selected === null ? 0 : CARD_CLEAR}
                    fixes={layers.track && selected !== null ? winFixes : []}
                    headAt={winEndMs ?? now}
                    tailAt={winStartMs ?? (winEndMs ?? now) - HEAD_MS}
                    sessions={sessions}
                    orders={orders}
                    tasking={mapTasking}
                    selectedId={selectedId}
                    hoverId={hoverId}
                    onSelect={selectRobot}
                    onHover={setHoverId}
                    onHidden={setHiddenLabels}
                    now={asOf}
                  />,
                  pane,
                )}

              {/* corner furniture — fixed positions, stroke-only */}
              <div className={styles.cornerTR} data-furniture="true" data-edge="true">
                {layersOpen && (
                  <LayerTree
                    layers={layers}
                    forcedGraticule={tiles === 'unavailable'}
                    counts={{
                      sites: extents.length,
                      robots: placed.length,
                      tracks: drawnVertices,
                      track: selected === null ? 0 : winFixes.length,
                      sessions: sessions.length,
                      commands: taskedCount,
                    }}
                    live={live}
                    onToggle={toggleLayer}
                    onClose={() => setLayersOpen(false)}
                  />
                )}
              </div>

              <div className={styles.cornerBR} data-furniture="true" data-edge="true">
                {legendOpen && (
                  <Legend
                    counts={drawnCounts}
                    offStage={offStageCounts}
                    offStageIds={offStageIds}
                    shape={drawnShape}
                    drawn={placed.length}
                    trackFixes={drawnVertices}
                    selectedFixes={selected === null ? null : winFixes.length}
                    siteCount={extents.length}
                    sessionCount={selected === null ? null : sessions.length}
                    orderCount={orders.length}
                    taskedCount={layers.commands ? taskedPins : 0}
                    queued={taskedCount}
                    live={live}
                    read={robotsRead}
                  />
                )}
                {/* Both map readouts on ONE plate, on the same grid, sharing
                    one right edge — every other overlay on this stage sits on
                    an opaque ground with a hairline, and printing these two
                    straight onto the tiles left them interleaved with the
                    basemap's own place names. */}
                <div className={styles.brFoot}>
                  <Cursor map={map} />
                  <ScaleBar map={map} view={view} />
                </div>
                <div className={styles.zoomStack}>
                  <IconButton
                    icon="zoom-in"
                    label="Zoom in"
                    onClick={() => map?.zoomIn(1, { animate: false })}
                  />
                  <IconButton
                    icon="zoom-out"
                    label="Zoom out"
                    onClick={() => map?.zoomOut(1, { animate: false })}
                  />
                </div>
              </div>

              <div className={styles.cornerBL} data-furniture="true" data-edge="true">
                {/* Drawn, but nowhere near the camera. Silently showing an
                    empty city after a filter is how a map lies by omission. */}
                {placed.length > 0 && inView === 0 && (
                  <div className={styles.noFix} data-alert role="status">
                    <StatusDot tone="warn" />
                    <span>
                      {placed.length} drawn, none in view
                    </span>
                    <Button size="sm" variant="ghost" icon="crosshair" onClick={fitMatches}>
                      Fit
                    </Button>
                  </div>
                )}
                {missing.map((g) => (
                  <div key={g.key} className={styles.noFix} role="status" title={g.why}>
                    <StatusDot tone={g.tone} hollow />
                    <span>
                      {g.robots.length} {g.text}
                    </span>
                    <span
                      className={styles.noFixIds}
                      title={g.robots.map((r) => r.robot_id).join(' · ')}
                    >
                      {g.robots
                        .slice(0, 6)
                        .map((r) => r.robot_id)
                        .join(' · ')}
                      {g.robots.length > 6 && ` +${g.robots.length - 6}`}
                    </span>
                  </div>
                ))}
                {feedOpen && <FeedCard sources={feeds} now={now} />}
                {/* Bottom-left is the ambient-truth corner (Gotham's
                    `Map health — Connected`). It answers the two questions
                    that are always live — is there ground under the pins, and
                    is anything still arriving — and it opens into the feed
                    card above rather than sending the operator to a menu. */}
                <button
                  type="button"
                  className={styles.health}
                  aria-expanded={feedOpen}
                  onClick={() => setFeedOpen((v) => !v)}
                  title="Per-source feed health — which channel is answering, how often, and how long ago"
                >
                  <span
                    className={styles.healthItem}
                    data-tone={tiles === 'unavailable' ? 'warn' : 'nominal'}
                    data-toned={tiles === 'unavailable'}
                  >
                    <StatusDot
                      tone={tiles === 'unavailable' ? 'warn' : 'nominal'}
                      hollow={tiles !== 'ok'}
                    />
                    {tiles === 'ok'
                      ? `Basemap — ${BASEMAP_NAME}`
                      : tiles === 'pending'
                        ? 'Basemap — connecting'
                        : 'Basemap unreachable — graticule only'}
                  </span>
                  {/* Past the last level the source actually publishes, the
                      ground is an interpolation of a coarser one. Say so and
                      say by how much — a blurred raster that does not admit
                      it is a claim about resolution the map cannot keep.
                      THE BAR §12.10: a decimated view says it is decimated. */}
                  {tiles === 'ok' && zoom > TILE_MAX_NATIVE && (
                    <>
                      <span className={styles.sep}>·</span>
                      <span
                        className={styles.healthItem}
                        data-tone="warn"
                        data-toned
                        title={`This basemap publishes tiles to z${TILE_MAX_NATIVE}. The view is at z${
                          Math.round(zoom * 10) / 10
                        }, so the ground is that level magnified — the overlay, the graticule and the scale bar are still exact.`}
                      >
                        upscaled ×{Math.round(2 ** (zoom - TILE_MAX_NATIVE))} past z
                        {TILE_MAX_NATIVE}
                      </span>
                    </>
                  )}
                  <span className={styles.sep}>·</span>
                  <span
                    className={styles.healthItem}
                    title="Robots drawn, of the robots this filter matches"
                  >
                    <Figure of={robotsRead} value={positioned.length} />/
                    <Figure of={robotsRead} value={filtered.length} /> fixes
                  </span>
                  <span className={styles.sep}>·</span>
                  <span
                    className={styles.healthItem}
                    data-tone={seenTone(oldestFix)}
                    data-toned={oldestFix !== null && oldestFix > SEEN_WARN_MS}
                    title="Age of the stalest pose drawn on this map"
                  >
                    oldest {oldestFix === null ? NO_VALUE : duration(oldestFix)}
                  </span>
                  <span className={styles.sep}>·</span>
                  {/* The size of the RECORDING, which is what the transport
                      bar below paints and scrubs. What is drawn inside the
                      window is that bar's own readout, not this one. */}
                  <span
                    className={styles.healthItem}
                    data-tone={tracks.failed > 0 && tracks.loaded === 0 ? 'error' : 'warn'}
                    data-toned={tracks.pending > 0 || (tracks.failed > 0 && tracks.loaded === 0)}
                    title={
                      recOldest === null
                        ? 'Fleet track layer — no history has arrived yet'
                        : `${tracks.fixes} records held from ${tracks.loaded} robots, oldest ${zulu(
                            recOldest,
                          )} → newest ${zulu(recNewest ?? undefined)}`
                    }
                  >
                    {tracks.pending > 0 && tracks.loaded === 0
                      ? 'history loading'
                      : tracks.failed > 0 && tracks.loaded === 0
                        ? 'history unavailable'
                        : `history ${recSpan === null ? NO_VALUE : duration(recSpan)}`}
                  </span>
                  <Icon name={feedOpen ? 'chevron-down' : 'chevron-up'} size={11} />
                </button>
                <div className={styles.attrib}>{ATTRIBUTION}</div>
              </div>

              {follow && selected !== null && (
                <div className={styles.followTag} data-furniture="true">
                  <StatusDot tone="accent" pulse />
                  <Eyebrow>Follow</Eyebrow>
                  <span className={styles.followId}>{selected.robot_id}</span>
                  <button
                    type="button"
                    className={styles.followOff}
                    onClick={() => setFollow(false)}
                    aria-label="Stop following"
                  >
                    <Icon name="close" size={10} />
                  </button>
                </div>
              )}

              {selected !== null && (
                <EntityCard
                  key={selected.robot_id}
                  robot={selected}
                  commands={tasking.get(selected.robot_id) ?? EMPTY_COMMANDS}
                  commandsLive={live}
                  latest={latest}
                  modeHeld={modeHeld}
                  fixes={winFixes}
                  windowed={winFixes.length !== fixes.length}
                  battery={battery}
                  trackLoading={trackFetching}
                  now={asOf}
                  live={live}
                  at={winEndMs}
                  follow={follow}
                  onFollow={() => setFollow((v) => !v)}
                  onCentre={frameSelection}
                  onClose={deselect}
                  onOpen={() => navigate('detail', { robotId: selected.robot_id })}
                />
              )}
            </div>

            {/* The one time axis, under the stage rather than over it — a
                transport bar belongs in the chrome, per Foxglove and Rerun. */}
            <TrackWindow
              presence={presence}
              startMs={winStartMs}
              endMs={winEndMs}
              spanMs={winSpan}
              live={live}
              loading={tracks.pending > 0 && tracks.loaded === 0}
              failed={tracks.failed > 0 && tracks.loaded === 0}
              drawnFixes={drawnVertices}
              drawnSpan={drawnSpan}
              playing={playing}
              rate={rate}
              runMs={playRunMs}
              onPlay={togglePlay}
              onRate={cycleRate}
              onStep={stepBy}
              onSpan={setWinSpan}
              onEnd={seekTo}
              onLive={goLive}
            />
            </div>
          </Panel>
        </PanelGrid>
      </div>
    </SurfaceShell>
  );
}

/* ==========================================================================
   Roster rail — a queue beside the map, grouped and time-stamped
   ========================================================================== */

function RosterList({
  groups,
  collapsed,
  onToggleGroup,
  onFitGroup,
  selectedId,
  hoverId,
  onSelect,
  onHover,
  onCentre,
  now,
}: {
  groups: readonly Group[];
  collapsed: ReadonlySet<string>;
  onToggleGroup: (key: string) => void;
  onFitGroup: (g: Group) => void;
  selectedId: string | null;
  hoverId: string | null;
  onSelect: (id: string | null) => void;
  onHover: (id: string | null) => void;
  onCentre: (r: MapRobot) => void;
  now: number;
}) {
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const rows = Array.from(
      e.currentTarget.querySelectorAll<HTMLButtonElement>('button[data-row="robot"]'),
    );
    const i = rows.indexOf(document.activeElement as HTMLButtonElement);
    if (i === -1) return;
    const next = rows[e.key === 'ArrowDown' ? Math.min(rows.length - 1, i + 1) : Math.max(0, i - 1)];
    if (next !== undefined) {
      next.focus();
      e.preventDefault();
    }
  };

  return (
    <div className={styles.roster} onKeyDown={onKeyDown}>
      {groups.map((g) => {
        const isCollapsed = collapsed.has(g.key);
        const worst = worstHealth(g.robots);
        /* The stalest record in the group. A collapsed group hides its rows
           but must not hide its worst reading, and "worst health" and "least
           current" are two different questions — THE BAR §13 tell 19 asks a
           queue for time-in-state on the group as well as the row. */
        let oldest: number | null = null;
        for (const r of g.robots) {
          const a = seenAge(r, now);
          if (a !== null && (oldest === null || a > oldest)) oldest = a;
        }
        /* Grouped by task, the badge answers the question the grouping asked:
           how long the oldest order in this state has been waiting. */
        const waited =
          g.issuedAt === undefined || g.issuedAt === null ? null : Math.max(0, now - g.issuedAt);
        const badge = waited ?? oldest;
        return (
          <div key={g.key} className={styles.group}>
            <div className={styles.groupHead} data-tone={HEALTH_TONE[worst]}>
              <span className={styles.groupEdge} aria-hidden="true" />
              <button
                type="button"
                className={styles.groupToggle}
                onClick={() => onToggleGroup(g.key)}
                aria-expanded={!isCollapsed}
                title={`${g.label} — ${g.robots.length} robots, worst reading ${HEALTH_LABEL[
                  worst
                ].toLowerCase()}, stalest record ${
                  oldest === null ? 'unknown' : `${duration(oldest)} old`
                }${
                  waited === null ? '' : `; oldest order in this state issued ${duration(waited)} ago`
                }`}
              >
                <Eyebrow>{g.label}</Eyebrow>
                <span className={styles.spacer} />
                <span
                  className={styles.groupAge}
                  data-tone={waited === null ? seenTone(badge) : ackTone(waited)}
                  data-toned={
                    waited === null
                      ? badge !== null && badge > SEEN_WARN_MS
                      : waited > ACK_WARN_MS
                  }
                >
                  {badge === null ? NO_VALUE : duration(badge)}
                </span>
                <span className={styles.groupCount}>{g.robots.length}</span>
                <Icon name={isCollapsed ? 'chevron-right' : 'chevron-down'} size={12} />
              </button>
              <IconButton
                icon="crosshair"
                size="sm"
                label={`Fit map to ${g.label}`}
                disabled={!g.robots.some(hasFix)}
                onClick={() => onFitGroup(g)}
              />
            </div>
            {isCollapsed ? (
              <GroupTexture
                robots={g.robots}
                selectedId={selectedId}
                hoverId={hoverId}
                onSelect={onSelect}
                onHover={onHover}
                now={now}
              />
            ) : (
              g.robots.map((r) => (
                <RosterRow
                  key={r.robot_id}
                  robot={r}
                  selected={r.robot_id === selectedId}
                  hovered={r.robot_id === hoverId}
                  onSelect={onSelect}
                  onHover={onHover}
                  onCentre={onCentre}
                  now={now}
                />
              ))
            )}
          </div>
        );
      })}
    </div>
  );
}

/* --------------------------------------------------------------------------
   A collapsed group, as texture
   --------------------------------------------------------------------------
   THE BAR §8 tell #11: row-per-device does not survive fleet scale. This rail
   is 26px per robot, which is the right pitch for 28 and the wrong one for
   150 — at 150 the operator can no longer see the fleet without scrolling,
   and a scrollbar is a change in the SHAPE of the screen, not just its width.

   So collapsing a group does not throw its readings away, it changes their
   encoding: one 13px cell per robot on a 19px pitch, coloured by that robot's
   worst reading and drawn hollow where nothing is arriving. Fifteen fit per
   rail row, so a 150-robot site collapses from 3 900px of list into 190px of
   texture that you read the way `inorbit-fleet-status-matrix.png` is read —
   as a surface with breaks in it, where you only go looking for a name where
   the texture breaks.

   The cells are live, not a picture: hovering one lights its marker on the
   map and names it in the caption, clicking one selects it. The caption's
   height is fixed so the rail does not move as the pointer crosses it.
   -------------------------------------------------------------------------- */
function GroupTexture({
  robots,
  selectedId,
  hoverId,
  onSelect,
  onHover,
  now,
}: {
  robots: readonly MapRobot[];
  selectedId: string | null;
  hoverId: string | null;
  onSelect: (id: string | null) => void;
  onHover: (id: string | null) => void;
  now: number;
}) {
  const hovered = robots.find((r) => r.robot_id === hoverId) ?? null;
  const counts = new Map<Health, number>();
  for (const r of robots) counts.set(healthOf(r), (counts.get(healthOf(r)) ?? 0) + 1);
  const flagged = HEALTH_ORDER.filter((h) => h !== 'nominal' && (counts.get(h) ?? 0) > 0);
  /* One tab stop for the block, arrows inside it. A collapsed fleet of 150
     that costs 150 tab stops is not keyboard access, it is a keyboard trap —
     the same rule the marker layer follows. */
  const tabId =
    robots.find((r) => r.robot_id === selectedId)?.robot_id ?? robots[0]?.robot_id ?? null;
  const onCellKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const keys = ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    const items = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>('button'));
    const i = items.indexOf(document.activeElement as HTMLButtonElement);
    if (i === -1) return;
    const last = items.length - 1;
    const next =
      e.key === 'Home'
        ? 0
        : e.key === 'End'
          ? last
          : e.key === 'ArrowRight' || e.key === 'ArrowDown'
            ? Math.min(last, i + 1)
            : Math.max(0, i - 1);
    items[next]?.focus();
    e.preventDefault();
    e.stopPropagation();
  };
  return (
    <div className={styles.texture}>
      <div
        className={styles.cells}
        role="group"
        aria-label="Collapsed group, one cell per robot"
        onKeyDown={onCellKey}
      >
        {robots.map((r) => {
          const h = healthOf(r);
          const age = seenAge(r, now);
          return (
            <button
              key={r.robot_id}
              type="button"
              className={styles.cell}
              data-tone={HEALTH_TONE[h]}
              data-hollow={isHollow(r)}
              data-selected={r.robot_id === selectedId}
              data-hovered={r.robot_id === hoverId}
              tabIndex={r.robot_id === tabId ? 0 : -1}
              onClick={() => onSelect(r.robot_id)}
              onMouseEnter={() => onHover(r.robot_id)}
              onMouseLeave={() => onHover(null)}
              onFocus={() => onHover(r.robot_id)}
              onBlur={() => onHover(null)}
              aria-label={`${r.robot_id} — ${HEALTH_LABEL[h]}, battery ${
                r.battery_pct === null ? 'unknown' : `${Math.round(r.battery_pct)}%`
              }, last record ${age === null ? 'unknown' : duration(age)} ago`}
              title={`${r.robot_id} · ${HEALTH_LABEL[h]} · ${
                r.battery_pct === null ? NO_VALUE : `${Math.round(r.battery_pct)}%`
              } · ${age === null ? NO_VALUE : duration(age)}`}
            />
          );
        })}
      </div>
      {/* One caption, fixed height, swapping in place — never a floating
          tooltip over a rail the operator is scanning. */}
      <div className={styles.cellCap}>
        {hovered !== null && robots.includes(hovered) ? (
          <>
            <span className={styles.cellCapId}>{hovered.robot_id}</span>
            <span className={styles.cellCapState}>{HEALTH_LABEL[healthOf(hovered)]}</span>
            <span className={styles.spacer} />
            <span className={styles.cellCapVal}>
              {hovered.battery_pct === null ? NO_VALUE : `${Math.round(hovered.battery_pct)}`}
              <span className={styles.cellCapUnit}>%</span>
            </span>
            <span className={styles.cellCapVal}>
              {seenAge(hovered, now) === null ? NO_VALUE : duration(seenAge(hovered, now), { compact: 1 })}
            </span>
          </>
        ) : (
          <>
            <span className={styles.cellCapState}>{robots.length} robots</span>
            <span className={styles.spacer} />
            {flagged.length === 0 ? (
              <span className={styles.cellCapState}>all nominal</span>
            ) : (
              flagged.map((h) => (
                <span key={h} className={styles.cellCapFlag} data-tone={HEALTH_TONE[h]}>
                  {HEALTH_LABEL[h]}
                  <span className={styles.cellCapVal}>{counts.get(h)}</span>
                </span>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}

function RosterRow({
  robot,
  selected,
  hovered,
  onSelect,
  onHover,
  onCentre,
  now,
}: {
  robot: MapRobot;
  selected: boolean;
  hovered: boolean;
  onSelect: (id: string | null) => void;
  onHover: (id: string | null) => void;
  onCentre: (r: MapRobot) => void;
  now: number;
}) {
  const health = healthOf(robot);
  const age = seenAge(robot, now);
  const fix = hasFix(robot);
  const state = STATUS_STATE[robot.status];
  const link = LIVENESS_STATE[robot.liveness];
  /* Off live with nothing held at that instant, this row knows one thing:
     that it knows nothing. Mode and link are properties of a record. */
  const noRec = robot.noRecord === true;
  /* This row already prints charge and silence as columns, so a reason list
     that repeated them would be the same reading twice, 40px apart. What it
     shows is the reason a row is FLAGGED WITH NOTHING VISIBLE ON IT — a cert
     inside 30 days, a de-registered robot, a mode fault — which is otherwise
     an amber edge bar with no evidence anywhere on the row. One token: the
     worst of them, in the tone that produced it. */
  const off = offColumnReasons(robot);
  /* Prefer a reading over an absence. A row that already prints `no fix`, an
     em-dash battery and an em-dash age has finished saying "we hold nothing
     for this one"; `CERT —` beside all that is a fourth way of writing the
     same blank. A real value — `CERT 9d` — is the one this row could not
     otherwise show, so it wins, and a hollow one is printed only where the
     row is otherwise populated. */
  const why = off.find((x) => !x.hollow) ?? (fix ? (off[0] ?? null) : null);
  const reasonText =
    robot.reasons.length === 0
      ? ''
      : ` · ${HEALTH_LABEL[health].toLowerCase()}: ${robot.reasons
          .map((x) => `${x.label} ${x.text}`)
          .join(', ')}`;

  return (
    <button
      type="button"
      data-row="robot"
      data-robot={robot.robot_id}
      className={styles.row}
      data-selected={selected}
      data-hovered={hovered}
      aria-pressed={selected}
      title={
        noRec
          ? `${robot.robot_id} · ${robot.name ?? ''} · no telemetry record held at this instant`
          : `${robot.robot_id} · ${robot.name ?? ''} · ${state.label} · ${link.label}${
              fix ? '' : ' · no position'
            }${reasonText}`
      }
      onClick={() => onSelect(selected ? null : robot.robot_id)}
      onDoubleClick={() => onCentre(robot)}
      onMouseEnter={() => onHover(robot.robot_id)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(robot.robot_id)}
      onBlur={() => onHover(null)}
    >
      <span className={styles.rowEdge} data-tone={HEALTH_TONE[health]} aria-hidden="true" />
      {/* Mode, projected: steel unless a human has to act. Twenty-eight
          saturated dots down a rail is the rainbow `matrixTone` exists to
          prevent — the word is still in the title and on the card. */}
      <StatusDot
        tone={noRec ? 'offline' : matrixTone(state.tone)}
        hollow={noRec || link.hollow}
        title={noRec ? 'No record held at this instant' : `${state.label} · ${link.label}`}
      />
      <span className={styles.rowId}>{robot.robot_id}</span>
      <span className={styles.rowName}>{robot.name ?? NO_VALUE}</span>
      <span className={styles.spacer} />
      {why !== null && (
        <span
          className={styles.rowWhy}
          data-tone={why.tone}
          data-hollow={why.hollow}
          title={`${why.label} ${why.text}${
            off.length > 1 ? ` · and ${off.length - 1} more reading${off.length === 2 ? '' : 's'}` : ''
          } — why this row is flagged ${HEALTH_LABEL[health].toLowerCase()}`}
        >
          {/* The space is a real text node, not the flex gap: a screen reader
              reads the button's text content, and `CERT` + `9d` with only a
              gap between them is announced as "Cert9d". */}
          <span className={styles.rowWhyLabel}>{why.label}</span>{' '}
          {why.text}
        </span>
      )}
      {!fix && (
        <span
          className={styles.rowNoFix}
          title={
            noRec
              ? 'No telemetry record held at this instant — not on the map'
              : 'No position — this robot is not on the map'
          }
        >
          {noRec ? 'no rec' : 'no fix'}
        </span>
      )}
      <span className={styles.rowMeter}>
        <Meter value={robot.battery_pct} size="sm" warn={BATTERY_WARN} bad={BATTERY_BAD} />
      </span>
      <span
        className={styles.rowAge}
        data-tone={seenTone(age)}
        data-toned={age !== null && age > SEEN_WARN_MS}
        title="Time since the last telemetry record"
      >
        {age === null ? NO_VALUE : duration(age, { compact: 1 })}
      </span>
    </button>
  );
}

/* ==========================================================================
   Map overlay — everything drawn over the tiles, in one pane
   ========================================================================== */

function MapOverlay({
  map,
  view,
  zoom,
  clusters,
  layers,
  graticule,
  extents,
  shownBySite,
  ambient,
  clearLeft,
  fixes,
  headAt,
  tailAt,
  sessions,
  orders,
  tasking,
  selectedId,
  hoverId,
  onSelect,
  onHover,
  onHidden,
  now,
}: {
  map: L.Map;
  view: number;
  zoom: number;
  clusters: readonly Cluster[];
  layers: LayerState;
  graticule: boolean;
  extents: readonly SiteExtent[];
  /** Robots of each site the current filter is drawing. */
  shownBySite: ReadonlyMap<string, number>;
  ambient: readonly AmbientTrack[];
  /** Left edge of the stage the docked inspector is NOT covering. */
  clearLeft: number;
  fixes: readonly Fix[];
  /** The window's leading edge — the instant the track head is measured from. */
  headAt: number;
  /** The window's trailing edge — the oldest instant any track may be drawn at. */
  tailAt: number;
  /** Capture-session bounding boxes for the selected robot. */
  sessions: readonly SessionExtent[];
  /** Commands in flight that name a place — one leg and one target each. */
  orders: readonly Order[];
  /** Every in-flight command, by robot — including the verbs with no
   *  geometry, which are a reading the pin carries even though the map has
   *  nowhere to draw them. */
  tasking: ReadonlyMap<string, readonly Command[]>;
  selectedId: string | null;
  hoverId: string | null;
  onSelect: (id: string | null) => void;
  onHover: (id: string | null) => void;
  /** How many chips this render had to drop, for the panel header to print. */
  onHidden: (n: number) => void;
  now: number;
}) {
  void view;
  /* A robot's four capture footprints overlap: they are four passes over the
     same block. Their name chips sit at four different corners, so "which box
     is `row-14`" is a question the picture cannot answer on its own. Pointing
     at the chip answers it — the chip is the only half of the pair that can
     take a pointer without stealing the map's drag. */
  const [capHover, setCapHover] = useState<number | null>(null);
  type Rect = { x0: number; y0: number; x1: number; y1: number; own?: string };
  const size = map.getSize();
  const origin = map.containerPointToLayerPoint([0, 0]);
  /* Every panel parked on the stage, measured once per render and expressed in
     LAYER coordinates — the space the pins are placed in. Nothing this layer
     draws is allowed to print underneath one of them. Measured rather than
     assumed: the legend toggles, the layer tree opens, the card's height
     depends on what the robot reports, and the notice stack grows a row per
     robot without a fix. */
  const furniture: Rect[] = [];
  {
    const stage = map.getContainer().parentElement;
    if (stage !== null) {
      const sr = map.getContainer().getBoundingClientRect();
      for (const el of stage.querySelectorAll<HTMLElement>(CHROME_SEL)) {
        const fr = el.getBoundingClientRect();
        if (fr.width < 1 || fr.height < 1) continue;
        furniture.push({
          x0: fr.left - sr.left + origin.x - 4,
          y0: fr.top - sr.top + origin.y - 4,
          x1: fr.right - sr.left + origin.x + 4,
          y1: fr.bottom - sr.top + origin.y + 4,
        });
      }
    }
  }
  const box = {
    x: origin.x - OVERLAY_PAD,
    y: origin.y - OVERLAY_PAD,
    w: size.x + OVERLAY_PAD * 2,
    h: size.y + OVERLAY_PAD * 2,
  };
  const rel = (p: L.Point) => ({ x: p.x - box.x, y: p.y - box.y });
  const at = (lat: number, lon: number) => rel(map.latLngToLayerPoint([lat, lon]));
  /** Box coords → layer coords, for chrome parked in the overlay's own DOM. */
  const abs = (p: { x: number; y: number }) => ({ x: p.x + box.x, y: p.y + box.y });
  /** Keep an anchored chip inside the viewport however the camera moved. The
   *  vertical bounds leave room for the CHIP, not just its anchor point: a
   *  16px chip hung above its anchor needs 19px of headroom, so an anchor
   *  clamped to the frame would still be drawn half off it. */
  const clampToView = (p: { x: number; y: number }, half = 96) => {
    const lo = OVERLAY_PAD + clearLeft + 10 + half;
    const hi = OVERLAY_PAD + size.x - 10 - half;
    return {
      x: hi < lo ? hi : Math.min(Math.max(p.x, lo), hi),
      y: Math.min(Math.max(p.y, OVERLAY_PAD + 30), OVERLAY_PAD + size.y - 28),
    };
  };
  /** Is this box anywhere near the picture the operator is actually looking at? */
  const onScreen = (pts: readonly { x: number; y: number }[]) => {
    if (pts.length === 0) return false;
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    for (const p of pts) {
      x0 = Math.min(x0, p.x);
      y0 = Math.min(y0, p.y);
      x1 = Math.max(x1, p.x);
      y1 = Math.max(y1, p.y);
    }
    return (
      x1 >= OVERLAY_PAD && y1 >= OVERLAY_PAD && x0 <= OVERLAY_PAD + size.x && y0 <= OVERLAY_PAD + size.y
    );
  };

  /* ---- site extents: stroke-only hulls, drawn at every zoom -------------
     The polygon is the convex hull of every fix these robots have reported
     inside the track window, pushed out far enough that a marker sitting on
     the boundary is not ON the line. It is drawn whatever the zoom: over the
     yard it is the working boundary, at continental zoom it collapses to a
     named patch, and either way the site is a shape rather than a rumour. */
  const offscreen: {
    key: string;
    label: string;
    short: string;
    members: number;
    shown: number;
    worst: Health;
    centre: { lat: number; lon: number };
    c: { x: number; y: number };
  }[] = [];
  const sites = extents.flatMap((s) => {
    const pts = s.ring.map((p) => at(p.lat, p.lon));
    const c = at(s.centre.lat, s.centre.lon);
    /* Push each vertex out along its own radius so the hull keeps its shape
       instead of being uniformly scaled — a long thin site must stay long
       and thin. */
    const padded = pts.map((p) => {
      const dx = p.x - c.x;
      const dy = p.y - c.y;
      const d = Math.hypot(dx, dy);
      if (d < 0.5) return p;
      const k = (d + SITE_PAD_PX) / d;
      return { x: c.x + dx * k, y: c.y + dy * k };
    });
    /* The radius the shape actually reaches — the number the name chip has to
       clear. A site whose hull collapses to under the minimum ring at
       continental zoom is DRAWN as that minimum ring, so "site extent" is a
       visible mark at every zoom instead of an invisible dot at some of
       them, and so the chip has one rule to clear rather than two. */
    const hullR = padded.reduce((m, p) => Math.max(m, Math.hypot(p.x - c.x, p.y - c.y)), 0);
    const asRing = padded.length < 3 || hullR < SITE_MIN_R;
    const drawnR = Math.max(hullR, SITE_MIN_R);
    const d = asRing
      ? null
      : `${padded.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')} Z`;
    /* The name chip rides the shape's top or bottom edge — whichever is free.
       Where the hull is a ring, the top of the RING is the edge, not the top
       hull vertex hiding inside it. */
    let topY = c.y - drawnR;
    let botY = c.y + drawnR;
    if (!asRing) {
      for (const p of padded) {
        topY = Math.min(topY, p.y);
        botY = Math.max(botY, p.y);
      }
    }
    /* A site 8 000 km away must not have its chip clamped to the edge of the
       picture — that would put Rotterdam in the corner of Portland. It gets an
       OFF-SCREEN indicator instead: a tick on the viewport edge at the site's
       true bearing, carrying its name, its count and its real range. The
       header says "3 sites"; this is what makes that true on the map. */
    if (!onScreen(padded.length > 0 ? padded : [c])) {
      offscreen.push({
        key: s.key,
        label: s.label,
        short: shortSite(s.key),
        members: s.members,
        shown: shownBySite.get(s.key) ?? 0,
        worst: s.worst,
        centre: s.centre,
        c,
      });
      return [];
    }
    return {
      key: s.key,
      label: s.label,
      members: s.members,
      shown: shownBySite.get(s.key) ?? 0,
      samples: s.samples,
      spanM: s.spanM,
      worst: s.worst,
      d,
      c,
      r: drawnR,
      /* The projected shape itself, so the name can be set INSIDE the ground
         it names rather than hung off its edge — see `spanAtY`. */
      poly: asRing ? null : padded,
      topY,
      botY,
    };
  });

  /* Park each off-screen site where the ray from the viewport's centre to that
     site leaves the picture, inside a rectangle that already excludes the
     docked inspector and the bottom-left health stack. Range is measured from
     the camera's own centre, and the arrow points at the true bearing. */
  const centreLL = map.getCenter();
  const edgeSites = offscreen.flatMap((s) => {
    const box0 = {
      l: OVERLAY_PAD + clearLeft + 92,
      r: OVERLAY_PAD + size.x - 92,
      t: OVERLAY_PAD + 22,
      b: OVERLAY_PAD + size.y - FURNITURE_H,
    };
    if (box0.r <= box0.l || box0.b <= box0.t) return [];
    const cx = OVERLAY_PAD + size.x / 2;
    const cy = OVERLAY_PAD + size.y / 2;
    const dx = s.c.x - cx;
    const dy = s.c.y - cy;
    if (dx === 0 && dy === 0) return [];
    let t = Infinity;
    if (dx > 0) t = Math.min(t, (box0.r - cx) / dx);
    if (dx < 0) t = Math.min(t, (box0.l - cx) / dx);
    if (dy > 0) t = Math.min(t, (box0.b - cy) / dy);
    if (dy < 0) t = Math.min(t, (box0.t - cy) / dy);
    if (!Number.isFinite(t) || t <= 0) return [];
    const range = centreLL.distanceTo(L.latLng(s.centre.lat, s.centre.lon));
    const x = cx + dx * t;
    const y = cy + dy * t;
    return {
      ...s,
      x,
      /* Which edge it is parked on decides which way it may step aside when
         two sites leave the picture through the same corner, and which way it
         may step INWARD when that whole edge is occupied — the bearing arrow
         stays true either way, so a tick 40 px off the frame still reads. */
      axis: Math.abs(x - box0.l) < 1 || Math.abs(x - box0.r) < 1 ? ('y' as const) : ('x' as const),
      inward:
        Math.abs(x - box0.l) < 1 || Math.abs(x - box0.r) < 1
          ? { x: dx > 0 ? -1 : 1, y: 0 }
          : { x: 0, y: dy > 0 ? -1 : 1 },
      y,
      /* Screen bearing of the arrow; the title carries the compass bearing. */
      deg: (Math.atan2(dy, dx) * 180) / Math.PI,
      range: metres(range),
      rangeM: range,
    };
  });

  /* ---- graticule ------------------------------------------------------- */
  let grat: {
    step: number;
    lons: { v: number; x: number; label: boolean }[];
    lats: { v: number; y: number; label: boolean }[];
    labelX: number;
    labelY: number;
  } | null = null;
  if (graticule) {
    const b = map.getBounds();
    const step = gratStep(Math.max(Math.abs(b.getEast() - b.getWest()), Math.abs(b.getNorth() - b.getSouth())));
    const lons: { v: number; x: number; label: boolean }[] = [];
    const lats: { v: number; y: number; label: boolean }[] = [];
    /* A line may run off the picture; its LABEL may not. Anything that would
       be sliced by the pane edge, hidden behind the docked inspector, or
       parked on top of the bottom-left health line is drawn as a bare line
       with no label rather than as half a number. */
    /* The label sits ABOVE its anchor, so the anchor rows are pushed far
       enough in that a 12px number is never sliced by the frame: 26px down
       from the top for the meridians, 12px in from the left for the
       parallels. */
    const labelBoxL = OVERLAY_PAD + clearLeft + 12;
    const labelBoxR = OVERLAY_PAD + size.x - 52;
    const labelBoxT = OVERLAY_PAD + 26;
    const labelBoxB = OVERLAY_PAD + size.y - 16;
    for (let v = Math.floor(b.getWest() / step) * step; v <= b.getEast() + step; v += step) {
      const x = at(b.getNorth(), v).x;
      lons.push({ v, x, label: x >= labelBoxL && x <= labelBoxR });
      if (lons.length > 48) break;
    }
    for (let v = Math.floor(b.getSouth() / step) * step; v <= b.getNorth() + step; v += step) {
      const y = at(v, b.getWest()).y;
      lats.push({ v, y, label: y >= labelBoxT && y <= labelBoxB });
      if (lats.length > 48) break;
    }
    grat = { step, lons, lats, labelX: labelBoxL, labelY: labelBoxT };
  }

  /* ---- ambient tracks: every robot's recent path, always on -------------
     This is the layer that decides whether this map has DATA on it. Drawn
     grey and hairline it was indistinguishable from the basemap's own minor
     road casings, and a panel advertising "4 320 fixes" over a picture with
     no visible line on it is a claim the surface cannot back.

     So each track is drawn in its robot's own state hue at 1.7 px, banded on
     an age ramp measured against the WINDOW's clock — every track on the map
     shares one ramp, so brightness means recency fleet-wide rather than
     "late in this robot's own slice". The last HEAD_MS steps up again and
     ends in a filled head at the newest fix: direction is readable without a
     single arrowhead, and a robot that has not moved shows as a knot instead
     of a line. */
  const path = (pts: readonly { x: number; y: number }[]) =>
    pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

  /* Culled to the drawing box and rebuilt only when the projection or the
     history actually changed — the 1 Hz age ticker must not re-lay-out four
     thousand vertices, and a track 8 000 km away must not be in the DOM at
     all. The ramp's ends are quantised to 30 s for the same reason. */
  const headBucket = Math.floor(headAt / 30_000);
  const tailBucket = Math.floor(tailAt / 30_000);
  const ambientDrawn = useMemo(() => {
    const t1 = headBucket * 30_000;
    const t0 = Math.min(tailBucket * 30_000, t1 - 1);
    const span = Math.max(1, t1 - t0);
    return ambient
      .filter((t) => t.id !== selectedId)
      .map((t) => {
        const pts = t.fixes.map((f) => at(f.lat, f.lon));
        /* One band index per VERTEX, then runs of equal index become one
           sub-path. Consecutive runs share their boundary vertex, so the ramp
           steps without the line ever breaking. */
        const bandOf = (ms: number) => {
          const u = Math.min(1, Math.max(0, (ms - t0) / span));
          return Math.min(AMB_BANDS - 1, Math.floor(u * AMB_BANDS));
        };
        const bands: { d: string; o: number }[] = [];
        let runStart = 0;
        let runBand = bandOf(t.fixes[0]?.t ?? t0);
        const flush = (endIdx: number, band: number) => {
          if (endIdx <= runStart) return;
          const seg = pts.slice(runStart, endIdx + 1);
          if (seg.length < 2) return;
          const u = band / (AMB_BANDS - 1);
          bands.push({ d: path(seg), o: AMB_MIN_A + (AMB_MAX_A - AMB_MIN_A) * u ** 1.4 });
        };
        for (let i = 1; i < t.fixes.length; i += 1) {
          const b = bandOf(t.fixes[i]?.t ?? t0);
          if (b !== runBand) {
            flush(i, runBand);
            runStart = i;
            runBand = b;
          }
        }
        flush(t.fixes.length - 1, runBand);

        let headFrom = t.fixes.length - 1;
        while (headFrom > 0) {
          const f = t.fixes[headFrom - 1];
          if (f === undefined || t1 - f.t > HEAD_MS) break;
          headFrom -= 1;
        }
        return {
          id: t.id,
          tone: t.tone,
          bands,
          head: headFrom < t.fixes.length - 1 ? path(pts.slice(headFrom)) : null,
          tip: pts[pts.length - 1] ?? null,
          pts,
          on: onScreen(pts),
        };
      })
      .filter((t) => t.on);
    // `at`, `path` and `onScreen` are pure functions of the projection, and
    // `view` is the projection's version — listing them would rebuild the
    // layer on every render and defeat the memo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, view, ambient, selectedId, headBucket, tailBucket]);

  /* ---- selected track: the accent, faded along its length by fix age -----
     Banded rather than per-segment: eighteen sub-paths carry a smooth ramp
     from the oldest record to the newest at a fraction of the DOM, and the
     window and its Zulu range are printed AT the track's oldest end so the
     line never has to be taken on trust. */
  const trackPts = fixes.length >= 2 ? fixes.map((f) => at(f.lat, f.lon)) : [];
  const bands: { d: string; o: number }[] = [];
  if (trackPts.length >= 2) {
    const n = trackPts.length;
    const per = Math.max(1, Math.ceil((n - 1) / FADE_BANDS));
    for (let start = 0; start < n - 1; start += per) {
      const end = Math.min(n - 1, start + per);
      const seg = trackPts.slice(start, end + 1);
      if (seg.length < 2) continue;
      /* Opacity tracks the age of the band's newest fix, not its index, so a
         gap in the history shows as a jump in brightness rather than being
         smoothed away. */
      const t0 = fixes[0]?.t ?? 0;
      const t1 = fixes[n - 1]?.t ?? 1;
      const tv = fixes[end]?.t ?? t1;
      const u = t1 === t0 ? 1 : (tv - t0) / (t1 - t0);
      bands.push({ d: path(seg), o: 0.2 + 0.8 * u });
    }
  }
  const trackStart = trackPts[0] ?? null;
  const trackHead = trackPts[trackPts.length - 1] ?? null;

  /* Capture footprints, projected. A box smaller than 10px on both axes is a
     dot the operator cannot read a name off, so it is dropped rather than
     drawn as a speck — the count stays in the layer tree and the legend. */
  const capture = sessions
    .map((e) => {
      const a = at(e.north, e.west);
      const b = at(e.south, e.east);
      return { e, x: a.x, y: a.y, w: b.x - a.x, h: b.y - a.y };
    })
    .filter((c) => c.w >= 10 && c.h >= 10 && onScreen([{ x: c.x, y: c.y }, { x: c.x + c.w, y: c.y + c.h }]));

  /* Commanded waypoints, projected with their legs. A target whose robot and
     whose destination are BOTH off the picture is nothing to draw; either end
     on screen is worth the line, because a leg running off the frame is the
     honest way to say "it has been sent somewhere over there". */
  const drawnOrders = orders
    .map((o) => ({ o, p: at(o.lat, o.lon), q: at(o.from.lat, o.from.lon) }))
    .filter((x) => onScreen([x.p]) || onScreen([x.q]));

  const showIds = layers.ids && zoom >= LABEL_ZOOM;
  const showFixDots = zoom >= FIX_ZOOM;

  /* What actually gets drawn: a cluster of one — or any cluster holding the
     selection — resolves to its own robots, so the operator never loses the
     thing they clicked inside a count bubble. */
  type Drawn =
    | { kind: 'robot'; id: string; placed: Placed }
    | { kind: 'cluster'; id: string; cluster: Cluster };
  const drawn: Drawn[] = [];
  for (const c of clusters) {
    const holdsSelection =
      selectedId !== null && c.members.some((m) => m.robot.robot_id === selectedId);
    if (c.members.length === 1 || holdsSelection) {
      for (const m of c.members) drawn.push({ kind: 'robot', id: m.robot.robot_id, placed: m });
    } else {
      drawn.push({ kind: 'cluster', id: c.key, cluster: c });
    }
  }

  /* ---- label placement --------------------------------------------------
     Overlapping labels are the classic map defect: two ids on top of each
     other is worse than one id and a pin. So every label is laid out against
     the ones already placed and against every marker glyph, and degrades in
     a fixed order — below the pin, above the pin, id only below, id only
     above, nothing. The selection and the hovered pin are placed FIRST so
     they always win their space. */
  const labelPlan = new Map<string, LabelPlan>();
  /** Each robot chip's resolved rectangle, in layer coordinates. The card's
   *  leader needs it to know where to stop. */
  const chipBoxes = new Map<string, Rect>();
  /** Resolved position of each site's name chip, in layer coordinates. */
  const siteChips = new Map<string, Anchored & { below: boolean }>();
  /** Resolved position of each off-screen site tick, in layer coordinates. */
  const edgeAt = new Map<string, Anchored>();
  /** Resolved position of each capture footprint's name chip, in layer coords. */
  const captureChips = new Map<number, Anchored>();
  /** Resolved position of each commanded waypoint's chip, in layer coords. */
  const orderChips = new Map<number, Anchored>();
  /** The graticule's degree readouts, resolved into LAYER coordinates so they
   *  can be drawn in the label layer with every other string on this map.
   *  They used to be `<text>` inside the vector SVG, which put them under the
   *  site hulls, the capture footprints and every track — and a dashed hull
   *  ruling through `-122.00°` destroys the one number that says where you
   *  are looking. Nothing on this stage prints text below geometry now. */
  const gratChips: { key: string; x: number; y: number; ay: number; text: string }[] = [];
  /** Chips this render could not fit anywhere. Printed in the panel header —
      a map that silently drops four names is a map that is lying about how
      much of itself it has told you. */
  let suppressed = 0;
  /** Every 1px hairline joining a displaced chip to the thing it names. */
  const leaders: Leader[] = [];
  const taken: Rect[] = [];
  const hits = (b: Rect, self: string) =>
    taken.some(
      (o) => o.own !== self && b.x0 < o.x1 && b.x1 > o.x0 && b.y0 < o.y1 && b.y1 > o.y0,
    );
  {
    /* Every OTHER pin's glyph is an obstacle: a label may not sit on a marker.
       A label always touches its own pin — that is what makes it that pin's
       label — so the pin it belongs to is exempt. */
    for (const d of drawn) {
      const p = d.kind === 'robot' ? d.placed.p : d.cluster.p;
      /* PIN_R is the drawn radius including the heading needle, so a label
         that clears this box clears the glyph rather than half of it — and
         where the pin prints its own age the box runs out to the end of that
         text instead of stopping at the ring. */
      const right = d.kind === 'robot' ? pinRightExtent(d.placed.robot, now) : PIN_R;
      taken.push({ x0: p.x - PIN_R, y0: p.y - PIN_R, x1: p.x + right, y1: p.y + PIN_R, own: d.id });
    }
    /* A commanded waypoint's target glyph is a symbol like any other and no
       label may print across it. */
    for (const x of drawnOrders) {
      taken.push({
        x0: x.p.x - ORDER_R - 2,
        y0: x.p.y - ORDER_R - 2,
        x1: x.p.x + ORDER_R + 2,
        y1: x.p.y + ORDER_R + 2,
      });
    }
    /* Panels win against everything: a label printed under the legend is not
       a label. Measured at the top of this render. */
    taken.push(...furniture);
    /* And so is every other piece of chrome this layer draws — the site
       chips, the off-screen site ticks, the track's window chip and the
       graticule's own numbers. They are laid out before the labels, so a
       label yields to them rather than printing across them.

       Widths are estimated from the strings at the type sizes in the CSS
       (10px mono advances ~6.05px, 10px Inter ~5.7px) rather than measured:
       measuring means a forced reflow per pin per second, and an estimate
       three pixels wide of the truth just makes a label yield slightly early. */
    const MONO = 6.05;
    const SANS = 5.7;
    const chip = (
      x: number,
      y: number,
      w: number,
      h: number,
      ax: number,
      ay: number,
    ): Rect => ({ x0: x - w * ax - 3, y0: y - h * ay - 3, x1: x + w * (1 - ax) + 3, y1: y + h * (1 - ay) + 3 });

    const chrome: Rect[] = [];
    /* Two classes of chrome, because they yield differently to a robot's own
       label. HARD chrome is a NAME — a site, a capture session, an off-screen
       site tick — and a robot id printed across one destroys both. SOFT
       chrome is a graticule number, which is inferable from its neighbours
       and therefore the one thing on this map that yields to everything. */
    const chromeHard: Rect[] = [];
    const chromeSoft: Rect[] = [];
    /* The pane's own edges are obstacles for CHROME (not for a robot's own
       label, which may run to the edge with its pin still visible): a site
       name or a range readout sliced in half by the frame is worse than the
       same fact one step further in. */
    const IN = 1e6;
    const walls: Rect[] = [
      { x0: -IN, y0: -IN, x1: origin.x + 8, y1: IN },
      { x0: origin.x + size.x - 8, y0: -IN, x1: IN, y1: IN },
      { x0: -IN, y0: -IN, x1: IN, y1: origin.y + 8 },
      { x0: -IN, y0: origin.y + size.y - 8, x1: IN, y1: IN },
    ];
    const clash = (b: Rect, extra: readonly Rect[]) =>
      hits(b, '') ||
      walls.some((c) => b.x0 < c.x1 && b.x1 > c.x0 && b.y0 < c.y1 && b.y1 > c.y0) ||
      extra.some((c) => b.x0 < c.x1 && b.x1 > c.x0 && b.y0 < c.y1 && b.y1 > c.y0);
    /** The 1px hairline a displaced chip grows back to the thing it names.
     *  It stops `pad` px short of the anchor so it touches the pin's ring
     *  rather than crossing the glyph. */
    const leaderTo = (b: Rect, anchor: { x: number; y: number }, pad: number): Leader => {
      const ex = Math.min(Math.max(anchor.x, b.x0 + 4), b.x1 - 4);
      const ey = anchor.y < b.y0 ? b.y0 : anchor.y > b.y1 ? b.y1 : anchor.y;
      const dx = anchor.x - ex;
      const dy = anchor.y - ey;
      const d = Math.hypot(dx, dy);
      const k = d <= pad ? 0 : (d - pad) / d;
      return { x0: ex, y0: ey, x1: ex + dx * k, y1: ey + dy * k };
    };
    /** The escape hatch every chip class shares. When no preferred slot is
     *  clear, walk whole rows away from the best one — nearest first, both
     *  directions — and take the first step that lands on nothing. A chip
     *  that has moved wears a leader back to its anchor, so a name 40 px from
     *  its shape is still unambiguously that shape's name. `null` means the
     *  chip is dropped and counted rather than printed over something. */
    const settle = (
      from: { x: number; y: number },
      ax: number,
      ay: number,
      w: number,
      h: number,
      anchor: { x: number; y: number },
      pad: number,
      extra: readonly Rect[],
    ): (Anchored & { box: Rect }) | null => {
      for (let k = 1; k <= CHIP_STEPS; k += 1) {
        for (const dir of [1, -1]) {
          const a = abs(clampToView({ x: from.x - box.x, y: from.y - box.y + dir * k * CHIP_ROW }, w / 2));
          const b = chip(a.x, a.y, w, h, ax, ay);
          if (clash(b, extra)) continue;
          return { x: a.x, y: a.y, ax, ay, leader: leaderTo(b, anchor, pad), box: b };
        }
      }
      return null;
    };
    /* A site chip printed THROUGH the ring it names is the same defect as a
       robot label printed through its own pin, so it gets the same treatment:
       a ladder — above the shape, below it, then stepped further out — tested
       against every pin, every cluster bubble and every chip already placed.
       The shape is what the chip must clear, not the centre it is drawn
       around. Nothing is placed on a cost compromise any more: a slot is
       clear or the chip steps off and grows a leader. */
    for (const s of sites) {
      const span = metres(s.spanM);
      const w =
        16 + s.label.length * SANS + siteCountText(s.shown, s.members).length * MONO + (span.value.length + span.unit.length) * MONO + 12;
      /* How wide the site's own shape is at a given scanline — the test that
         lets its name be set INSIDE the ground it names. A convex hull's
         intersection with a horizontal line is one interval, so this is two
         numbers, and a circle (the collapsed-hull case) is closed form. */
      const spanAtY = (y: number): { x0: number; x1: number } | null => {
        if (s.poly === null) {
          const dy = y - s.c.y;
          if (Math.abs(dy) >= s.r) return null;
          const half = Math.sqrt(s.r * s.r - dy * dy);
          return { x0: s.c.x - half, x1: s.c.x + half };
        }
        let lo = Infinity;
        let hi = -Infinity;
        const ring = s.poly;
        for (let i = 0; i < ring.length; i += 1) {
          const a = ring[i];
          const b = ring[(i + 1) % ring.length];
          if (a === undefined || b === undefined) continue;
          if (a.y === b.y) continue;
          const t = (y - a.y) / (b.y - a.y);
          if (t < 0 || t > 1) continue;
          const x = a.x + (b.x - a.x) * t;
          lo = Math.min(lo, x);
          hi = Math.max(hi, x);
        }
        return Number.isFinite(lo) && hi > lo ? { x0: lo, x1: hi } : null;
      };
      const opts: { below: boolean; y: number; dx: number }[] = [];
      /* Inside first, top-down. A zone label belongs on its zone — that is
         what every annotated warehouse map in the reference set does
         (`inorbit-navigation-annotated-map.png`) — and only when the shape is
         genuinely too narrow to hold the name does it move to the edge. */
      for (const frac of [0.12, 0.24, 0.36, 0.5, 0.64, 0.78, 0.9]) {
        const y = s.topY + (s.botY - s.topY) * frac;
        const seg = spanAtY(y);
        if (seg === null || seg.x1 - seg.x0 < w + 14) continue;
        const lo = seg.x0 + w / 2 + 7;
        const hi = seg.x1 - w / 2 - 7;
        for (const u of [0.5, 0.22, 0.78]) {
          const cx = Math.min(Math.max(seg.x0 + (seg.x1 - seg.x0) * u, lo), hi);
          opts.push({ below: true, y: y - 8, dx: cx - s.c.x });
        }
      }
      for (const dx of [0, -150, 150, -300, 300]) {
        for (const step of [6, 26, 46]) {
          opts.push({ below: false, y: s.topY - step, dx });
          opts.push({ below: true, y: s.botY + step, dx });
        }
      }
      const anchor = abs({ x: s.c.x, y: s.c.y });
      let done: (Anchored & { below: boolean; box: Rect }) | null = null;
      for (const o of opts) {
        const a = abs(clampToView({ x: s.c.x + o.dx, y: o.y }, w / 2));
        /* The 3px is the gap between the shape's edge and the chip's near
           edge; it is folded into the anchor so the box the placer reserves
           and the box the browser paints are the same rectangle. */
        const y = o.below ? a.y + 3 : a.y - 3;
        const b = chip(a.x, y, w, 16, 0.5, o.below ? 0 : 1);
        if (clash(b, chrome)) continue;
        done = { x: a.x, y, ax: 0.5, ay: o.below ? 0 : 1, below: o.below, leader: null, box: b };
        break;
      }
      if (done === null) {
        const from = abs(clampToView({ x: s.c.x, y: s.botY + 6 }, w / 2));
        const step = settle(from, 0.5, 0, w, 16, anchor, Math.max(12, s.r), chrome);
        if (step !== null) done = { ...step, below: true };
      }
      if (done === null) {
        suppressed += 1;
        continue;
      }
      siteChips.set(s.key, { x: done.x, y: done.y, ax: done.ax, ay: done.ay, below: done.below, leader: done.leader });
      if (done.leader !== null) leaders.push(done.leader);
      chrome.push(done.box);
      chromeHard.push(done.box);
    }
    /* A capture footprint names itself at a corner of its own box, and the
       chip is an obstacle for everything laid out after it — the same
       treatment the site chips get, for the same reason. */
    for (const c of capture) {
      const w = 22 + c.e.name.length * MONO + (c.e.ran === null ? 1 : 4) * MONO + 12;
      const corners = [
        { x: c.x + 3, y: c.y - 3, ax: 0, ay: 1 },
        { x: c.x + 3, y: c.y + c.h + 3, ax: 0, ay: 0 },
        { x: c.x + c.w - 3, y: c.y - 3, ax: 1, ay: 1 },
        { x: c.x + c.w - 3, y: c.y + c.h + 3, ax: 1, ay: 0 },
      ];
      const anchor = abs({ x: c.x + c.w / 2, y: c.y + c.h / 2 });
      let done: (Anchored & { box: Rect }) | null = null;
      for (const o of corners) {
        const a = abs(clampToView({ x: o.x, y: o.y }, w / 2));
        const b = chip(a.x, a.y, w, 16, o.ax, o.ay);
        if (clash(b, chrome)) continue;
        done = { x: a.x, y: a.y, ax: o.ax, ay: o.ay, leader: null, box: b };
        break;
      }
      if (done === null) {
        const from = abs(clampToView({ x: c.x + c.w / 2, y: c.y + c.h + 3 }, w / 2));
        done = settle(from, 0.5, 0, w, 16, anchor, Math.max(10, Math.min(c.w, c.h) / 2), chrome);
      }
      if (done === null) {
        suppressed += 1;
        continue;
      }
      captureChips.set(c.e.id, { x: done.x, y: done.y, ax: done.ax, ay: done.ay, leader: done.leader });
      if (done.leader !== null) leaders.push(done.leader);
      chrome.push(done.box);
      chromeHard.push(done.box);
    }
    /* A commanded waypoint names the robot it belongs to, not itself: the leg
       already says where, and `move` on its own says nothing about WHOSE move
       it is when two robots have been tasked into the same block. Placed with
       the named chrome, before the robot ids, for the same reason a site name
       is — an id printed across an order is two readings destroyed. */
    for (const x of drawnOrders) {
      if (!onScreen([x.p])) continue;
      const w = 20 + (x.o.robotId.length + x.o.command.length) * MONO + 20;
      const around: { x: number; y: number; ax: number; ay: number }[] = [
        { x: x.p.x, y: x.p.y - ORDER_R - 3, ax: 0.5, ay: 1 },
        { x: x.p.x, y: x.p.y + ORDER_R + 3, ax: 0.5, ay: 0 },
        { x: x.p.x + ORDER_R + 3, y: x.p.y, ax: 0, ay: 0.5 },
        { x: x.p.x - ORDER_R - 3, y: x.p.y, ax: 1, ay: 0.5 },
      ];
      /* A commanded position often lands in the corner the legend and the
         zoom stack own — that is where the fleet's own geography puts it, not
         a layout accident — so the ladder walks sideways as well as up. A
         target parked under a panel with no name on it is the one mark on
         this stage an operator cannot interpret at all. */
      for (const dy of [-1, 1]) {
        for (const dx of [44, -44, 96, -96]) {
          around.push({
            x: x.p.x + dx,
            y: x.p.y + dy * (ORDER_R + 3),
            ax: 0.5,
            ay: dy < 0 ? 1 : 0,
          });
        }
      }
      const anchor = abs({ x: x.p.x, y: x.p.y });
      let done: (Anchored & { box: Rect }) | null = null;
      for (const o of around) {
        const a = abs(clampToView({ x: o.x, y: o.y }, w / 2));
        const b = chip(a.x, a.y, w, 16, o.ax, o.ay);
        if (clash(b, chrome)) continue;
        done = { x: a.x, y: a.y, ax: o.ax, ay: o.ay, leader: null, box: b };
        break;
      }
      if (done === null) {
        const from = abs(clampToView({ x: x.p.x, y: x.p.y + ORDER_R + 3 }, w / 2));
        done = settle(from, 0.5, 0, w, 16, anchor, ORDER_R + 2, chrome);
      }
      if (done === null) {
        suppressed += 1;
        continue;
      }
      orderChips.set(x.o.id, { x: done.x, y: done.y, ax: done.ax, ay: done.ay, leader: done.leader });
      if (done.leader !== null) leaders.push(done.leader);
      chrome.push(done.box);
      chromeHard.push(done.box);
    }
    /* Two sites can leave the picture through the same corner. When they do,
       the second one steps along the edge it is parked on rather than
       printing across the first — the bearing arrow still points true, so a
       step of 22 px costs nothing an operator reads. */
    for (const s of edgeSites) {
      const w =
        21 + s.short.length * SANS + siteCountText(s.shown, s.members).length * MONO + (s.range.value.length + s.range.unit.length) * MONO + 12;
      let picked: { x: number; y: number } | null = null;
      for (const inRow of [0, 1, 2, 3, 4, 5, 6, 7, 8]) {
        for (const step of [0, -22, 22, -44, 44, -66, 66, -110, 110]) {
          const along =
            s.axis === 'y' ? { x: s.x, y: s.y + step } : { x: s.x + step * 3, y: s.y };
          const q = {
            x: along.x + s.inward.x * inRow * CHIP_ROW,
            y: along.y + s.inward.y * inRow * CHIP_ROW,
          };
          const a = abs(q);
          if (clash(chip(a.x, a.y, w, 16, 0.5, 0.5), chrome)) continue;
          picked = q;
          break;
        }
        if (picked !== null) break;
      }
      if (picked === null) {
        suppressed += 1;
        continue;
      }
      const a = abs(picked);
      edgeAt.set(s.key, { x: a.x, y: a.y, ax: 0.5, ay: 0.5, leader: null });
      const edgeBox = chip(a.x, a.y, w, 16, 0.5, 0.5);
      chrome.push(edgeBox);
      chromeHard.push(edgeBox);
    }
    /* Graticule numbers are the lowest-priority text on this map: they are
       the one thing that yields to everything else. A number sliced by a
       panel, printed through a site chip, or stacked on the meridian label in
       the corner is worse than no number at all — the line is still there and
       still readable off its neighbours. Longitudes are resolved first, so a
       latitude in the top-left corner is the one that drops. */
    if (grat !== null) {
      const g = grat;
      const yields = (b: Rect) => clash(b, chrome);
      for (const l of g.lons) {
        if (!l.label) continue;
        const t = fmtDeg(l.v, g.step);
        const a = abs({ x: l.x + 3, y: g.labelY });
        const b = chip(a.x, a.y, t.length * MONO, 12, 0, 1);
        if (yields(b)) l.label = false;
        else {
          chrome.push(b);
          chromeSoft.push(b);
          gratChips.push({ key: `x${l.v}`, x: a.x, y: a.y, ay: 1, text: t });
        }
      }
      for (const l of g.lats) {
        if (!l.label) continue;
        const t = fmtDeg(l.v, g.step);
        const a = abs({ x: g.labelX, y: l.y - 3 });
        const b = chip(a.x, a.y, t.length * MONO, 12, 0, 1);
        if (yields(b)) l.label = false;
        else {
          chrome.push(b);
          chromeSoft.push(b);
          gratChips.push({ key: `y${l.v}`, x: a.x, y: a.y, ay: 1, text: t });
        }
      }
    }

    /* Only pins the operator can actually see get a chip. A robot 8 000 km
       off the left of the picture has nothing to label, and counting its
       chip as "hidden" would make the header's caveat meaningless. */
    const rows = drawn
      .filter((d): d is { kind: 'robot'; id: string; placed: Placed } => d.kind === 'robot')
      .filter((d) => showIds || d.id === selectedId || d.id === hoverId)
      .filter((d) => onScreen([rel(d.placed.p)]))
      .sort((a, b) => a.placed.p.y - b.placed.p.y);

    const place = (d: { id: string; placed: Placed }) => {
      const r = d.placed.robot;
      const bat = r.battery_pct === null ? NO_VALUE : `${Math.round(r.battery_pct)}%`;
      const age = seenAge(r, now);
      /* Pins that print their own age drop the chip's age field — see
         `RobotMarker`. The chip is that much narrower, and the placer has to
         know it or every one of them reserves 40 px it will not use. */
      const pinAge = isHollow(r) || (age !== null && age > SEEN_WARN_MS);
      const ageStr = pinAge ? '' : age === null ? NO_VALUE : duration(age, { compact: 1 });
      /* A flagged robot whose chip fields cannot explain its colour prints the
         reading that can — the same one `RobotChip` renders, through the same
         helper, so the reserved box and the drawn chip are one number. */
      const whyR = chipReason(r);
      const whyStr = whyR === null ? '' : chipReasonText(whyR);
      /* The tick, dividers and insets are the constants in `.markerLabel`. */
      const wide =
        14 +
        r.robot_id.length * MONO +
        (pinAge ? 9 : 18) +
        (bat.length + ageStr.length) * MONO +
        (whyStr === '' ? 0 : 9 + whyStr.length * MONO);
      const narrow = 14 + r.robot_id.length * MONO;
      const p = d.placed.p;
      /* Four sides, two widths, in the order that costs the operator least:
         the full chip below, then above, then out to either side, then the id
         alone in the same four places. A pin in a crowded yard now has eight
         ways to wear its name instead of two. */
      const opts: { side: LabelSide; compact: boolean; w: number }[] = [];
      for (const compact of [false, true]) {
        const w = compact ? narrow : wide;
        for (const side of SIDES) opts.push({ side, compact, w });
      }
      /* LABEL_OFF is the gap from the pin's centre to the chip's near edge, so
         a chip on any of the four sides clears the 15.2px needle and the
         11.6px fault ring rather than cutting the arc of its own marker. */
      /* A chip hung on the pin's right has to clear the pin's OWN age text,
         which is the one obstacle the `own` exemption above would otherwise
         wave it straight through. */
      const rightOff = Math.max(LABEL_OFF, pinRightExtent(r, now) + LABEL_GAP);
      const boxFor = (side: LabelSide, w: number): Rect => {
        if (side === 'above') {
          const y0 = p.y - LABEL_OFF - LABEL_H;
          return { x0: p.x - w / 2, y0, x1: p.x + w / 2, y1: y0 + LABEL_H };
        }
        if (side === 'below') {
          const y0 = p.y + LABEL_OFF;
          return { x0: p.x - w / 2, y0, x1: p.x + w / 2, y1: y0 + LABEL_H };
        }
        const y0 = p.y - LABEL_H / 2;
        return side === 'right'
          ? { x0: p.x + rightOff, y0, x1: p.x + rightOff + w, y1: y0 + LABEL_H }
          : { x0: p.x - LABEL_OFF - w, y0, x1: p.x - LABEL_OFF, y1: y0 + LABEL_H };
      };
      /** A side's box expressed as the anchor the chip is rendered from. */
      const anchorFor = (side: LabelSide, w: number) => {
        const b = boxFor(side, w);
        if (side === 'above') return { x: p.x, y: b.y1, ax: 0.5, ay: 1 };
        if (side === 'below') return { x: p.x, y: b.y0, ax: 0.5, ay: 0 };
        return side === 'right'
          ? { x: b.x0, y: p.y, ax: 0, ay: 0.5 }
          : { x: b.x1, y: p.y, ax: 1, ay: 0.5 };
      };
      /* A chip sliced by the pane's own edge is a truncated readout, so the
         frame is an obstacle like any other: the placer swings the chip to
         the side that keeps it whole rather than letting it run off. */
      const whole = (b: Rect) =>
        b.x0 >= origin.x + 4 &&
        b.x1 <= origin.x + size.x - 4 &&
        b.y0 >= origin.y + 2 &&
        b.y1 <= origin.y + size.y - 2;
      for (const o of opts) {
        const b = boxFor(o.side, o.w);
        if (hits(b, d.id) || !whole(b)) continue;
        taken.push(b);
        chipBoxes.set(d.id, b);
        const a = anchorFor(o.side, o.w);
        labelPlan.set(d.id, { ...a, compact: o.compact, leader: null });
        return;
      }
      /* Nothing against the pin is free. Rather than print an id across a
         neighbour, the chip STEPS AWAY by whole rows and grows a 1px leader
         back to its own marker — the id is still unambiguously that pin's id,
         and nothing underneath it is destroyed. */
      const clampX = (x: number) =>
        Math.min(
          Math.max(x, origin.x + 4 + narrow / 2),
          origin.x + size.x - 4 - narrow / 2,
        );
      for (let k = 1; k <= CHIP_STEPS; k += 1) {
        for (const dir of [1, -1]) {
          for (const dx of [0, -narrow / 2 - 22, narrow / 2 + 22]) {
            const cx = clampX(p.x + dx);
            const y0 = p.y + LABEL_OFF + dir * k * CHIP_ROW;
            const b: Rect = { x0: cx - narrow / 2, y0, x1: cx + narrow / 2, y1: y0 + LABEL_H };
            if (hits(b, d.id) || !whole(b)) continue;
            taken.push(b);
            chipBoxes.set(d.id, b);
            const lead = leaderTo(b, p, PIN_R);
            labelPlan.set(d.id, {
              x: cx,
              y: y0,
              ax: 0.5,
              ay: 0,
              compact: true,
              leader: lead,
            });
            leaders.push(lead);
            return;
          }
        }
      }
      /* The robot the operator selected or is pointing at ALWAYS wears its id.
         Falling through to no label at all is how the one marker you asked
         for becomes the one marker you cannot name; it takes the least-worst
         side instead, and its accent border and raised z-index make it the
         chip that reads on top. Every other pin's chip is dropped and
         counted, and the header prints the count. */
      if (d.id === selectedId || d.id === hoverId) {
        let best: { side: LabelSide; area: number } | null = null;
        for (const side of SIDES) {
          const b = boxFor(side, narrow);
          let area = 0;
          for (const o of taken) {
            if (o.own === d.id) continue;
            const w = Math.min(b.x1, o.x1) - Math.max(b.x0, o.x0);
            const h = Math.min(b.y1, o.y1) - Math.max(b.y0, o.y0);
            if (w > 0 && h > 0) area += w * h;
          }
          if (best === null || area < best.area) best = { side, area };
        }
        const side = best?.side ?? 'below';
        const b0 = boxFor(side, narrow);
        /* Even the last resort stays whole: the box is nudged back inside the
           frame, and a nudge big enough to break the join grows a leader. */
        const shift =
          Math.max(0, origin.x + 4 - b0.x0) - Math.max(0, b0.x1 - (origin.x + size.x - 4));
        const b: Rect = { ...b0, x0: b0.x0 + shift, x1: b0.x1 + shift };
        taken.push(b);
        chipBoxes.set(d.id, b);
        const a = anchorFor(side, narrow);
        const lead = Math.abs(shift) > 6 ? leaderTo(b, p, PIN_R) : null;
        if (lead !== null) leaders.push(lead);
        labelPlan.set(d.id, { ...a, x: a.x + shift, compact: true, leader: lead });
        return;
      }
      suppressed += 1;
    };

    /* Two passes. The selection and the hovered pin are the two labels an
       operator is actually looking for, so they are placed against the pins,
       the panels and every NAMED chip — a site or a capture session sliced by
       the id of the robot you just clicked is two labels destroyed, not one
       label won. Only the graticule's numbers are deferred: they are the one
       text on this map that a neighbour can be read off, so they alone yield
       to the selection. */
    taken.push(...chromeHard);
    for (const d of rows) if (d.id === selectedId || d.id === hoverId) place(d);
    taken.push(...chromeSoft);
    for (const d of rows) if (d.id !== selectedId && d.id !== hoverId) place(d);
  }

  /* The window chip is the last thing placed, and it steps off the track's
     oldest vertex until it is clear of everything already there — otherwise
     the one caption that says how old this line is ends up underneath the
     label of the robot the line belongs to, or through a cluster bubble. It
     never settles for a compromise: the first CLEAR step wins, and a step
     that had to travel grows a leader back to the vertex it is captioning.
     If the stage has nowhere clear at all it is dropped and counted, because
     a timestamp printed across a robot's id destroys two readings. */
  let trackChipAt: { x: number; y: number } | null = null;
  if (trackStart !== null && fixes.length > 1) {
    const base = abs(clampToView(trackStart));
    /* The estimate runs a little WIDE on purpose: a chip that reserves three
       pixels it does not use yields slightly early, and a chip that reserves
       three fewer than it needs prints over its neighbour. */
    const w =
      20 +
      (`${fixes.length} fixes`.length +
        `${zulu(fixes[0]?.t)} → ${zulu(fixes[fixes.length - 1]?.t)}`.length) *
        6.2;
    const loX = origin.x + w / 2 + 8;
    const hiX = origin.x + size.x - w / 2 - 8;
    const loY = origin.y + 8;
    const hiY = origin.y + size.y - 30;
    let found: { x: number; y: number } | null = null;
    for (const dy of [12, -30, 36, -54, 60, -78, 84, -102, 108]) {
      for (const dx of [0, -w / 2 - 22, w / 2 + 22]) {
        const x = hiX < loX ? loX : Math.min(Math.max(base.x + dx, loX), hiX);
        const y = hiY < loY ? loY : Math.min(Math.max(base.y + dy, loY), hiY);
        /* The box reserved here and the box `.trackChip` paints are the same
           rectangle: the chip is 16px tall, hung from `y`, plus a 3px gutter
           on every side. */
        const b = { x0: x - w / 2 - 3, y0: y - 3, x1: x + w / 2 + 3, y1: y + LABEL_H + 3 };
        if (taken.some((o) => b.x0 < o.x1 && b.x1 > o.x0 && b.y0 < o.y1 && b.y1 > o.y0)) continue;
        found = { x, y };
        taken.push(b);
        break;
      }
      if (found !== null) break;
    }
    if (found === null) suppressed += 1;
    else {
      trackChipAt = found;
      /* Far enough from the vertex that the join has to be drawn. */
      if (Math.hypot(found.x - base.x, found.y - base.y) > 34) {
        const ex = Math.min(Math.max(base.x, found.x - w / 2 + 4), found.x + w / 2 - 4);
        const ey =
          base.y < found.y ? found.y : base.y > found.y + LABEL_H ? found.y + LABEL_H : base.y;
        leaders.push({ x0: ex, y0: ey, x1: base.x, y1: base.y });
      }
    }
  }

  /* ---- the inspector's leader -------------------------------------------
     The docked card is tied to its marker by one dashed accent hairline, and
     it is drawn HERE — in the label layer, under the chips — rather than by
     the card itself. Drawn by the card it sits above the whole overlay and
     runs through whatever chips lie between the two, which is the same defect
     as a capture bbox through a robot's id: a line that points at one marker
     by striking out another marker's battery reading. Under the chips it
     passes behind them, and the chips are opaque, so the tie survives and
     nothing is defaced. */
  let cardLead: Leader | null = null;
  if (selectedId !== null && clearLeft > 0) {
    const sel = drawn.find((d) => d.kind === 'robot' && d.id === selectedId);
    const stage = map.getContainer().parentElement;
    const cardEl = stage?.querySelector<HTMLElement>('[data-anchored="true"]') ?? null;
    if (sel !== undefined && sel.kind === 'robot' && cardEl !== null) {
      const sr = map.getContainer().getBoundingClientRect();
      const cr = cardEl.getBoundingClientRect();
      const card = {
        x0: cr.left - sr.left + origin.x,
        y0: cr.top - sr.top + origin.y,
        x1: cr.right - sr.left + origin.x,
        y1: cr.bottom - sr.top + origin.y,
      };
      const p = sel.placed.p;
      const covered =
        p.x <= card.x1 + 6 && p.y >= card.y0 - 6 && p.y <= card.y1 + 6;
      if (!covered && onScreen([rel(p)])) {
        const ay = Math.min(Math.max(p.y, card.y0 + 14), card.y1 - 14);
        const ax = card.x1;
        /* Stop at the marker's own chip, or at its ring — never on the glyph. */
        const chip = chipBoxes.get(selectedId) ?? null;
        const dx = p.x - ax;
        const dy = p.y - ay;
        const len = Math.hypot(dx, dy);
        let end = { x: p.x, y: p.y };
        if (len > 1) {
          const steps = Math.min(320, Math.ceil(len));
          for (let i = 0; i <= steps; i += 1) {
            const t = i / steps;
            const qx = ax + dx * t;
            const qy = ay + dy * t;
            const onChip =
              chip !== null && qx >= chip.x0 && qx <= chip.x1 && qy >= chip.y0 && qy <= chip.y1;
            if (onChip || Math.hypot(qx - p.x, qy - p.y) <= PIN_R + 2) {
              end = { x: qx, y: qy };
              break;
            }
          }
        }
        cardLead = { x0: ax, y0: ay, x1: end.x, y1: end.y };
      }
    }
  }

  /* The count of dropped chips belongs in the map's own header, not in this
     layer — a map that says "27 drawn" while naming 23 of them is answering a
     question the operator did not ask. Reported after commit so the header
     and the picture describe the same render. */
  useEffect(() => {
    onHidden(suppressed);
  }, [onHidden, suppressed]);

  /* The layer's single tab stop: the selection if it is drawn, else the first
     thing on the map. */
  const tabId =
    selectedId !== null && drawn.some((d) => d.id === selectedId)
      ? selectedId
      : (drawn[0]?.id ?? null);

  const onMarkerKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    const items = Array.from(
      e.currentTarget.querySelectorAll<HTMLButtonElement>('button[data-pin]'),
    );
    if (items.length === 0) return;
    const i = items.indexOf(document.activeElement as HTMLButtonElement);
    if (i === -1) return;
    const last = items.length - 1;
    const next =
      e.key === 'Home'
        ? 0
        : e.key === 'End'
          ? last
          : e.key === 'ArrowRight' || e.key === 'ArrowDown'
            ? Math.min(last, i + 1)
            : Math.max(0, i - 1);
    items[next]?.focus();
    e.preventDefault();
  };

  return (
    <div className={styles.overlay}>
      <svg
        className={styles.vector}
        style={{ left: box.x, top: box.y, width: box.w, height: box.h }}
        viewBox={`0 0 ${box.w} ${box.h}`}
        aria-hidden="true"
      >
        {grat !== null && (
          <g className={styles.grat}>
            {grat.lons.map((l) => (
              <line key={`x${l.v}`} x1={l.x} x2={l.x} y1={0} y2={box.h} />
            ))}
            {grat.lats.map((l) => (
              <line key={`y${l.v}`} x1={0} x2={box.w} y1={l.y} y2={l.y} />
            ))}
          </g>
        )}

        {sites.map((s) =>
          s.d !== null ? (
            <path key={s.key} className={styles.site} d={s.d} />
          ) : (
            <circle key={s.key} className={styles.site} cx={s.c.x} cy={s.c.y} r={s.r} />
          ),
        )}

        {/* Capture footprints: the ground the selected robot actually mapped,
            straight off each scan's WGS84 bounding box. Stroke-only over a 5%
            fill, per THE BAR §5 — and the PIPELINE STATE is carried by the
            stroke's dash, not by a colour, so four sessions in four states
            read as four shapes rather than four hues. Only `failed` spends
            ink on red, because only `failed` needs acting on. */}
        <g className={styles.capture}>
          {capture.map((c) => (
            <rect
              key={c.e.id}
              data-state={c.e.state ?? 'unknown'}
              data-hovered={c.e.id === capHover}
              x={c.x}
              y={c.y}
              width={c.w}
              height={c.h}
            />
          ))}
        </g>

        {/* Ambient tracks sit under the selected one and under every symbol,
            each in its own robot's state hue on the shared age ramp. */}
        {ambientDrawn.map((t) => (
          <g key={t.id} className={styles.ambient} data-tone={t.tone}>
            {t.bands.map((b, i) => (
              <path key={i} d={b.d} style={{ opacity: b.o }} />
            ))}
            {t.head !== null && <path className={styles.ambientHead} d={t.head} />}
            {showFixDots &&
              t.pts.map((p, i) => (
                <circle key={`f${i}`} className={styles.ambientFix} cx={p.x} cy={p.y} r={1} />
              ))}
            {t.tip !== null && (
              <circle className={styles.ambientTip} cx={t.tip.x} cy={t.tip.y} r={2.4} />
            )}
          </g>
        ))}

        {bands.length > 0 && (
          <g className={styles.track}>
            {bands.map((b, i) => (
              <path key={i} d={b.d} style={{ opacity: b.o }} />
            ))}
            {showFixDots &&
              trackPts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={1.1} />)}
            {trackStart !== null && (
              <rect
                className={styles.trackStart}
                x={trackStart.x - 2.5}
                y={trackStart.y - 2.5}
                width={5}
                height={5}
              />
            )}
            {trackHead !== null && (
              <circle className={styles.trackHead} cx={trackHead.x} cy={trackHead.y} r={2.6} />
            )}
          </g>
        )}

        {/* Tasking, drawn last of the vectors and stroke-only: a 1px dashed
            leg from where the robot IS to where it has been TOLD to be, and
            an open target on the commanded position. Nothing here is filled —
            a solid mark would read as a thing that exists on the ground, and
            this is an intention, not an object. */}
        {drawnOrders.length > 0 && (
          <g className={styles.order}>
            {drawnOrders.map((x) => (
              <g key={x.o.id} data-status={x.o.status}>
                <line className={styles.orderLeg} x1={x.q.x} y1={x.q.y} x2={x.p.x} y2={x.p.y} />
                <circle className={styles.orderTarget} cx={x.p.x} cy={x.p.y} r={ORDER_R} />
                <line
                  className={styles.orderCross}
                  x1={x.p.x - ORDER_R - 2.5}
                  y1={x.p.y}
                  x2={x.p.x + ORDER_R + 2.5}
                  y2={x.p.y}
                />
                <line
                  className={styles.orderCross}
                  x1={x.p.x}
                  y1={x.p.y - ORDER_R - 2.5}
                  x2={x.p.x}
                  y2={x.p.y + ORDER_R + 2.5}
                />
              </g>
            ))}
          </g>
        )}
      </svg>

      {/* One tab stop for the whole marker layer, arrows to traverse it. A
          canvas of 27 markers that each take a tab stop — on top of the 28
          roster rows that address the same robots — is a keyboard trap, not
          keyboard access. Enter/Space still selects, as on any button. */}
      <div className={styles.markers} onKeyDown={onMarkerKey}>
        {drawn.map((d) =>
          d.kind === 'robot' ? (
            <RobotMarker
              key={d.id}
              placed={d.placed}
              selected={d.id === selectedId}
              hovered={d.id === hoverId}
              tabbable={d.id === tabId}
              orders={tasking.get(d.id) ?? EMPTY_COMMANDS}
              onSelect={onSelect}
              onHover={onHover}
              now={now}
            />
          ) : (
            <ClusterMarker
              key={d.id}
              cluster={d.cluster}
              map={map}
              tabbable={d.id === tabId}
              cardOpen={selectedId !== null}
              tasking={tasking}
              onHover={onHover}
            />
          ),
        )}
      </div>

      {/* ---- the label layer -------------------------------------------
          One layer, above every vector overlay AND every symbol, carrying
          every string this map prints over the world. Nothing here is
          translucent and nothing here overlaps: the placer above resolved
          all of it against the pins, the panels and each other, stepped
          whatever would not fit off its anchor onto a leader, and counted
          whatever it still had to drop. THE BAR §5 — map labels are small
          opaque chips with a hairline, anchored to what they name. */}
      <div className={styles.labels}>
        {/* The viewBox is stated in LAYER coordinates — the space the placer
            resolved every chip in — so a leader needs no second frame of
            reference to be drawn in the right place. */}
        <svg
          className={styles.leadSvg}
          style={{ left: box.x, top: box.y, width: box.w, height: box.h }}
          viewBox={`${box.x} ${box.y} ${box.w} ${box.h}`}
          aria-hidden="true"
        >
          {leaders.map((l, i) => (
            <line key={i} className={styles.lead} x1={l.x0} y1={l.y0} x2={l.x1} y2={l.y1} />
          ))}
          {cardLead !== null && (
            <line
              className={styles.cardLead}
              x1={cardLead.x0}
              y1={cardLead.y0}
              x2={cardLead.x1}
              y2={cardLead.y1}
            />
          )}
        </svg>

        {/* The graticule's degree readouts. Drawn here rather than in the
            vector SVG so no hull, footprint or track can rule through the one
            number that says where this picture is. Set on an opaque ground
            like every other string on the stage — a 10px readout on an 85%
            halo is still a readout with a line through it. */}
        {gratChips.map((g) => (
          <span
            key={g.key}
            className={styles.gratChip}
            style={{ left: g.x, top: g.y, transform: `translate(0, ${-g.ay * 100}%)` }}
            aria-hidden="true"
          >
            {g.text}
          </span>
        ))}

      {capture.map((c) => {
        const a = captureChips.get(c.e.id);
        if (a === undefined) return null;
        return (
          <span
            key={c.e.id}
            className={styles.captureChip}
            data-state={c.e.state ?? 'unknown'}
            data-hovered={c.e.id === capHover}
            onMouseEnter={() => setCapHover(c.e.id)}
            onMouseLeave={() => setCapHover(null)}
            style={{
              left: a.x,
              top: a.y,
              transform: `translate(${-a.ax * 100}%, ${-a.ay * 100}%)`,
            }}
            title={`Capture session ${c.e.session} · ${
              c.e.state ?? 'state unknown'
            } · started ${zulu(c.e.started ?? undefined)}${
              c.e.ran === null ? ' · still running' : ` · ran ${duration(c.e.ran)}`
            } · bbox ${c.e.south.toFixed(5)},${c.e.west.toFixed(5)} → ${c.e.north.toFixed(
              5,
            )},${c.e.east.toFixed(5)}`}
          >
            <span className={styles.captureTick} aria-hidden="true" />
            <span className={styles.captureName}>{c.e.name}</span>
            <span className={styles.captureMeta}>
              {c.e.ran === null ? NO_VALUE : duration(c.e.ran)}
            </span>
          </span>
        );
      })}

      {drawnOrders.map((x) => {
        const a = orderChips.get(x.o.id);
        if (a === undefined) return null;
        const range = rangeM(x.o.from, { lat: x.o.lat, lon: x.o.lon });
        const m = metres(range);
        return (
          <span
            key={x.o.id}
            className={styles.orderChip}
            data-status={x.o.status}
            style={{
              left: a.x,
              top: a.y,
              transform: `translate(${-a.ax * 100}%, ${-a.ay * 100}%)`,
            }}
            title={`${x.o.robotId} — ${x.o.command} ${x.o.status}, issued ${zulu(
              x.o.issuedAt,
            )} (${duration(Math.max(0, now - x.o.issuedAt))} ago) to ${x.o.lat.toFixed(
              5,
            )}, ${x.o.lon.toFixed(5)} — ${Math.round(range)} m from its current fix`}
          >
            <span className={styles.orderTick} aria-hidden="true" />
            <span className={styles.orderWho}>{x.o.robotId}</span>
            <span className={styles.orderVerb}>{x.o.command}</span>
            <span className={styles.orderRange}>
              {m.value}
              <span className={styles.orderUnit}>{m.unit}</span>
            </span>
          </span>
        );
      })}

      {sites.map((s) => {
        const span = metres(s.spanM);
        const at2 = siteChips.get(s.key);
        /* Placed or dropped — never printed on top of something. A site whose
           name would not fit anywhere is counted in the header instead. */
        if (at2 === undefined) return null;
        return (
          <span
            key={s.key}
            className={styles.siteLabel}
            style={{
              left: at2.x,
              top: at2.y,
              transform: `translate(${-at2.ax * 100}%, ${-at2.ay * 100}%)`,
            }}
            data-below={at2.below}
            data-tone={HEALTH_TONE[s.worst]}
            title={`${s.label} — ${s.shown} of ${s.members} robots with a fix drawn under this filter, worst reading ${HEALTH_LABEL[
              s.worst
            ].toLowerCase()}. Extent is the hull of ${s.samples} recorded fixes over the whole fleet, ${Math.round(
              s.spanM,
            )} m across.`}
          >
            <span className={styles.siteEdge} aria-hidden="true" />
            {s.label}
            <span className={styles.siteCount}>
              {s.shown !== s.members && (
                <span className={styles.siteShown}>{s.shown}/</span>
              )}
              {s.members}
            </span>
            <span className={styles.siteSpan}>
              {span.value}
              <span className={styles.siteUnit}>{span.unit}</span>
            </span>
          </span>
        );
      })}

      {edgeSites.map((s) => {
        const p = edgeAt.get(s.key);
        if (p === undefined) return null;
        return (
          <span
            key={s.key}
            className={styles.edgeSite}
            style={{
              left: p.x,
              top: p.y,
              transform: `translate(${-p.ax * 100}%, ${-p.ay * 100}%)`,
            }}
            data-tone={HEALTH_TONE[s.worst]}
            title={`${s.label} — off screen, ${Math.round(
              s.rangeM / 1000,
            )} km from the centre of this view. ${s.shown} of ${s.members} robots with a fix drawn under this filter, worst reading ${HEALTH_LABEL[
              s.worst
            ].toLowerCase()}.`}
          >
            <svg width={9} height={9} viewBox="0 0 9 9" aria-hidden="true">
              <path d="M1.4 1 L8 4.5 L1.4 8 Z" transform={`rotate(${s.deg} 4.5 4.5)`} />
            </svg>
            {s.short}
            <span className={styles.siteCount}>
              {s.shown !== s.members && (
                <span className={styles.siteShown}>{s.shown}/</span>
              )}
              {s.members}
            </span>
            <span className={styles.siteSpan}>
              {s.range.value}
              <span className={styles.siteUnit}>{s.range.unit}</span>
            </span>
          </span>
        );
      })}

      {/* The selected track states its own window where the track begins. */}
      {trackChipAt !== null && (
        <span
          className={styles.trackChip}
          style={{ left: trackChipAt.x, top: trackChipAt.y }}
          title="The window this track covers — every vertex is one telemetry record"
        >
          <span className={styles.trackChipN}>{fixes.length} fixes</span>
          <span className={styles.trackChipRange}>
            {zulu(fixes[0]?.t)} → {zulu(fixes[fixes.length - 1]?.t)}
          </span>
        </span>
      )}
        {drawn.map((d) => {
          if (d.kind !== 'robot') return null;
          const plan = labelPlan.get(d.id);
          if (plan === undefined) return null;
          return (
            <RobotChip
              key={d.id}
              robot={d.placed.robot}
              plan={plan}
              selected={d.id === selectedId}
              hovered={d.id === hoverId}
              onSelect={onSelect}
              onHover={onHover}
              now={now}
            />
          );
        })}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
   The pin
   --------------------------------------------------------------------------
   One symbol, four independent readings, and NOT ONE OF THEM CARRIED BY
   BRIGHTNESS ALONE.

     ring colour   the robot's worst reading  (5 tones)
     ring dash     nothing is arriving from it
     core          filled = the fix is current · open = the fix is old ·
                   absent = there is no telemetry at all
     second ring   faulted — the one thing that has to be findable across a
                   whole yard without reading a label
     wedge         heading; no wedge means the robot does not report one
     age suffix    printed on the pin itself when the pose is old or gone

   Every stroke is carried on a near-opaque `--bg-0` ground of its own,
   because the thing under a map marker is whatever the world happens to look
   like there. And nothing is dimmed: dimming a symbol is how you say "no
   data" on a flat panel, where the ground is a known constant. Over a
   basemap it just makes the marker disappear into the terrain — and the
   markers it would hide are exactly the ones an operator opened this screen
   to find. Absence of data is said with SHAPE here, and the shape is drawn
   at full strength. THE BAR §5 (maps), §3 (grey is a state).
   -------------------------------------------------------------------------- */

interface PinShape {
  /** Heading in degrees true, or `null` when the robot does not report one. */
  heading: number | null;
  /** Nothing is arriving: the ring goes dashed and the core disappears. */
  hollow: boolean;
  /** The newest fix is older than the freshness limit: the core opens up. */
  staleFix: boolean;
  /** Faulted: a second ring, outside the first. */
  alert: boolean;
  /** Carrying a command that has been issued and not acknowledged. */
  tasked: boolean;
}

/** The pin's geometry, in one place, so the map and the legend cannot drift. */
function PinArt({ heading, hollow, staleFix, alert, tasked }: PinShape) {
  const groundR = alert ? ALERT_GROUND_R : GROUND_R;
  return (
    <>
      {tasked && <circle className={styles.taskRing} cx={PIN_C} cy={PIN_C} r={TASK_R} />}
      {heading !== null && (
        <path
          className={styles.needleShadow}
          d={`M${PIN_C} ${PIN_C - NEEDLE_TIP - 1.5} L${PIN_C + 4.5} ${PIN_C - 8.4} L${PIN_C - 4.5} ${PIN_C - 8.4} Z`}
          transform={`rotate(${heading} ${PIN_C} ${PIN_C})`}
        />
      )}
      <circle className={styles.ground} cx={PIN_C} cy={PIN_C} r={groundR} />
      {alert && <circle className={styles.alertRing} cx={PIN_C} cy={PIN_C} r={ALERT_R} />}
      {heading !== null && (
        <path
          className={styles.needle}
          d={`M${PIN_C} ${PIN_C - NEEDLE_TIP} L${PIN_C + 3.4} ${PIN_C - 8.1} L${PIN_C - 3.4} ${PIN_C - 8.1} Z`}
          transform={`rotate(${heading} ${PIN_C} ${PIN_C})`}
        />
      )}
      <circle
        className={styles.ring}
        cx={PIN_C}
        cy={PIN_C}
        r={RING_R}
        data-dashed={hollow}
      />
      {!hollow &&
        (staleFix ? (
          <circle className={styles.coreOpen} cx={PIN_C} cy={PIN_C} r={2.9} />
        ) : (
          <circle className={styles.core} cx={PIN_C} cy={PIN_C} r={2.9} />
        ))}
    </>
  );
}

function RobotMarker({
  placed,
  selected,
  hovered,
  tabbable,
  orders,
  onSelect,
  onHover,
  now,
}: {
  placed: Placed;
  selected: boolean;
  hovered: boolean;
  tabbable: boolean;
  /** Commands issued to this robot and not yet acknowledged. */
  orders: readonly Command[];
  onSelect: (id: string | null) => void;
  onHover: (id: string | null) => void;
  now: number;
}) {
  const r = placed.robot;
  const health = healthOf(r);
  const hollow = isHollow(r);
  const heading = r.heading_deg;
  const age = seenAge(r, now);
  const stale = age !== null && age > SEEN_WARN_MS;
  const state = STATUS_STATE[r.status];
  const link = LIVENESS_STATE[r.liveness];
  /* An old or absent pose is stated ON the pin, not only in the chip beside
     it — a hollow marker with no reading next to it is the one symbol on this
     map that an operator has to be able to act on at a glance. The chip drops
     its own age field when the pin carries it, so the same number is never
     printed twice 20 px apart. */
  const pinAge = hollow || stale;
  /* The queue is spoken as a fact, not only as a ring: a symbol nobody can
     read the meaning of is decoration, and a screen reader gets no ring. */
  const queue =
    orders.length === 0
      ? ''
      : `, ${orders.length} command${orders.length === 1 ? '' : 's'} in flight: ${orders
          .map((c) => `${c.command} ${c.status}`)
          .join(', ')}`;

  return (
    <button
      type="button"
      data-pin="robot"
      data-marker={r.robot_id}
      tabIndex={tabbable ? 0 : -1}
      className={styles.marker}
      style={{ left: placed.p.x, top: placed.p.y }}
      data-tone={HEALTH_TONE[health]}
      data-selected={selected}
      data-hovered={hovered}
      data-hollow={hollow}
      data-stale={stale}
      aria-pressed={selected}
      aria-label={`${r.robot_id}, ${whyText(r)}, ${state.label}, ${link.label}, ${
        r.battery_pct === null ? 'no battery reading' : `battery ${Math.round(r.battery_pct)} percent`
      }, ${age === null ? 'never seen' : `last record ${duration(age)} ago`}, heading ${
        heading === null ? 'unknown' : `${Math.round(heading)} degrees`
      }${queue}`}
      /* The pin's colour, explained. A symbol painted amber whose readout
         does not name the reading that made it amber is colour carrying state
         on its own — the reasons are the evidence, in the same words the card
         and the operations exception list use. */
      title={`${r.robot_id} · ${whyText(r)}\n${state.label} · ${link.label} · ${
        age === null ? 'never seen' : `${duration(age)} old`
      } · ${heading === null ? 'no heading' : `${Math.round(heading)}°`}${
        orders.length === 0
          ? ''
          : ` · ${orders.map((c) => `${c.command} ${c.status}`).join(' · ')}`
      }`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(selected ? null : r.robot_id);
      }}
      onMouseEnter={() => onHover(r.robot_id)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(r.robot_id)}
      onBlur={() => onHover(null)}
    >
      <svg
        className={styles.glyph}
        width={PIN_BOX}
        height={PIN_BOX}
        viewBox={`0 0 ${PIN_BOX} ${PIN_BOX}`}
        aria-hidden="true"
      >
        {selected && <circle className={styles.halo} cx={PIN_C} cy={PIN_C} r={12.8} />}
        <PinArt
          heading={heading}
          hollow={hollow}
          staleFix={stale}
          alert={health === 'fault'}
          /* A selected pin is already entirely accent, halo included, so a
             third amber ring on it is mush — and the card 300 px away is
             printing that robot's whole queue, verb by verb, which is
             strictly more than a ring can say. */
          tasked={orders.length > 0 && !selected}
        />
        {pinAge && (
          <text
            className={styles.pinAge}
            x={PIN_C + (health === 'fault' ? ALERT_GROUND_R : GROUND_R) + 2.5}
            y={PIN_C + 0.5}
          >
            {duration(age, { compact: 1 })}
          </text>
        )}
      </svg>
    </button>
  );
}

/* --------------------------------------------------------------------------
   A pin's chip
   --------------------------------------------------------------------------
   Three readings per pin, not one: who, how much charge is left, and how old
   the pose is. That is the difference between a map you interrogate one
   marker at a time and one you can read.

   It is drawn in the label layer rather than inside the button, because a
   Leaflet pane gives every positioned `<svg>` a z-index of 200 and a chip
   nested in a marker therefore printed UNDER every vector this map draws —
   a capture bounding box through a robot's own id, a graticule dot-line
   through its battery reading. One layer, above everything, opaque.
   -------------------------------------------------------------------------- */

function RobotChip({
  robot,
  plan,
  selected,
  hovered,
  onSelect,
  onHover,
  now,
}: {
  robot: FixedRobot;
  plan: LabelPlan;
  selected: boolean;
  hovered: boolean;
  onSelect: (id: string | null) => void;
  onHover: (id: string | null) => void;
  now: number;
}) {
  const health = healthOf(robot);
  const age = seenAge(robot, now);
  /* Pins that print their own age drop the chip's age field, so the same
     number is never printed twice 20px apart. */
  const pinAge = isHollow(robot) || (age !== null && age > SEEN_WARN_MS);
  /* The reading that justifies this chip's colour when none of its own fields
     can. Dropped in the compact form, where even the id is all there is room
     for — the pin's title and the card still carry it. */
  const why = chipReason(robot);
  return (
    /* The chip is part of the marker, not a caption beside it: it is five
       times the pin's hit area and it is the part of the symbol an operator
       actually points at. It carries no tab stop and no accessible name of
       its own — the pin's button already has both, and two stops for one
       robot on top of its roster row is a keyboard trap, not access. */
    <button
      type="button"
      tabIndex={-1}
      className={styles.markerLabel}
      data-chip={robot.robot_id}
      data-tone={HEALTH_TONE[health]}
      data-selected={selected}
      data-hovered={hovered}
      aria-hidden="true"
      onClick={(e) => {
        e.stopPropagation();
        onSelect(selected ? null : robot.robot_id);
      }}
      onMouseEnter={() => onHover(robot.robot_id)}
      onMouseLeave={() => onHover(null)}
      style={{
        left: plan.x,
        top: plan.y,
        transform: `translate(${-plan.ax * 100}%, ${-plan.ay * 100}%)`,
      }}
    >
      <span className={styles.markerTick} aria-hidden="true" />
      <span className={styles.markerId}>{robot.robot_id}</span>
      {!plan.compact && (
        <span
          className={styles.markerBat}
          data-tone={levelTone(level(robot.battery_pct, BATTERY_WARN, BATTERY_BAD))}
          data-known={robot.battery_pct !== null}
        >
          {robot.battery_pct === null ? NO_VALUE : Math.round(robot.battery_pct)}
          {robot.battery_pct !== null && <span className={styles.markerUnit}>%</span>}
        </span>
      )}
      {!plan.compact && !pinAge && (
        <span className={styles.markerAge} data-tone={seenTone(age)}>
          {age === null ? NO_VALUE : duration(age)}
        </span>
      )}
      {!plan.compact && why !== null && (
        <span className={styles.markerWhy} data-tone={why.tone} data-hollow={why.hollow}>
          {chipReasonText(why)}
        </span>
      )}
    </button>
  );
}

function ClusterMarker({
  cluster,
  map,
  tabbable,
  cardOpen,
  tasking,
  onHover,
}: {
  cluster: Cluster;
  map: L.Map;
  tabbable: boolean;
  cardOpen: boolean;
  /** Every in-flight command, by robot — a collapsed robot's tasking ring is
   *  gone with its pin, so the count has to survive in the bubble's readout. */
  tasking: ReadonlyMap<string, readonly Command[]>;
  onHover: (id: string | null) => void;
}) {
  const members = cluster.members.map((m) => m.robot);
  const worst = worstHealth(members);
  const anyHollow = members.some(isHollow);
  const alert = worst === 'fault';
  /* How many of the collapsed robots are NOT nominal. A bare count answers
     "how many robots are here"; this answers "how many of them need me",
     which is the only reason to zoom in. */
  const flagged = members.filter((m) => healthOf(m) !== 'nominal').length;
  const tasked = members.filter((m) => (tasking.get(m.robot_id)?.length ?? 0) > 0).length;
  const label = `${members.length} robots, worst reading ${HEALTH_LABEL[worst].toLowerCase()}${
    tasked === 0 ? '' : `, ${tasked} carrying orders`
  }: ${members.map((m) => m.robot_id).join(', ')}`;

  return (
    <button
      type="button"
      data-pin="cluster"
      tabIndex={tabbable ? 0 : -1}
      className={styles.cluster}
      style={{ left: cluster.p.x, top: cluster.p.y }}
      data-tone={HEALTH_TONE[worst]}
      data-hollow={anyHollow}
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.stopPropagation();
        /* Capped at the basemap's last real level. Two robots parked 4 m
           apart would otherwise pull the camera to z18 and hand the operator
           a 4x-upscaled raster: blurred buildings that look like a lower-
           resolution world rather than a closer one. Past z16 the ground is
           an interpolation, and the health line says so. */
        map.fitBounds(cluster.bounds, {
          ...stagePadding(map, cardOpen),
          maxZoom: TILE_MAX_NATIVE + 1,
        });
      }}
      onMouseEnter={() => onHover(null)}
    >
      <svg
        className={styles.glyph}
        width={PIN_BOX}
        height={PIN_BOX}
        viewBox={`0 0 ${PIN_BOX} ${PIN_BOX}`}
        aria-hidden="true"
      >
        <circle className={styles.clusterGround} cx={PIN_C} cy={PIN_C} r={alert ? 15.4 : 13.6} />
        {alert && <circle className={styles.alertRing} cx={PIN_C} cy={PIN_C} r={14.6} />}
        <circle
          className={styles.clusterRing}
          cx={PIN_C}
          cy={PIN_C}
          r={12.6}
          data-dashed={anyHollow}
        />
        <text className={styles.clusterCount} x={PIN_C} y={PIN_C}>
          {members.length}
        </text>
      </svg>
      {flagged > 0 && (
        <span className={styles.clusterFlag} data-tone={HEALTH_TONE[worst]}>
          {flagged}
        </span>
      )}
    </button>
  );
}

/* ==========================================================================
   Entity card — anchored to its marker, never a floating modal
   ========================================================================== */

function EntityCard({
  robot,
  commands,
  commandsLive,
  latest,
  modeHeld,
  fixes,
  windowed,
  battery,
  trackLoading,
  now,
  live,
  at,
  follow,
  onFollow,
  onCentre,
  onClose,
  onOpen,
}: {
  robot: MapRobot;
  /** Commands issued to this robot and not yet acknowledged, oldest first. */
  commands: readonly Command[];
  /** The command queue holds no history, so off live it has nothing to say. */
  commandsLive: boolean;
  latest: TelemetryReading | null;
  /** The unbroken run of records carrying the current mode, measured. */
  modeHeld: {
    mode: RobotStatus;
    ms: number;
    bounded: boolean;
    records: number;
    since: number;
  } | null;
  fixes: readonly Fix[];
  /** The track window is narrower than everything held for this robot. */
  windowed: boolean;
  battery: { values: (number | null)[]; times: string[] };
  trackLoading: boolean;
  now: number;
  /** The surface's window is on the newest record. */
  live: boolean;
  /** The instant every reading on this card is read at. */
  at: number | null;
  follow: boolean;
  onFollow: () => void;
  onCentre: () => void;
  onClose: () => void;
  onOpen: () => void;
}) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const fix = hasFix(robot);

  /* The inspector DOCKS to the stage's leading edge rather than chasing its
     marker around the viewport. Skydio's dispatch inspector does exactly this
     (`skydio-dfr-incident-dispatch.png`: a fixed card floating ~11px off the
     queue rail, the map untouched behind it) and the reason is operational,
     not aesthetic: a card that follows the pin sits ON the pin's own label and
     on the track it is describing, and it moves every time the fleet moves.
     Docked, it costs one known rectangle of map and never covers the subject.

     A dashed leader still ties it to the marker, so "anchored" stays true —
     but the LABEL LAYER draws that line, not this component, because only the
     placer knows where every chip between the two ended up. See `MapOverlay`.
     What this component owes the overlay is one measurable rectangle, which is
     what `data-anchored` marks. */

  const age = seenAge(robot, now);
  const link = LIVENESS_STATE[robot.liveness];
  const noRec = robot.noRecord === true;
  /* Only claimed when the run's mode agrees with the record's own — two
     sources disagreeing is a fact to hide behind an em dash, not to average. */
  const held = modeHeld !== null && modeHeld.mode === robot.status ? modeHeld : null;
  const rssi = latest === null ? null : payloadNumber(latest.payload, 'link_rssi_dbm');
  const sats = latest === null ? null : payloadNumber(latest.payload, 'gnss_sats');
  const kind = metaString(robot, 'kind');
  const bay = metaString(robot, 'bay');
  const speed = latest === null ? null : latest.speed_mps;

  /* Every distance and bearing names the datum it is measured against — the
     label carries the frame, so no number on this card is unqualified. */
  const rows: KVItem[] = [
    {
      label: 'Fix wgs84',
      value: fix ? latlon(robot.lat, robot.lon) : NO_VALUE,
      title: 'Latitude, longitude — 5 decimals, ~1.1 m',
    },
    {
      label: 'Heading true',
      value:
        robot.heading_deg === null ? (
          NO_VALUE
        ) : (
          <span className={styles.headingCell}>
            <HeadingDial deg={robot.heading_deg} />
            {num(robot.heading_deg, 1)}
          </span>
        ),
      unit: robot.heading_deg === null ? undefined : '°',
      title: 'Degrees clockwise from TRUE north, not magnetic',
    },
    {
      label: 'Speed',
      value: speed === null ? NO_VALUE : num(speed, 2),
      unit: speed === null ? undefined : 'm/s',
      title: 'Ground speed at the last record',
    },
    {
      label: 'GNSS',
      value: sats === null ? NO_VALUE : String(sats),
      unit: sats === null ? undefined : 'sats',
      title: 'Satellites used in the last reported fix',
    },
    {
      label: 'Radio',
      value: rssi === null ? NO_VALUE : String(rssi),
      unit: rssi === null ? undefined : 'dBm',
      title: 'Link RSSI reported by the agent',
    },
    {
      label: 'Site',
      value: bay === null ? siteLabel(robot) : `${siteLabel(robot)} · ${bay}`,
      mono: false,
      title: 'Site and bay, from the robot record',
    },
    {
      label: 'Mode held',
      value:
        held === null
          ? NO_VALUE
          : `${held.bounded ? '\u2265 ' : ''}${duration(held.ms)}`,
      title:
        held === null
          ? 'No telemetry record carries a mode for this robot'
          : `${STATUS_STATE[held.mode].label} across ${held.records} unbroken record${
              held.records === 1 ? '' : 's'
            } since ${utc(new Date(held.since).toISOString())}${
              held.bounded
                ? ' — the oldest record held, so the true figure is at least this'
                : ''
            }`,
    },
    {
      label: live ? 'Last record' : 'Record in force',
      value: robot.last_seen === null ? NO_VALUE : utc(robot.last_seen),
      title:
        robot.last_seen === null
          ? live
            ? 'Never phoned home'
            : 'No record at or before the window\u2019s leading edge'
          : robot.last_seen,
    },
  ];

  return (
    <div
      ref={cardRef}
      className={styles.card}
      data-furniture="true"
      data-anchored={fix}
      role="dialog"
      aria-label={`${robot.robot_id} detail`}
    >
        <header className={styles.cardHead}>
          <span className={styles.cardId}>{robot.robot_id}</span>
          <span className={styles.cardName}>{robot.name ?? NO_VALUE}</span>
          <span className={styles.spacer} />
          <span
            className={styles.cardAge}
            data-tone={seenTone(age)}
            data-toned={age !== null && age > SEEN_WARN_MS}
            data-known={age !== null}
            title="Time since the last telemetry record"
          >
            {age === null ? NO_VALUE : duration(age)}
          </span>
          <IconButton icon="close" label="Close (Esc)" size="sm" onClick={onClose} />
        </header>

        {/* One provenance LINE, not a provenance panel: who it is, what it
            is, and which agent produced every number below. */}
        <div className={styles.cardProv} title="Morphology · model · agent version">
          {robot.morphology}
          <span className={styles.sep}>·</span>
          {robot.model}
          <span className={styles.sep}>·</span>
          <span className={styles.cardAgent} title="Agent / platform version">
            {robot.firmware ?? NO_VALUE}
          </span>
        </div>

        {/* Off live, the card says so in its own chrome rather than relying on
            the transport bar three panes away. Every row beneath this line —
            mode, link, battery, fix, heading, speed — is the record in force
            at that instant, so the instant is printed once, at the top. */}
        {!live && (
          <div className={styles.cardReplay} title="Every reading on this card is the telemetry record in force at this instant, not the current one">
            <StatusDot tone="warn" hollow />
            <span>Replay</span>
            <span className={styles.spacer} />
            <span className={styles.cardReplayAt}>{zulu(at ?? undefined)}</span>
          </div>
        )}

        <div className={styles.cardStates}>
          {noRec ? (
            <span className={styles.cardUnknown}>mode · link unknown at this instant</span>
          ) : (
            <>
              <StatusBadge status={robot.status} />
              <LivenessBadge
                liveness={robot.liveness}
                pulse={live && robot.liveness === 'live'}
              />
            </>
          )}
          <span className={styles.spacer} />
          {/* Real hardware or a simulation is never left implied. */}
          <span
            className={styles.cardKind}
            data-sim={kind === 'sim'}
            title={
              kind === 'sim'
                ? 'Simulated robot — Isaac Sim, not a physical machine'
                : kind === null
                  ? 'Build not declared on the robot record'
                  : 'Physical hardware'
            }
          >
            {kind ?? 'build ?'}
          </span>
        </div>

        {/* WHY this robot wears the class its pin, its row edge and the facet
            count give it. The four words the console publishes — fault / no
            data / held / not current — are the worst of six readings, and a
            verdict with no evidence is not something an operator can act on.

            MODE, LINK, BATT and SEEN are already on this card as the two
            badges, the meter and the header's age, so only the readings the
            card has no other place for are printed here — a cert inside its
            window, a de-registered robot. Reprinting `BATT 12%` two lines
            above a meter reading `12.1%` would be the same number twice at
            two precisions, which reads as a disagreement. The full basis is
            in the line's title. THE BAR §5: coloured text, never badges. */}
        {robot.reasons.length > 0 && (
          <div
            className={styles.cardWhy}
            data-tone={HEALTH_TONE[robot.health]}
            title={`Classed by the worst of ${HEALTH_BASIS} — the same rule the app strip and the operations exception list use. Readings behind it: ${robot.reasons
              .map((x) => `${x.label.toUpperCase()} ${x.text}`)
              .join(' · ')}`}
          >
            <span className={styles.cardWhyClass}>{HEALTH_LABEL[robot.health]}</span>
            <ReasonList reasons={offColumnReasons(robot)} />
            <span className={styles.spacer} />
            <span className={styles.cardWhyN}>
              {robot.reasons.length} of {FLEET_METRICS.length}
            </span>
          </div>
        )}

        {!fix && (
          <div className={styles.cardNoFix}>
            {noRec
              ? `No telemetry record held at ${zulu(
                  at ?? undefined,
                )} — the oldest record this console pulled for it is later than that. Not drawn on the map.`
              : `No position — ${link.label.toLowerCase()}${
                  robot.last_seen === null ? ', has never phoned home' : ''
                }. Not drawn on the map.`}
          </div>
        )}

        <div className={styles.cardBattery}>
          <Meter
            value={robot.battery_pct}
            label="Battery"
            size="md"
            warn={BATTERY_WARN}
            bad={BATTERY_BAD}
            digits={1}
          />
          <Sparkline
            values={battery.values}
            times={battery.times}
            unit="%"
            digits={1}
            warn={BATTERY_WARN}
            bad={BATTERY_BAD}
            width={84}
            height={18}
            head
            label={`${robot.robot_id} battery`}
          />
        </div>

        <KeyValue items={rows} layout="rows" className={styles.cardRows} />

        {/* What this machine has been TOLD to do, above what it has done.
            THE BAR §11.9: `sent` is not `accepted` — the two states this
            contract distinguishes are rendered as themselves, and a `move`
            prints the range from the robot's current fix to the position it
            was ordered to. Nothing here is actionable from the map: issuing
            and cancelling live on the robot's own page, one click away. */}
        <div className={styles.cardTrack}>
          <Eyebrow>Queue</Eyebrow>
          <span className={styles.spacer} />
          <span className={styles.cardTrackMeta}>
            {!commandsLive
              ? 'no queue history at a past instant'
              : commands.length === 0
                ? 'nothing in flight'
                : `${commands.length} in flight · oldest ${duration(
                    Math.max(0, now - Date.parse(commands[0]?.issued_at ?? '')),
                  )}`}
          </span>
        </div>
        {commandsLive &&
          commands.slice(0, 4).map((c) => {
            const wp = orderWaypoint(c);
            const range = wp !== null && fix ? rangeM({ lat: robot.lat, lon: robot.lon }, wp) : null;
            const m = range === null ? null : metres(range);
            const issued = Date.parse(c.issued_at);
            return (
              <div
                key={c.id}
                className={styles.cmdRow}
                data-status={c.status}
                title={`${c.command} · ${c.status} · issued by ${c.issued_by} at ${utc(
                  c.issued_at,
                )}${
                  wp === null
                    ? ''
                    : ` · commanded to ${wp.lat.toFixed(5)}, ${wp.lon.toFixed(5)}`
                }`}
              >
                <StatusDot
                  tone={c.status === 'sent' ? 'accent' : 'nominal'}
                  hollow={c.status !== 'sent'}
                />
                <span className={styles.cmdName}>{c.command}</span>
                <span className={styles.cmdState}>{c.status}</span>
                <span className={styles.spacer} />
                {m !== null && (
                  <span className={styles.cmdRange}>
                    {m.value}
                    <span className={styles.cmdUnit}>{m.unit}</span>
                  </span>
                )}
                {/* An order's age is toned on the same ladder every other age
                    on this surface uses. A command that has been on the wire
                    for four hours without an acknowledgement is the reading
                    this block exists to surface. */}
                <span
                  className={styles.cmdAge}
                  data-tone={ackTone(Number.isNaN(issued) ? null : Math.max(0, now - issued))}
                  data-toned={!Number.isNaN(issued) && now - issued > ACK_WARN_MS}
                >
                  {Number.isNaN(issued) ? NO_VALUE : duration(Math.max(0, now - issued))}
                </span>
              </div>
            );
          })}
        {commandsLive && commands.length > 4 && (
          <div className={styles.cmdMore}>+{commands.length - 4} more in flight</div>
        )}

        <div className={styles.cardTrack}>
          <Eyebrow>Track</Eyebrow>
          <span className={styles.spacer} />
          {/* The card reports the track it is actually drawing, which is the
              one inside the transport bar's window — and says WHICH of the
              two reasons an empty track has, because "no records" and "no
              records in this window" are different facts. */}
          <span className={styles.cardTrackMeta}>
            {trackLoading && fixes.length === 0
              ? 'loading…'
              : fixes.length === 0
                ? windowed
                  ? 'no fixes inside the window'
                  : `no fixes in the last ${SELECTED_LIMIT} records`
                : `${fixes.length} fixes · ${zulu(fixes[0]?.t)} → ${zulu(
                    fixes[fixes.length - 1]?.t,
                  )}`}
          </span>
        </div>

        <footer className={styles.cardFoot}>
          <div className={styles.cardVerbs}>
            <Button
              size="sm"
              icon="crosshair"
              onClick={onCentre}
              disabled={!fix}
              title={
                fix
                  ? fixes.length > 1
                    ? `Frame this robot and its ${fixes.length} recorded fixes`
                    : 'Centre the map on this robot'
                  : 'No position to centre on'
              }
            >
              {fixes.length > 1 ? 'Frame track' : 'Centre'}
            </Button>
            <Button
              size="sm"
              icon="waypoint"
              active={follow}
              aria-pressed={follow}
              onClick={onFollow}
              disabled={!fix || !live}
              title={
                !live
                  ? 'Replay — go live to follow this robot'
                  : fix
                    ? 'Keep the map centred as this robot moves'
                    : 'No position to follow'
              }
            >
              Follow
            </Button>
          </div>
          <Button variant="primary" size="md" block iconEnd="arrow-right" onClick={onOpen}>
            Open robot
          </Button>
      </footer>
    </div>
  );
}

function HeadingDial({ deg }: { deg: number }) {
  return (
    <svg className={styles.dial} width={13} height={13} viewBox="0 0 13 13" aria-hidden="true">
      <circle cx={6.5} cy={6.5} r={5} />
      <line x1={6.5} y1={6.5} x2={6.5} y2={1.6} transform={`rotate(${deg} 6.5 6.5)`} />
    </svg>
  );
}

/* ==========================================================================
   Corner furniture
   ========================================================================== */

/** A legend swatch that IS the map's symbol, at half scale — not a stand-in
 *  for it. The two cannot drift: they are the same geometry. */
function LegendPin({ tone, shape }: { tone: Tone; shape: PinShape }) {
  return (
    <svg
      className={styles.legendPin}
      data-tone={tone}
      width={PIN_BOX / 2}
      height={PIN_BOX / 2}
      viewBox={`0 0 ${PIN_BOX} ${PIN_BOX}`}
      aria-hidden="true"
    >
      <PinArt {...shape} />
    </svg>
  );
}

/**
 * The canonical pin for each health class, as the map actually draws it.
 *
 * Health and fix-age are two INDEPENDENT axes on this map, and this key only
 * owns the first. The alert ring is health's (a fault pin genuinely wears a
 * wider ground); fill-vs-open belongs to whether anything is arriving and is
 * keyed separately below, so `Not current` must not borrow the open ring —
 * a robot flagged for a 20-day cert has a 12-second-old fix, and drawing it
 * as an old fix here would be this legend telling a lie about the axis the
 * next three rows are about to explain.
 */
const HEALTH_PIN: Record<Health, PinShape> = {
  fault: { heading: null, hollow: false, staleFix: false, alert: true, tasked: false },
  nodata: { heading: null, hollow: true, staleFix: false, alert: false, tasked: false },
  held: { heading: null, hollow: false, staleFix: false, alert: false, tasked: false },
  notcurrent: { heading: null, hollow: false, staleFix: false, alert: false, tasked: false },
  nominal: { heading: null, hollow: false, staleFix: false, alert: false, tasked: false },
};

/* ==========================================================================
   Feed — what is actually arriving, per source
   ==========================================================================
   THE BAR §13 tells 13 and 27. One "connected" word standing in for every
   channel is the defect; this surface reads three independent HTTP feeds on
   three different cadences plus a realtime transport that is not running
   locally, and each one fails differently.

   Everything here is measured off the query cache — when each source last
   answered, how many requests are behind it, how many of them failed — never
   quoted from a nominal interval. The fleet-track layer is a fan-out of one
   request per robot, so its age is a RANGE: the freshest answer and the
   stalest one, because a layer whose slowest robot answered 4 minutes ago is
   not a layer that answered 4 seconds ago.

   And the realtime row states the truth about itself: no endpoint is
   configured, so the console is polling. Naming the env var is the difference
   between "offline" and "not wired up here". */
interface FeedSource {
  key: string;
  label: string;
  /** The endpoint or the gate, verbatim. */
  source: string;
  /** How much this source has delivered — rows, vertices, records. */
  count: string;
  /** Cadence as configured, not as hoped. */
  every: string;
  /** Age of the newest response, epoch ms — `null` when nothing has landed. */
  at: number | null;
  /** Age of the SLOWEST response in a fan-out, epoch ms. */
  atSlow?: number | null | undefined;
  tone: Tone;
  hollow?: boolean | undefined;
  note?: string | undefined;
}

function FeedCard({ sources, now }: { sources: readonly FeedSource[]; now: number }) {
  /* The absolute stamp of the last thing that arrived from anywhere. Relative
     ages are right everywhere else on this surface and wrong here: when a feed
     dies its relative age is the only thing still ticking, and it looks
     alive. THE BAR §11.3. */
  let newest: number | null = null;
  for (const f of sources) if (f.at !== null && (newest === null || f.at > newest)) newest = f.at;
  return (
    <div className={styles.feed}>
      <div className={styles.feedHead}>
        <Eyebrow>Feed</Eyebrow>
        <span className={styles.feedNote}>
          {sources.filter((f) => f.at !== null).length}/{sources.length} answering
        </span>
        <span className={styles.feedCol}>records</span>
        <span className={styles.feedCol}>every</span>
        <span className={styles.feedCol}>age</span>
      </div>
      {sources.map((f) => {
        const age = f.at === null ? null : Math.max(0, now - f.at);
        const slow =
          f.atSlow === null || f.atSlow === undefined ? null : Math.max(0, now - f.atSlow);
        return (
          <div key={f.key} className={styles.feedRow} title={f.note}>
            <StatusDot tone={f.tone} hollow={f.hollow} />
            <span className={styles.feedLabel}>{f.label}</span>
            <span className={styles.feedSrc}>{f.source}</span>
            <span className={styles.feedCount}>{f.count}</span>
            <span className={styles.feedEvery}>{f.every}</span>
            <span className={styles.feedAge} data-known={age !== null}>
              {age === null ? NO_VALUE : duration(age)}
              {slow !== null && slow - (age ?? 0) > 1500 && (
                <span className={styles.feedSlow}>&rarr;{duration(slow)}</span>
              )}
            </span>
          </div>
        );
      })}
      <div className={styles.feedFoot}>
        <span className={styles.feedFootKey}>last response</span>
        <span className={styles.spacer} />
        <span className={styles.feedFootVal}>
          {newest === null ? NO_VALUE : new Date(newest).toISOString()}
        </span>
      </div>
    </div>
  );
}

function Legend({
  counts,
  offStage,
  offStageIds,
  shape,
  drawn,
  trackFixes,
  selectedFixes,
  siteCount,
  sessionCount,
  orderCount,
  taskedCount,
  queued,
  live,
  read,
}: {
  counts: Record<Health, number>;
  /** Per class, robots the filter matched that have no position to draw. */
  offStage: Record<Health, number>;
  offStageIds: ReadonlyMap<Health, readonly string[]>;
  shape: { fresh: number; old: number; heading: number };
  /** Markers on the stage. Every count in this key is a share of it. */
  drawn: number;
  trackFixes: number;
  selectedFixes: number | null;
  siteCount: number;
  /** Capture footprints held for the selection, or `null` with none selected. */
  sessionCount: number | null;
  /** Commanded waypoints drawn — the subset of the queue that has geometry. */
  orderCount: number;
  /** Robots on this stage wearing the tasking ring. */
  taskedCount: number;
  /** Commands in flight for the robots on this stage — always ≥ the rings. */
  queued: number;
  live: boolean;
  /** The roster read every count in this census is a share of. */
  read: Read;
}) {
  /* The notation is folded away by default and the census is not.
     A key is read ONCE — the first week — and then it is furniture; a census
     is read every time the picture changes. Spending 130px of permanent map
     on "filled means the fix is current" is the legend teaching an operator
     who learned it on day one, so it costs one click instead. The three rows
     that stay are the only ones that answer a question the operator has right
     now: what is on this stage, and how bad is the worst of it. */
  const [keyOpen, setKeyOpen] = useState(false);
  return (
    <div className={styles.legend}>
      <div className={styles.legendHead}>
        <Eyebrow>Legend</Eyebrow>
        <span className={styles.spacer} />
        <span
          className={styles.legendNote}
          title="Every count in this census is of the markers currently DRAWN. The toolbar's health facets count the robots the filter MATCHES, over the same five classes — a robot with no position is in one and not the other, and any row where the two differ prints the difference as +n."
        >
          of <span className={styles.legendNoteN}>
            <Figure of={read} value={drawn} />
          </span> drawn
        </span>
        <button
          type="button"
          className={styles.legendKeyBtn}
          aria-expanded={keyOpen}
          onClick={() => setKeyOpen((v) => !v)}
          title={keyOpen ? 'Hide the symbol key' : 'What each mark on the map means'}
        >
          key
          <Icon name={keyOpen ? 'chevron-up' : 'chevron-down'} size={11} />
        </button>
      </div>
      <div className={styles.legendGrid}>
        {HEALTH_ORDER.map((h) => {
          const missing = offStage[h];
          const ids = offStageIds.get(h) ?? [];
          return (
            <span key={h} className={styles.legendRow}>
              <LegendPin tone={HEALTH_TONE[h]} shape={HEALTH_PIN[h]} />
              <span className={styles.legendLabel}>{HEALTH_LABEL[h]}</span>
              <span className={styles.legendCount}>
                <Figure of={read} value={counts[h]} />
                {/* The reconciliation, printed. Without it this row reads `0`
                    while the toolbar reads `1` for the same word, and nothing
                    on screen says why. */}
                {missing > 0 && (
                  <span
                    className={styles.legendOff}
                    title={`${missing} more ${HEALTH_LABEL[
                      h
                    ].toLowerCase()} in the filter with no position to draw: ${ids.join(', ')}`}
                  >
                    +{missing}
                  </span>
                )}
              </span>
            </span>
          );
        })}
      </div>
      {keyOpen && (
        <>
          {/* The pin's second axis, stated as its own key rather than left for
              the operator to infer from a ring they have not seen before. */}
          <div className={styles.legendGrid} data-geom="true">
            <span className={styles.legendRow}>
              <LegendPin
                tone="nominal"
                shape={{
                  heading: null,
                  hollow: false,
                  staleFix: false,
                  alert: false,
                  tasked: false,
                }}
              />
              <span className={styles.legendLabel}>
                Fix current
                <span className={styles.legendSub}>filled</span>
              </span>
              <span className={styles.legendCount}>{shape.fresh}</span>
            </span>
            <span className={styles.legendRow}>
              <LegendPin
                tone="nominal"
                shape={{
                  heading: null,
                  hollow: false,
                  staleFix: true,
                  alert: false,
                  tasked: false,
                }}
              />
              <span className={styles.legendLabel}>
                {`Fix over ${Math.round(SEEN_WARN_MS / 60_000)} min`}
                <span className={styles.legendSub}>open · age on pin</span>
              </span>
              <span className={styles.legendCount}>{shape.old}</span>
            </span>
            <span className={styles.legendRow}>
              <LegendPin
                tone="nominal"
                shape={{
                  heading: 0,
                  hollow: false,
                  staleFix: false,
                  alert: false,
                  tasked: false,
                }}
              />
              <span className={styles.legendLabel}>
                Heading true
                <span className={styles.legendSub}>wedge</span>
              </span>
              <span className={styles.legendCount}>{shape.heading}</span>
            </span>
          </div>
          {/* The geometry half of the key: what the lines and the polygon
              mean, each carrying the count of what is actually drawn. */}
          <div className={styles.legendGrid} data-geom="true">
            <span className={styles.legendRow}>
              <svg
                className={styles.legendRamp}
                width={22}
                height={9}
                viewBox="0 0 22 9"
                aria-hidden="true"
              >
                {[0, 1, 2, 3].map((i) => (
                  <rect
                    key={i}
                    x={i * 4.6}
                    y={3.6}
                    width={4.8}
                    height={1.7}
                    opacity={AMB_MIN_A + (AMB_MAX_A - AMB_MIN_A) * (i / 3) ** 1.4}
                  />
                ))}
                <circle cx={20} cy={4.5} r={2.2} />
              </svg>
              <span className={styles.legendLabel}>
                Track · in window
                <span
                  className={styles.legendSub}
                  title={`Each robot's own state hue, faded by fix age; the last ${Math.round(
                    HEAD_MS / 60_000,
                  )} minutes carry full weight and end in a filled head.`}
                >
                  state · age
                </span>
              </span>
              <span className={styles.legendCount}>{trackFixes}</span>
            </span>
            <span className={styles.legendRow}>
              <span className={styles.legendTrack} data-kind="selected" aria-hidden="true" />
              <span className={styles.legendLabel}>Selected · faded by age</span>
              <span className={styles.legendCount}>{selectedFixes ?? NO_VALUE}</span>
            </span>
            <span className={styles.legendRow}>
              <span className={styles.legendTrack} data-kind="site" aria-hidden="true" />
              <span className={styles.legendLabel}>Site extent · fix hull</span>
              <span className={styles.legendCount}>{siteCount}</span>
            </span>
            {sessionCount !== null && (
              <span className={styles.legendRow}>
                <span className={styles.legendTrack} data-kind="capture" aria-hidden="true" />
                <span className={styles.legendLabel}>
                  Capture session
                  <span className={styles.legendSub}>scan bbox</span>
                </span>
                <span className={styles.legendCount}>{sessionCount}</span>
              </span>
            )}
            {/* Tasking. Two readings, because a queue and a destination are
                different facts: how many machines are carrying orders, and how
                many of those orders name a place on this map. */}
            <span className={styles.legendRow}>
              <LegendPin
                tone="nominal"
                shape={{
                  heading: null,
                  hollow: false,
                  staleFix: false,
                  alert: false,
                  tasked: true,
                }}
              />
              <span
                className={styles.legendLabel}
                title={`${queued} command${
                  queued === 1 ? '' : 's'
                } in flight across the robots drawn. The selected robot never wears the ring — its whole queue is on its card.`}
              >
                Carrying orders
                <span className={styles.legendSub}>dashed ring</span>
              </span>
              <span className={styles.legendCount}>{live ? taskedCount : NO_VALUE}</span>
            </span>
            <span className={styles.legendRow}>
              <span className={styles.legendTrack} data-kind="order" aria-hidden="true" />
              <span className={styles.legendLabel}>
                Commanded waypoint
                <span className={styles.legendSub}>leg to target</span>
              </span>
              <span className={styles.legendCount}>{live ? orderCount : NO_VALUE}</span>
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function LayerTree({
  layers,
  forcedGraticule,
  counts,
  live,
  onToggle,
  onClose,
}: {
  layers: LayerState;
  forcedGraticule: boolean;
  counts: {
    sites: number;
    robots: number;
    tracks: number;
    track: number;
    sessions: number;
    commands: number;
  };
  /** The surface is on the newest record. The command queue has no history. */
  live: boolean;
  onToggle: (key: keyof LayerState) => void;
  onClose: () => void;
}) {
  const countFor = (key: keyof LayerState): ReactNode => {
    if (key === 'sites') return counts.sites;
    if (key === 'robots') return counts.robots;
    if (key === 'tracks') return counts.tracks;
    if (key === 'track') return counts.track === 0 ? null : counts.track;
    if (key === 'sessions') return counts.sessions === 0 ? null : counts.sessions;
    /* Off live this layer is not empty, it is UNKNOWABLE — the queue endpoint
       holds no history. An em dash says that; a zero would be a claim. */
    if (key === 'commands') return live ? (counts.commands === 0 ? null : counts.commands) : NO_VALUE;
    return null;
  };
  return (
    <div className={styles.tree}>
      <div className={styles.treeHead}>
        <Eyebrow>Layers</Eyebrow>
        <span className={styles.spacer} />
        <IconButton icon="close" label="Close layers" size="sm" onClick={onClose} />
      </div>
      {LAYER_ROWS.map((row, i) => {
        const forced = row.key === 'graticule' && forcedGraticule;
        const on = layers[row.key] || forced;
        const hint =
          row.key === 'commands' && !live
            ? 'The command queue holds no history — nothing to draw at a past instant'
            : forced
              ? 'Forced on — no basemap tiles are reachable'
              : `Toggle ${row.label}`;
        return (
          <button
            key={row.key}
            type="button"
            className={styles.treeRow}
            data-on={on}
            aria-pressed={on}
            disabled={forced}
            title={hint}
            onClick={() => onToggle(row.key)}
          >
            <span className={styles.treeIndex}>{i + 1}</span>
            <span className={styles.treeLabel}>{row.label}</span>
            <span className={styles.treeCount}>{countFor(row.key)}</span>
            <Icon name={on ? 'eye' : 'eye-off'} size={13} />
          </button>
        );
      })}
    </div>
  );
}

/* The pointer's own position, in the same frame every other coordinate on
   this surface is stated in. Anduril parks `Cursor:` in exactly this corner
   (`anduril-lattice-asset-detail-tasking.png`) and the reason is that a map
   without it can only answer "where is that robot", never "where is THAT".

   It writes through a ref rather than through state: at 60 mousemoves a
   second, re-rendering the marker layer to move six characters would cost the
   whole frame budget for no picture. */
/* ==========================================================================
   Track window — the surface's one time axis, and its data-presence band
   ==========================================================================
   THE BAR §5 (timelines) and §13 tells 21/22. Three things it must do and a
   fourth it must refuse:

     · paint DATA PRESENCE, so a hole in the recording is a hole in the band
       rather than a flat line the operator reads as "quiet";
     · NAME the axis it scrubs — a recording carries several clocks and they
       disagree. Here there is exactly one, the telemetry record's own `ts`,
       and it is printed;
     · make live-vs-window an unmistakable mode with one control that flips.

   And the fourth, which is why this bar is a clock and not a filter: what it
   resolves to is the instant the ENTIRE surface reads at. Drag it and the
   pins move to where those robots were, the rail's ages are measured from
   there, and both panel tags flip to `Polling off`. A transport that moved
   only the lines under the markers would be a control that changes the
   picture without changing what the picture claims.
*/

function TrackWindow({
  presence,
  startMs,
  endMs,
  spanMs,
  live,
  loading,
  failed,
  drawnFixes,
  drawnSpan,
  playing,
  rate,
  runMs,
  onPlay,
  onRate,
  onStep,
  onSpan,
  onEnd,
  onLive,
}: {
  presence: {
    t0: number;
    t1: number;
    step: number;
    fixes: Int32Array;
    robots: Int32Array;
    peak: number;
  } | null;
  startMs: number | null;
  endMs: number | null;
  spanMs: number | null;
  live: boolean;
  loading: boolean;
  /** Every history request failed — say so rather than reporting emptiness. */
  failed: boolean;
  drawnFixes: number;
  /** Wall-clock span the drawn vertices actually cover, measured. */
  drawnSpan: number | null;
  /** The playhead is advancing on its own. */
  playing: boolean;
  /** Replay rate, × real time. */
  rate: number;
  /** Recorded time a press of play would run through, from here. */
  runMs: number | null;
  onPlay: () => void;
  onRate: () => void;
  /** Move the leading edge by whole presence buckets. */
  onStep: (buckets: number) => void;
  onSpan: (ms: number | null) => void;
  onEnd: (ms: number) => void;
  onLive: () => void;
}) {
  const bandRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef(false);
  /* The ruler's tick step is derived from the band's MEASURED width, so the
     labels never crowd at 1280 and never thin out at 1920. */
  const [bandW, setBandW] = useState(0);
  useEffect(() => {
    const el = bandRef.current;
    if (el === null || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      if (e !== undefined) setBandW(Math.round(e.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const t0 = presence?.t0 ?? null;
  const t1 = presence?.t1 ?? null;
  const total = t0 !== null && t1 !== null ? t1 - t0 : null;
  const pct = (t: number) =>
    total === null || total <= 0 || t0 === null ? 0 : ((t - t0) / total) * 100;

  /* Ticks land on real wall-clock boundaries, never on even fractions of the
     span: `|04:00` is a time you can compare against a log, `|+33%` is not. */
  const ticks = useMemo(() => {
    if (t0 === null || t1 === null || t1 <= t0) return [];
    /* The ruler's right end belongs to the axis name, so a tick never lands
       under it, and every label needs ~46px of its own. */
    const room = Math.max(1, Math.floor((bandW - AXIS_W) / 46));
    const hours = (t1 - t0) / 3_600_000;
    const steps = [1 / 12, 0.25, 0.5, 1, 2, 3, 4, 6, 12, 24];
    const step = (steps.find((h) => hours / h <= room) ?? 24) * 3_600_000;
    /* A tick that would print under the axis name is dropped: the line is
       still there and still readable off its neighbours, which is more than a
       sliced label is. The cut reserves the axis name's width AND the tick
       label's OWN width — the label is left-anchored at its tick, so a tick
       landing exactly on the axis name's left edge still runs six characters
       straight through it. */
    const cut =
      t0 + ((t1 - t0) * Math.max(0, bandW - AXIS_W - TICK_W)) / Math.max(1, bandW);
    const out: { t: number; label: string }[] = [];
    for (let t = Math.ceil(t0 / step) * step; t <= cut; t += step) {
      out.push({ t, label: utcTime(new Date(t).toISOString()).slice(0, 5) });
    }
    return out;
  }, [t0, t1, bandW]);

  const seek = useCallback(
    (clientX: number) => {
      const el = bandRef.current;
      if (el === null || t0 === null || t1 === null || total === null || total <= 0) return;
      const r = el.getBoundingClientRect();
      const u = Math.min(1, Math.max(0, (clientX - r.left) / Math.max(1, r.width)));
      const t = t0 + u * total;
      /* Within a bucket of the newest record, this IS live — snapping avoids a
         window that is a second behind and calls itself replay. */
      if (t >= t1 - (presence?.step ?? 0)) onLive();
      else onEnd(Math.max(t0 + WINDOW_MIN_MS, t));
    },
    [t0, t1, total, presence, onEnd, onLive],
  );

  const onKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (t0 === null || t1 === null || total === null || endMs === null) return;
    const step = total / 60;
    if (e.key === 'ArrowLeft') onEnd(Math.max(t0 + WINDOW_MIN_MS, endMs - step));
    else if (e.key === 'ArrowRight') {
      const next = endMs + step;
      if (next >= t1) onLive();
      else onEnd(next);
    } else if (e.key === 'Home') onEnd(Math.max(t0 + WINDOW_MIN_MS, t0 + total / 60));
    else if (e.key === 'End') onLive();
    else return;
    e.preventDefault();
  };

  const range =
    startMs === null || endMs === null
      ? NO_VALUE
      : `${zulu(startMs)} → ${live ? 'now' : zulu(endMs)}`;

  return (
    <div className={styles.timebar} data-live={live}>
      <span className={styles.timeKey}>
        <Eyebrow>Track window</Eyebrow>
      </span>

      {/* The transport. Rerun's bottom bar, at our geometry: step / play /
          step, then the rate, then the axis. Play is the ONE control here
          that is ever filled — while it is running the picture is moving on
          its own, and that has to be legible from across the room. */}
      <span className={styles.transport} role="group" aria-label="Replay transport">
        <button
          type="button"
          className={styles.tBtn}
          disabled={presence === null}
          onClick={() => onStep(-1)}
          aria-label="Step back one bucket"
          title={
            presence === null
              ? 'No history to step through'
              : `Step the leading edge back one bucket (${duration(
                  presence.step,
                )}) — , `
          }
        >
          <Icon name="chevron-left" size={12} />
        </button>
        <button
          type="button"
          className={styles.tBtn}
          data-play="true"
          data-on={playing}
          disabled={presence === null}
          onClick={onPlay}
          aria-label={playing ? 'Pause replay' : 'Play replay'}
          aria-pressed={playing}
          title={
            presence === null
              ? 'No history to replay'
              : playing
                ? 'Pause — space'
                : `Replay ${
                    runMs === null ? 'the window' : duration(runMs)
                  } of recorded time forward at ${rate}× — ${
                    runMs === null ? '' : `${duration(Math.round(runMs / rate))} of wall clock. `
                  }Space.`
          }
        >
          <Icon name={playing ? 'pause' : 'play'} size={12} />
        </button>
        <button
          type="button"
          className={styles.tBtn}
          disabled={presence === null}
          onClick={() => onStep(1)}
          aria-label="Step forward one bucket"
          title={
            presence === null
              ? 'No history to step through'
              : `Step the leading edge forward one bucket (${duration(
                  presence.step,
                )}) — . `
          }
        >
          <Icon name="chevron-right" size={12} />
        </button>
        <button
          type="button"
          className={styles.rateBtn}
          disabled={presence === null}
          onClick={onRate}
          aria-label={`Replay rate, ${rate} times real time`}
          title={`Replay rate — ${RATES.map((r) => `${r}×`).join(' · ')}. At ${rate}× one minute of wall clock replays ${duration(
            rate * 60_000,
          )} of recorded time${
            runMs === null ? '' : `, so the ${duration(runMs)} in front of the playhead runs in ${duration(Math.round(runMs / rate))}`
          }.`}
        >
          {rate}
          <span className={styles.rateX}>&times;</span>
        </button>
      </span>

      <div className={styles.band}>
        <div
          ref={bandRef}
          className={styles.bandHit}
          role="slider"
          tabIndex={presence === null ? -1 : 0}
          aria-label="Track window leading edge, in recorded fix time"
          aria-valuemin={t0 ?? 0}
          aria-valuemax={t1 ?? 0}
          aria-valuenow={endMs ?? 0}
          aria-valuetext={endMs === null ? 'no history' : live ? 'live' : zulu(endMs)}
          aria-disabled={presence === null}
          /* The band's encoding is stated, not left to be inferred: bar height
             is the number of robots that reported in that bucket. */
          title={
            presence === null
              ? undefined
              : `Recorded fix coverage, ${zulu(presence.t0)} → ${zulu(
                  presence.t1,
                )}. Bar height is robots reporting in that bucket, peak ${
                  presence.peak
                }; a gap is a bucket nothing reported in. Drag or use the arrow keys to move the window's leading edge.`
          }
          onKeyDown={onKey}
          onPointerDown={(e) => {
            if (presence === null) return;
            dragRef.current = true;
            e.currentTarget.setPointerCapture(e.pointerId);
            seek(e.clientX);
          }}
          onPointerMove={(e) => {
            if (dragRef.current) seek(e.clientX);
          }}
          onPointerUp={(e) => {
            dragRef.current = false;
            e.currentTarget.releasePointerCapture(e.pointerId);
          }}
        >
          {presence === null ? (
            <span className={styles.bandEmpty} data-error={failed}>
              {loading
                ? 'history loading'
                : failed
                  ? 'history unavailable — telemetry request failed'
                  : 'no history recorded'}
            </span>
          ) : (
            <>
              {/* Presence. Bar height is the number of robots reporting in that
                  bucket — a bounded quantity, so height is judgeable — and a
                  bucket nothing reported in draws NOTHING. */}
              <svg
                className={styles.bandSvg}
                viewBox={`0 0 ${PRESENCE_BUCKETS} 10`}
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                {Array.from({ length: PRESENCE_BUCKETS }, (_, i) => {
                  const n = presence.robots[i] ?? 0;
                  if (n === 0) return null;
                  const h = Math.max(2.4, (n / presence.peak) * 10);
                  const bt = presence.t0 + (i + 0.5) * presence.step;
                  const on =
                    startMs !== null && endMs !== null && bt >= startMs && bt <= endMs;
                  return (
                    <rect
                      key={i}
                      className={styles.bandBar}
                      data-on={on}
                      x={i}
                      y={10 - h}
                      width={0.86}
                      height={h}
                    />
                  );
                })}
              </svg>
              {/* The window, as a stroke over the band — drawn only when it
                  is a genuine sub-range. A highlight that covers the whole
                  band says nothing and spends the accent on nothing. */}
              {startMs !== null && endMs !== null && pct(endMs) - pct(startMs) < 99 && (
                <span
                  className={styles.bandWin}
                  style={{
                    left: `${pct(startMs)}%`,
                    width: `${Math.max(0.4, pct(endMs) - pct(startMs))}%`,
                  }}
                  aria-hidden="true"
                />
              )}
              {endMs !== null && (
                <span
                  className={styles.bandHead}
                  style={{ left: `${pct(endMs)}%` }}
                  aria-hidden="true"
                />
              )}
            </>
          )}
        </div>
        <div className={styles.ruler} aria-hidden="true">
          {ticks.map((t) => (
            <span key={t.t} className={styles.tick} style={{ left: `${pct(t.t)}%` }}>
              |{t.label}
            </span>
          ))}
          {/* The axis names itself ON the axis, parked absolutely at its right
              end so it costs the band no width: a recording carries capture
              time, ingest time and publish time and they disagree; this one
              is the telemetry record's own `ts`. THE BAR §13 tell 22. */}
          <span className={styles.timeAxis}>fix time · UTC</span>
        </div>
      </div>

      <span
        className={styles.timeRange}
        title={
          drawnSpan === null
            ? "Resolved window, in the record's own Zulu time — no vertices inside it"
            : `Resolved window, in the record's own Zulu time. ${drawnFixes} track vertices drawn, spanning ${duration(
                drawnSpan,
              )}`
        }
      >
        {range}
        {/* `0 fixes` beside `no history recorded` is a count of a recording
            nobody read. `presence === null` is this bar's own word for that. */}
        <span className={styles.timeUnit}>
          {' · '}{presence === null ? NO_VALUE : drawnFixes} fixes
        </span>
      </span>

      <span className={styles.timeSpans} role="group" aria-label="Window length">
        {WINDOW_SPANS.map((w) => (
          <button
            key={w.label}
            type="button"
            className={styles.spanBtn}
            aria-pressed={spanMs === w.ms}
            disabled={presence === null}
            onClick={() => onSpan(w.ms)}
          >
            {w.label}
          </button>
        ))}
      </span>

      {/* One control, two states — the mode is legible without reading it. */}
      <button
        type="button"
        className={styles.liveBtn}
        data-live={live}
        disabled={presence === null}
        onClick={onLive}
        title={
          live
            ? 'The window follows the newest record that arrives — L'
            : 'Return the window to the newest record — L'
        }
      >
        <StatusDot tone={live ? 'nominal' : 'offline'} hollow={!live} />
        {live ? 'Live' : 'Go live'}
        {/* The key is printed in the affordance, not hidden in a tooltip —
            THE BAR §6. The space is reserved in both states so returning to
            live does not shift the control that did it. */}
        <span className={styles.liveKey} aria-hidden="true">
          L
        </span>
      </button>

    </div>
  );
}

function Cursor({ map }: { map: L.Map | null }) {
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (map === null || el === null) return;
    const write = (t: string, off: boolean) => {
      el.textContent = t;
      el.dataset.off = String(off);
    };
    const onMove = (e: L.LeafletMouseEvent) => write(latlon(e.latlng.lat, e.latlng.lng), false);
    const onOut = () => write('off map', true);
    map.on('mousemove', onMove);
    map.on('mouseout', onOut);
    return () => {
      map.off('mousemove', onMove);
      map.off('mouseout', onOut);
    };
  }, [map]);

  return (
    <div className={styles.brRow} title="Pointer position — WGS84, 5 decimals (~1.1 m)">
      <span className={styles.brKey}>Cursor</span>
      <span className={styles.brFill} />
      <span ref={ref} className={styles.cursorVal} data-off="true">
        off map
      </span>
    </div>
  );
}

function ScaleBar({ map, view }: { map: L.Map | null; view: number }) {
  const scale = useMemo(() => {
    if (map === null) return null;
    void view;
    const size = map.getSize();
    if (size.x < 40) return null;
    const y = Math.round(size.y / 2);
    const perPx = map.containerPointToLatLng([0, y]).distanceTo(map.containerPointToLatLng([120, y])) / 120;
    if (!Number.isFinite(perPx) || perPx <= 0) return null;
    const raw = perPx * 120;
    const pow = Math.pow(10, Math.floor(Math.log10(raw)));
    const options = [1, 2, 5, 10].map((m) => m * pow).filter((v) => v <= raw);
    const chosen = options.length > 0 ? (options[options.length - 1] as number) : pow;
    const text =
      chosen >= 1000
        ? `${(chosen / 1000).toFixed(chosen >= 10000 ? 0 : 1)} km`
        : `${Math.round(chosen)} m`;
    return { px: chosen / perPx, text };
  }, [map, view]);

  if (scale === null) return null;
  return (
    <div className={styles.brRow} title="Scale at the viewport's centre latitude">
      <span className={styles.brKey}>Scale</span>
      <span className={styles.brFill} />
      <span
        className={styles.scaleBar}
        style={{ width: `${Math.round(scale.px)}px` } as CSSProperties}
      />
      <span className={styles.scaleText}>{scale.text}</span>
    </div>
  );
}
